import { Language } from './types';

export const TRANSLATIONS: Record<Language, any> = {
  "en": {
    "ownerPartnership": {
      "hero": {
        "tagline": "Strategic Construction Advisory",
        "title1": "Strategic Delivery.",
        "title2": "Absolute Financial Control.",
        "desc": "Navigate the complexities of custom construction with an expert Project Management Office by your side. You retain complete financial control while we drive the day-to-day execution.",
        "cta": "Book a Consultation"
      },
      "why": {
        "tagline": "The Advisory Advantage",
        "title": "Reimagining the Client-Builder Relationship",
        "p1": "Traditional fixed-price contracts inherently position the builder's profit against the homeowner's budget. Variations become disputes, and opacity serves as a financial shield. We created the Owner Partnership to eliminate this friction entirely.",
        "p2": "By operating on a transparent management fee, our financial incentives are perfectly aligned with yours: delivering an exceptional architectural asset as efficiently as possible, without compromise.",
        "cardTitle": "True Alignment",
        "cardDesc": "Shared objectives replace traditional conflict.",
        "b1Title": "100% Cost Transparency",
        "b1Desc": "Every trade discount and savings is passed directly back to you.",
        "b2Title": "Absolute Security",
        "b2Desc": "You hold the project capital. Funds never leave your account until you approve the work."
      },
      "process": {
        "tagline": "Strategic Phasing",
        "title": "The Delivery Methodology",
        "steps": [
          {
            "title": "Strategic Alignment",
            "desc": "We analyze your architectural plans, establish realistic budgetary frameworks, and define the master delivery strategy."
          },
          {
            "title": "Transparent Procurement",
            "desc": "We tender your project to our vetted network of premium trades, ensuring competitive market rates are passed directly to you."
          },
          {
            "title": "Precision Execution",
            "desc": "Our dedicated site management team coordinates daily operations, enforcing strict quality control and adherence to the master schedule."
          },
          {
            "title": "Quality Handover",
            "desc": "We conduct exhaustive defect reviews, finalize certifications, and formally hand over your completed architectural asset."
          }
        ]
      },
      "matrix": {
        "tagline": "Defined Scope",
        "title": "Clarity in Execution",
        "ownerTitle": "The Owner",
        "ownerItems": [
          "Retains capital in a dedicated private account",
          "Authorizes all major design and scope variations",
          "Approves and releases milestone payments to trades",
          "Maintains ultimate executive oversight"
        ],
        "sharedTitle": "Shared",
        "sharedItems": [
          "Ongoing budget optimization and reviews",
          "Proactive value engineering decisions",
          "Architectural finish and material selections",
          "Comprehensive monthly progress assessments"
        ],
        "lsTitle": "Le Sector (PMO)",
        "lsItems": [
          "Comprehensive site management and WHS compliance",
          "Rigorous trade procurement and micro-scheduling",
          "Uncompromising quality assurance and defect resolution",
          "End-to-end council and certifier coordination"
        ]
      },
      "flow": {
        "tagline": "Capital Protection",
        "title": "Uncompromising Financial Security",
        "p1": "The greatest risk in custom construction is relinquishing control of your capital without visibility into its deployment.",
        "p2": "Our model mitigates this entirely. Le Sector never holds your construction funds. We rigorously certify the work, but you retain the authority to release payments.",
        "s1Title": "Expert Certification",
        "s1Desc": "Our site managers rigorously verify that trade work meets our quality standards.",
        "s2Title": "Financial Validation",
        "s2Desc": "The PMO reconciles invoices against the approved tender and procurement schedule.",
        "s3Title": "Executive Approval",
        "s3Desc": "You authorize the release of funds directly to the trades from your secure account."
      },
      "dash": {
        "tagline": "Real-Time Intelligence",
        "title": "Visibility Across Every Dimension",
        "desc": "Eliminate the anxiety of the unknown. Our digital infrastructure provides you with executive-level visibility into your project's health, progress, and financial standing at all times.",
        "items": [
          "Dynamic schedule forecasting",
          "Comprehensive daily site logs",
          "Real-time budget variance analysis",
          "Live procurement tracking"
        ],
        "hub": "Project Hub",
        "budget": "Capital Deployed",
        "schedule": "Master Schedule",
        "onTrack": "On Track",
        "week": "Week 14 of 32",
        "cashflow": "Forecasted Cashflow"
      },
      "suit": {
        "tagline": "Client Profile",
        "title": "Is This Model Right For You?",
        "idealTitle": "Ideal For",
        "idealItems": [
          "Clients undertaking complex, premium architectural builds",
          "Homeowners who demand uncompromising financial transparency",
          "Those seeking executive oversight without the burden of daily management",
          "Developers prioritizing cost-efficiency and direct trade access"
        ],
        "notTitle": "Not Ideal For",
        "notItems": [
          "Clients seeking a traditional 'set-and-forget' fixed-price contract",
          "Those unwilling or unable to authorize weekly milestone payments",
          "Projects that rely on builder-supplied construction finance",
          "Standardized, volume-based residential construction"
        ]
      },
      "faq": {
        "tagline": "Advisory Insights",
        "title": "Frequently Asked Questions",
        "q1": "How exactly does the capital control mechanism function?",
        "a1": "Your project funds remain securely in your own nominated bank account. While Le Sector expertly manages procurement, scheduling, and quality certification, we have no direct access to your capital. You act as the final financial authority, releasing payments directly to verified trades only when milestones are successfully met.",
        "q2": "If Le Sector doesn't hold the funds, what is the scope of your responsibility?",
        "a2": "We serve as your dedicated Project Management Office (PMO) and elite site team. We assume complete responsibility for strategic planning, rigorous procurement, daily site management, uncompromising quality control, and schedule coordination. We execute the complex reality of construction, allowing you to focus purely on executive oversight.",
        "q3": "Does this model require me to have prior construction experience?",
        "a3": "Not at all. In fact, our model provides significantly more guidance and protection than a traditional contract. By pairing our transparent reporting infrastructure with our expert advisory team, you are empowered to make informed decisions without needing technical construction expertise."
      },
      "cta": {
        "title": "Begin the Conversation",
        "desc": "Schedule a private consultation to explore how our advisory model can protect your capital and elevate the delivery of your architectural project."
      },
      "form": {
        "step": "Step",
        "of": "of",
        "s1Title": "Project Type",
        "s1Opts": [
          "Single Dwelling",
          "Multi-Unit Development",
          "Commercial",
          "Other"
        ],
        "s2Title": "Project Stage",
        "s2Opts": [
          "Concept / Idea",
          "Planning / Design",
          "Council Approval",
          "Ready to Build"
        ],
        "s3Title": "Project Location",
        "s3Ph": "Suburb or Postcode",
        "s4Title": "Estimated Construction Budget",
        "s4Opts": [
          "Under $500k",
          "$500k - $1M",
          "$1M - $2.5M",
          "$2.5M+"
        ],
        "s5Title": "Services Required",
        "s5Opts": [
          "Feasibility Study",
          "Architectural Design",
          "Project Management",
          "Construction Delivery"
        ],
        "s6Title": "Client Priorities",
        "s6Opts": [
          "Cost Efficiency",
          "Premium Quality",
          "Speed of Delivery",
          "Maximum Transparency"
        ],
        "s7Title": "Timeline",
        "s7Opts": [
          "ASAP",
          "3-6 Months",
          "6-12 Months",
          "12+ Months"
        ],
        "s8Title": "Additional Information",
        "s8Ph": "Tell us more about your project vision...",
        "s9Title": "Contact Details",
        "s9Ph1": "Full Name",
        "s9Ph2": "Email Address",
        "s9Ph3": "Phone Number",
        "back": "Back",
        "continue": "Continue",
        "submit": "Request Consultation",
        "submitting": "Submitting...",
        "error": "Please complete all required fields.",
        "emailError": "Please enter a valid email address.",
        "submitError": "An error occurred. Please try again."
      },
      "confirm": {
        "title": "Application Received",
        "desc": "Thank you for your interest. We will review your project details and contact you within 24 hours.",
        "nextSteps": "What happens next?",
        "bookTitle": "Initial Consultation",
        "bookDesc": "One of our advisors will reach out to schedule your introductory call.",
        "dlTitle": "Project Review",
        "dlDesc": "We will conduct a preliminary assessment of your project details."
      }
    },
    "promo": {
      "open": "Open promotional offers",
      "latestUpdates": "Latest Updates",
      "close": "Close promotional popup",
      "prev": "Previous slide",
      "next": "Next slide",
      "goto": "Go to slide",
      "dontShowAgain": "Don't show again"
    },
    "nav": {
      "home": "Home",
      "about": "About",
      "services": "Services",
      "howItWorks": "How it Works",
      "faq": "FAQ",
      "contact": "Contact",
      "hub": "Ads Hub"
    },
    "footer": {
      "tagline": "Traditional & Modular Custom Homes. Delivered with Certainty.",
      "mission": "We provide professional Project Management and Project Delivery for both Traditional Custom Homes and Modular Homes, bringing clarity, structured control, and certainty to client-designed plans.",
      "col1": "Gadgets",
      "col2": "Services",
      "col3": "Contact",
      "col4": "Partners & Legal",
      "gadgets": {
        "calculator": "Cost Calculator",
        "measurer": "Site Measurer",
        "design": "Design Studio"
      },
      "serviceList": [
        "Modular Housing Delivery",
        "Design Feasibility",
        "Modular Optimisation",
        "Pre-Construction Coordination"
      ],
      "contactDetails": {
        "phone": "0432 866 168",
        "area": "Australia (Victoria)",
        "hours": "Monday – Friday, 9:00am – 5:00pm"
      },
      "partners": [
        {
          "name": "1st Mortgage Group",
          "role": "Mortgage Broker",
          "url": "https://www.1stmg.com.au"
        },
        {
          "name": "8 Miles Estate",
          "role": "Real Estate Agency",
          "url": "https://www.8milesestate.com.au"
        },
        {
          "name": "Core-Base Construction",
          "role": "Footing & Groundworks",
          "url": "https://www.corebasecons.com.au"
        }
      ],
      "legal": [
        "Privacy Policy",
        "Terms & Conditions"
      ],
      "copyright": "© Le Sector 2025. All rights reserved.",
      "disclaimer": "Le Sector provides modular housing delivery and coordination services. Design services are provided by external architects and designers. Subject to council approval and site conditions.",
      "cta": {
        "title": "Facing challenges with Land or Finance?",
        "sub": "Speak with our strategic partners to solve your pre-construction hurdles.",
        "btn": "Connect with Partners"
      }
    },
    "home": {
      "heroTitle": "Le Sector Project Delivery. Traditional & Modular Homes. Managed with Certainty.",
      "heroSub": "We help clients choose the most suitable construction approach for their project—delivering both Traditional Custom Homes and Modular Homes through disciplined, professional project management from planning to completion.",
      "ctaStart": "Start Your Building Journey",
      "ctaSpeak": "Speak With Our Team",
      "whoTitle": "Who We Are",
      "whoMain": "Le Sector is a professional Project Management and Project Delivery company delivering both Traditional Custom Homes and Modular Homes.",
      "whoSub1": "We coordinate planning, procurement, consultants, site construction, and modular manufacturing—empowering homeowners, architects, and developers to select the construction approach that best suits their vision and budget.",
      "whoSub2": "Whether delivering a Traditional Custom Home or a Modular Home, every project follows our structured project management system—supported by an ISO management framework for rigorous quality assurance, transparent documentation, and disciplined project delivery from concept to completion.",
      "salesPoints": {
        "warranty": {
          "title": "Regulated 7-Year Warranty",
          "desc": "Full statutory building warranty protection (7 years) across both Traditional Custom Homes and Modular projects."
        },
        "delays": {
          "title": "Controlled Schedules",
          "desc": "Whether through factory modular manufacturing or disciplined on-site management, we minimize rain delays and trade gaps."
        },
        "fixedPrice": {
          "title": "Fixed Price Contracts",
          "desc": "Stay Fixed Price. We lock in project costs before construction or manufacturing begins, providing total financial certainty."
        },
        "costRises": {
          "title": "No Material Cost Spikes",
          "desc": "Never face unexpected material cost increases. We procure materials and coordinate trades upfront to protect you from inflation."
        }
      },
      "notContainer": {
        "title": "Not a container house.",
        "desc": "Le Sector modular homes are built using engineered steel-frame structures and advanced lightweight concrete systems designed for permanent residential living."
      },
      "comparison": {
        "title": "How We Compare",
        "criteria": [
          "Structural strength",
          "Fire resistance",
          "Water resistance",
          "Acoustic insulation",
          "Fast installation",
          "Permanent residential quality",
          "Project Management System"
        ],
        "headers": [
          "Le Sector Managed Build",
          "Container Homes",
          "Traditional Unmanaged Build"
        ],
        "data": [
          [
            "High (Engineered Steel)",
            "High (Steel Box)",
            "Varies (Timber/Steel)"
          ],
          [
            "High (Non-combustible)",
            "Varies (Needs Cladding)",
            "Varies"
          ],
          [
            "High",
            "High",
            "Varies"
          ],
          [
            "High (Advanced panels)",
            "Low (Metal echo)",
            "Varies"
          ],
          [
            "Yes (Fast offsite)",
            "Yes",
            "No (Slow onsite)"
          ],
          [
            "Yes",
            "No",
            "Yes"
          ],
          [
            "Structured Quality Assurance & Control",
            "None",
            "Varies (Builder dependent)"
          ]
        ]
      },
      "lowRisk": {
        "badge": "Financial Advantage — Modular Pathway",
        "title": "Low-Risk Modular Building Model",
        "desc": "While Le Sector delivers both Traditional Custom Homes and Modular Homes, our modular delivery model is specifically structured as an available pathway to reduce financial risk for homeowners. Unlike standard construction loans that require progressive drawdowns and ongoing interest during the build, choosing our modular option allows clients to advance with significantly lower financial exposure during assembly.",
        "cardTitle": "Pay on Completion (Modular Pathway)",
        "highlight": "When choosing our modular delivery option, construction begins with foundation works followed by rapid module assembly. Clients pay a deposit starting from 5% of the total contract value, with the balance payable at settlement when your home is completed and ready for handover.",
        "keyAdvantages": "Key Modular Pathway Advantages",
        "bullets": [
          "No need for a traditional construction loan in many cases",
          "Reduced exposure to project delays and cost uncertainty",
          "No ongoing interest payments during the construction period",
          "Greater certainty between project commencement and completion",
          "Payment aligned with delivery of the finished home"
        ],
        "conclusion": "By offering modular delivery as an alternative to traditional custom builds, Le Sector gives homeowners the freedom to choose a safer and more predictable financial pathway when it fits their project."
      }
    },
    "ecosystem": {
      "tag": "Integrated Ecosystem",
      "title": "Le Sector + CoreBase",
      "desc": "Le Sector works closely with CoreBase Construction, specialists in foundations and groundworks, ensuring site preparation and structural works for both traditional and modular builds are aligned from day one.",
      "quote": "This collaboration removes the common disconnect between design, off-site manufacturing, and on-site works—resulting in smoother delivery and fewer surprises across any construction method.",
      "leSectorSubtitle": "Project Management & Delivery",
      "coreBaseSubtitle": "Groundworks"
    },
    "process": {
      "tag": "Our Process",
      "title": "Process-Led Confidence",
      "steps": [
        {
          "num": "01",
          "title": "Design Review & Feasibility",
          "desc": "We assess architectural plans for buildability, compliance, and pathway suitability—comparing Traditional Custom Home and Modular options."
        },
        {
          "num": "02",
          "title": "Design & Pathway Optimisation",
          "desc": "Plans are refined for traditional on-site execution or off-site modular manufacturing, improving cost certainty and quality control."
        },
        {
          "num": "03",
          "title": "Pre-Construction Coordination",
          "desc": "We align planning, procurement, consultants, trades, and factory schedules to eliminate disconnects."
        },
        {
          "num": "04",
          "title": "Turnkey Project Delivery",
          "desc": "Whether built on site or produced modularly off site, every project is delivered with disciplined quality assurance and precision."
        }
      ],
      "finalCta": "Ready to explore your building pathway?",
      "bookBtn": "Book a Consultation"
    },
    "about": {
      "tag": "Our Approach",
      "title": "Architecture First. Delivery Always.",
      "sub1": "Le Sector was established to support a growing demand for design-driven modular housing—where speed and certainty are required without sacrificing architectural quality.",
      "sub2": "Rather than offering pre-designed kits, we specialise in delivering client-designed plans through a modular construction methodology.",
      "approachTitle": "Our Approach to Modern Modular Homes",
      "approachP1": "At Le Sector, we specialise in delivering modern modular homes built with engineered steel frame structures and advanced lightweight concrete panels.",
      "approachP2": "Our construction system is designed to provide the durability, safety, and comfort expected from a permanent home, while benefiting from the efficiency of modular construction.",
      "approachP3": "Unlike container housing, our modular homes are not converted shipping containers. Each home is purpose-built using a structural steel frame and precision-manufactured wall panels with an integrated locking system. This allows for strong structural integrity, excellent insulation performance, and efficient on-site installation.",
      "approachP4": "Once the slab foundation is prepared, the main modular structure can be installed rapidly on site, significantly reducing the time typically required for traditional construction.",
      "approachP5": "Our goal is to combine modern building technology with thoughtful project coordination, giving homeowners a reliable and efficient pathway from design to completed home.",
      "roleTitle": "Our Role",
      "roleSub": "We are not a volume builder. We are a delivery partner.",
      "roles": [
        {
          "title": "Translation",
          "desc": "Translating complex architectural design into modular execution."
        },
        {
          "title": "Management",
          "desc": "Managing interfaces between consultants, manufacturing, and site works."
        },
        {
          "title": "Clarity",
          "desc": "Providing clarity, structure, and certainty throughout the process."
        }
      ],
      "integrity": "Design Integrity",
      "builtTitle": "Built for Strength, Comfort and Longevity",
      "builtP1": "Le Sector modular homes are designed for long-term residential living. Our structural system incorporates steel framing and advanced lightweight concrete panels that are fire-resistant, water-resistant, and provide strong acoustic and thermal performance.",
      "builtP2": "This approach ensures that modular construction is not a compromise in quality, but a smarter way to deliver modern homes with greater efficiency and certainty."
    },
    "services": {
      "tag": "Our Expertise",
      "title": "Design-Led Project Delivery",
      "desc": "Complete oversight of both Traditional Custom Homes and Modular delivery—from initial feasibility review to turnkey handover.",
      "list": [
        "Feasibility & construction pathway selection",
        "Traditional custom home & modular coordination",
        "Consultant and engineering alignment",
        "Pre-construction planning & fixed-price contracts",
        "Modular manufacturing & site trades oversight",
        "Structured quality assurance & turnkey delivery"
      ],
      "feasibility": {
        "title": "Modular Feasibility Assessment Package",
        "price": "$490 - $990",
        "tag": "First Step",
        "desc": "We are not just selling a build. We are selling clarity. Before signing any major contract, we help you determine if the project is worth pursuing.",
        "features": [
          "Preliminary site feasibility check",
          "Budget range analysis",
          "Timeline prediction",
          "Risk register",
          "Funding structure advice",
          "Cost comparison (Traditional vs Modular)"
        ]
      },
      "modelsTitle": "Engagement Models",
      "models": [
        {
          "title": "Mode A: Project Agency",
          "tag": "Design Coordination + Management",
          "fee": "Fixed Management Fee: 8–12%",
          "features": [
            "You carry the construction contract",
            "We manage the integration",
            "Zero markup on materials",
            "Ideal for Owner-Builders"
          ]
        },
        {
          "title": "Mode B: Investment Connection",
          "tag": "Connecting Land & Capital",
          "fee": "Structure Fee + Management Fee",
          "features": [
            "We connect Landowners with Capital",
            "We connect Investors with Projects",
            "Intermediate structure design",
            "Full project delivery management"
          ]
        }
      ],
      "groundTitle": "Foundation & Site Readiness",
      "groundTag": "Delivered via CoreBase",
      "groundList": [
        {
          "title": "Foundations",
          "desc": "Expertly engineered foundations optimized for module weight and site conditions."
        },
        {
          "title": "Groundworks",
          "desc": "Complete civil works, drainage, and utility trenching."
        },
        {
          "title": "Structural Preparation",
          "desc": "Precision set-out to ensure module-to-ground interface is millimeter-perfect."
        }
      ]
    },
    "faq": {
      "tag": "Trust & Clarity",
      "title": "Frequently Asked Questions",
      "items": [
        {
          "q": "Do you provide designs?",
          "a": "No. We work with client-designed plans or external architects. Our value is in the execution and modular delivery of your specific vision."
        },
        {
          "q": "Is modular cheaper?",
          "a": "Modular is about certainty and efficiency, not shortcuts. It provides predictable costs and significantly reduced construction timelines, but quality remains premium."
        },
        {
          "q": "Can designs be customised?",
          "a": "Yes—our process is built specifically to support bespoke architectural outcomes. We don't force you into a \"kit\" home."
        },
        {
          "q": "Who manages foundations?",
          "a": "Foundations are delivered in coordination with CoreBase Construction to ensure a seamless interface between ground and module."
        }
      ]
    },
    "contact": {
      "tag": "Let's Discuss",
      "title": "Start Your Modular Home Journey",
      "desc": "Whether you already own land, have approved plans, or are just exploring modular construction options, Le Sector is ready to help. Free project quotes available.",
      "labels": {
        "phone": "Direct Line",
        "email": "Email",
        "area": "Service Area",
        "formTitle": "Consultation Request",
        "name": "Full Name",
        "emailLabel": "Email",
        "phoneLabel": "Phone Number",
        "ownLand": "Do you already own land?",
        "location": "Site suburb/location",
        "typeInterested": "Type of home interested in",
        "budget": "Estimated budget range",
        "havePlans": "Do you already have plans?",
        "message": "Message",
        "fileUpload": "Upload Site Plans, Drawings or Supporting Documents (Optional)",
        "submit": "Book a Consultation"
      }
    },
    "calculator": {
      "disclaimer": "* Disclaimer: All prices are estimates and subject to the final contract price."
    },
    "exploreOpportunities": {
      "hero": {
        "badge": "LE SECTOR ECOSYSTEM • FACILITATED INTRODUCTIONS",
        "headline1": "EVERY GREAT PROJECT STARTS WITH THE RIGHT CONNECTION.",
        "subheadline": "Explore project opportunities and potential collaborations, professionally facilitated by Le Sector.",
        "primaryCta": "Explore Opportunities",
        "secondaryCta": "Tell Us What You're Looking For"
      },
      "pathways": {
        "tagline": "Ecosystem Pillars",
        "title": "What Can You Explore?",
        "desc": "Discover active pathways across residential real estate, development capital, and modular project delivery.",
        "items": [
          {
            "title": "Property & Sites",
            "desc": "Land opportunities, development potential, and property-related opportunities suitable for custom residential or modular delivery."
          },
          {
            "title": "Investment & Capital",
            "desc": "Investment opportunities, capital requirements, and potential funding alignment for vetted residential build proposals."
          },
          {
            "title": "Projects & Delivery",
            "desc": "Active projects seeking licensed builders, developers, specialized manufacturing capability, or delivery partners."
          },
          {
            "title": "Professional Expertise",
            "desc": "Architects, structural engineers, urban planners, and specialized consultants seeking early-stage integration."
          }
        ]
      },
      "howItWorks": {
        "tagline": "Facilitated Journey",
        "title": "How It Works",
        "steps": [
          {
            "step": "01",
            "title": "Explore",
            "desc": "Browse opportunities without exposing private contact information or property addresses."
          },
          {
            "step": "02",
            "title": "Express Interest",
            "desc": "Tell Le Sector which opportunity aligns with your goals and capabilities."
          },
          {
            "step": "03",
            "title": "We Review",
            "desc": "Le Sector reviews the inquiry, verifies intent, and checks project suitability."
          },
          {
            "step": "04",
            "title": "We Introduce",
            "desc": "Where appropriate, Le Sector facilitates a warm, double-opt-in professional introduction."
          },
          {
            "step": "05",
            "title": "Explore the Opportunity",
            "desc": "Relevant parties evaluate joint synergy, supported by Le Sector's project management expertise."
          }
        ]
      },
      "why": {
        "tagline": "The Le Sector Advantage",
        "title": "Why Through Le Sector?",
        "desc": "A professionally facilitated opportunity network free from unvetted forums, social media noise, and cold solicitations.",
        "features": [
          {
            "title": "Privacy First",
            "desc": "No public phone numbers, email addresses, or exact property addresses are ever exposed."
          },
          {
            "title": "Professionally Reviewed",
            "desc": "Opportunities and expressions of interest are thoroughly evaluated prior to introductions."
          },
          {
            "title": "Structured Introductions",
            "desc": "Le Sector manages and moderates the initial introduction rather than dumping contact details."
          },
          {
            "title": "Project Delivery Expertise",
            "desc": "Supported directly by Le Sector's disciplined custom home and modular project delivery framework."
          },
          {
            "title": "ISO-Aligned Quality Management",
            "desc": "Guided by structured management frameworks and ISO-aligned quality assurance principles."
          },
          {
            "title": "Zero Commercial Noise",
            "desc": "Free from spam, cold sales solicitations, or unvetted marketplace advertisements."
          }
        ]
      },
      "scenarios": {
        "tagline": "Illustrative Use Cases",
        "title": "Opportunity Scenarios",
        "desc": "Real-world examples showing how stakeholders connect within our ecosystem. (Illustrative scenarios — distinct from live directory listings).",
        "badge": "USE CASE SCENARIO",
        "items": [
          {
            "title": "I Have Land",
            "quote": "\"I want to understand what development opportunities may exist for my property without listing it publicly.\"",
            "targetMatch": "Developers, Equity Investors, Modular Specialists"
          },
          {
            "title": "I Have Capital",
            "quote": "\"I'm looking for property or development opportunities backed by structured project management.\"",
            "targetMatch": "Land Owners, Qualified Developers, Custom Projects"
          },
          {
            "title": "I Have A Project",
            "quote": "\"I'm looking for the right delivery capability, builder, or investment partner to move into construction.\"",
            "targetMatch": "Licensed Builders, Equity Partners, Project Directors"
          },
          {
            "title": "I Have Expertise",
            "quote": "\"I'm looking for suitable residential projects where I can contribute specialized design or engineering.\"",
            "targetMatch": "Project Owners, Developers, Planning Consultants"
          }
        ]
      },
      "directory": {
        "tagline": "Active Directory",
        "title": "Current Opportunities",
        "filterAllCategories": "All Categories",
        "filterAllModes": "All Delivery Modes",
        "modeTraditional": "Traditional Custom",
        "modeModular": "Modular Assembly",
        "modeHybrid": "Hybrid Delivery",
        "emptyState": {
          "title": "New opportunities are currently being reviewed.",
          "desc": "Tell us what you're looking for and we'll help determine whether there may be a suitable opportunity within the Le Sector network.",
          "cta": "Tell Us What You're Looking For"
        },
        "card": {
          "ref": "REF",
          "status": "Status",
          "location": "Location",
          "stage": "Stage",
          "type": "Project Type",
          "budget": "Budget Range",
          "seeking": "Seeking",
          "timeframe": "Timeframe",
          "expressInterest": "Express Interest (EOI)",
          "viewDetails": "View Summary"
        }
      },
      "privacy": {
        "tagline": "Discretion & Security",
        "title": "Privacy, Discretion & Trust Guarantee",
        "desc": "We safeguard your identity, project details, and commercial interests at every step.",
        "items": [
          {
            "title": "No Public Contact Info",
            "desc": "Your personal identity, phone, and email are never published or shared automatically."
          },
          {
            "title": "Reviewed Introductions",
            "desc": "All initial inquiries are vetted by Le Sector advisors prior to any formal introduction."
          },
          {
            "title": "Double Opt-In",
            "desc": "Introductions only occur when both parties explicitly consent after initial screening."
          }
        ]
      },
      "cta": {
        "title": "Have an Opportunity in Mind?",
        "desc": "Tell us what you're looking for and let Le Sector help determine whether there may be a suitable connection within our network.",
        "button": "Tell Us What You're Looking For"
      },
      "formModal": {
        "title": "Confidential Opportunity Inquiry",
        "sub": "Tell us about your requirements or opportunity. A Le Sector advisor will review your inquiry with total discretion.",
        "roleLabel": "Your Primary Role",
        "roles": {
          "landOwner": "Property / Land Owner",
          "investor": "Private / Equity Investor",
          "projectOwner": "Project Developer / Owner",
          "expert": "Professional / Industry Specialist"
        },
        "name": "Full Name",
        "email": "Email Address",
        "phone": "Phone Number",
        "region": "Target Region / Location",
        "category": "Interest Category",
        "details": "Project Parameters & What You Are Seeking",
        "disclaimer": "Your information is treated with strict confidentiality. No contact details or exact property addresses will be shared publicly or with third parties without your explicit consent.",
        "submit": "Submit Inquiry",
        "successTitle": "Inquiry Received",
        "successDesc": "Thank you for reaching out. Reference code: ",
        "successSub": "A Le Sector advisor will review your inquiry confidentially and get in touch within 24-48 hours.",
        "close": "Close"
      },
      "pathwayDetails": {
        "propertySites": {
          "badge": "LE SECTOR ECOSYSTEM • PROPERTY & SITES",
          "title": "Property & Sites Pathway",
          "headline": "Land, Development Sites & Property Potential",
          "subheadline": "Introduce opportunities involving land, development sites, and property potential suitable for custom residential or modular delivery.",
          "heroCta": "Enquire About Property & Sites",
          "secondaryCta": "View Opportunity Directory",
          "backToAll": "Back to All Opportunities",
          "highlights": [
            "Subdivision Evaluation",
            "Development Potential Assessment",
            "Modular Site Logistics",
            "Confidential Facilitation"
          ],
          "overviewTagline": "Land & Property Ecosystem",
          "overviewTitle": "What This Pathway Is About",
          "overviewDesc": "The Property & Sites pathway connects landowners, estate holders, and property partners with structured development capabilities. Whether evaluating greenfield subdivision potential, infill redevelopment, or modular custom home suitability, Le Sector provides early-stage coordination and feasibility alignment.",
          "overviewSubText": "Le Sector acts as a professional facilitator—helping property owners assess development potential and coordinate with experienced delivery partners without public listing pressure.",
          "cardTitle": "Discreet Site Coordination",
          "cardDesc": "Le Sector conducts preliminary site evaluation and connects landowners with suitable delivery capability while maintaining strict confidentiality.",
          "whoTagline": "Target Participants",
          "whoIsItForTitle": "Who This Is For",
          "whoIsItForItems": [
            {
              "title": "Landowners & Estate Holders",
              "desc": "Exploring subdivision potential or land utilization for custom residential or modular development."
            },
            {
              "title": "Subdivision Partners",
              "desc": "Seeking well-located sites or landowners interested in structured residential joint delivery."
            },
            {
              "title": "Redevelopment Site Owners",
              "desc": "Owners of existing residential properties seeking infill redevelopment or modular rebuild partnerships."
            }
          ],
          "typesTagline": "Opportunity Scope",
          "opportunityTypesTitle": "What Opportunities May Exist",
          "opportunityTypesItems": [
            {
              "title": "Residential Subdivision Sites",
              "desc": "Land parcels suitable for multi-lot residential subdivision or modern modular masterplans.",
              "tag": "Subdivision"
            },
            {
              "title": "Infill Development Sites",
              "desc": "Urban and suburban residential lots suitable for custom architectural homes or prefabricated builds.",
              "tag": "Infill Redevelopment"
            },
            {
              "title": "Development Partnerships",
              "desc": "Opportunities for landowners to partner with experienced developers and builder networks.",
              "tag": "Joint Venture"
            },
            {
              "title": "Modular Compatible Sites",
              "desc": "Sites evaluated for crane access, factory assembly logistics, and rapid modular deployment.",
              "tag": "Modular Ready"
            }
          ],
          "facTagline": "Facilitated Execution",
          "facilitationTitle": "How Le Sector Facilitates",
          "facilitationSteps": [
            {
              "step": "01",
              "title": "Site Evaluation",
              "desc": "Initial review of site orientation, access, zoning, and modular build feasibility."
            },
            {
              "step": "02",
              "title": "Structuring & Strategy",
              "desc": "Determining optimal project pathways—custom build, subdivision, or joint delivery."
            },
            {
              "step": "03",
              "title": "Confidential Matching",
              "desc": "Introducing vetted delivery partners or capital without public exposure."
            },
            {
              "step": "04",
              "title": "Execution Alignment",
              "desc": "Coordinating architectural, engineering, and manufacturing teams through delivery."
            }
          ],
          "scenariosTagline": "Practical Applications",
          "scenariosTitle": "Example Project Scenarios",
          "scenariosItems": [
            {
              "title": "3-Lot Residential Subdivision",
              "type": "Subdivision Potential",
              "description": "Landowner with a large suburban block seeking to subdivide into 3 residential lots using factory-crafted modular homes for rapid project completion.",
              "targetMatch": "Landowner + Modular Delivery Network"
            },
            {
              "title": "Infill Lot Re-development",
              "type": "Redevelopment Site",
              "description": "Site owner looking to replace an aging dwelling with a modern dual-occupancy custom residential build.",
              "targetMatch": "Site Owner + Custom Builder / Designer"
            }
          ],
          "resourcesTagline": "Information & References",
          "futureResourcesTitle": "Resources & Project Information",
          "futureResourcesDesc": "Access site planning checklists, modular site feasibility guidelines, and related Le Sector services.",
          "futureResourcesItems": [
            {
              "title": "Modular Site Feasibility Checklist",
              "description": "Guidance on crane access, site setbacks, utility connections, and slope evaluation.",
              "status": "Reference Guide",
              "linkText": "View Feasibility Framework"
            },
            {
              "title": "Subdivision Information Brief",
              "description": "Standardized parameters for presenting land parcels to development and delivery partners.",
              "status": "Information Slot",
              "linkText": "Request Brief Format"
            },
            {
              "title": "Le Sector Site Advisory Services",
              "description": "Learn how our advisory team conducts early-stage site and manufacturing compatibility reviews.",
              "status": "Service Overview",
              "linkText": "Explore Site Advisory"
            }
          ],
          "ctaBox": {
            "title": "Have a Property or Land Opportunity?",
            "desc": "Speak with a Le Sector advisor to evaluate site potential and discuss confidential facilitation options.",
            "buttonText": "Enquire About Property & Sites"
          }
        },
        "investmentCapital": {
          "badge": "LE SECTOR ECOSYSTEM • INVESTMENT & CAPITAL",
          "title": "Investment & Capital Pathway",
          "headline": "Capital & Development Alignment",
          "subheadline": "Connect development capital with suitable property, residential development, and construction delivery opportunities.",
          "heroCta": "Enquire About Investment & Capital",
          "secondaryCta": "View Opportunity Directory",
          "backToAll": "Back to All Opportunities",
          "highlights": [
            "Development Capital Alignment",
            "Asset-Backed Opportunities",
            "Professional Project Governance",
            "Facilitated Partnerships"
          ],
          "overviewTagline": "Capital Ecosystem",
          "overviewTitle": "What This Pathway Is About",
          "overviewDesc": "The Investment & Capital pathway introduces opportunities connecting capital with vetted property, development, and construction projects. Le Sector acts as a professional facilitator and project-delivery partner, ensuring rigorous early-stage vetting, project governance, and execution structure.",
          "overviewSubText": "Rather than operating as a public investment marketplace or public fund, Le Sector discreetly aligns capital with structured residential and modular build opportunities.",
          "cardTitle": "Professional Facilitation Partner",
          "cardDesc": "Le Sector coordinates capital alignment with project delivery partners under strict confidentiality and risk governance frameworks.",
          "whoTagline": "Target Participants",
          "whoIsItForTitle": "Who This Is For",
          "whoIsItForItems": [
            {
              "title": "Private & Equity Investors",
              "desc": "Seeking structured involvement in vetted residential build and development opportunities."
            },
            {
              "title": "Development Funding Partners",
              "desc": "Looking for professionally managed, asset-backed residential projects with clear delivery milestones."
            },
            {
              "title": "Project Developers",
              "desc": "Seeking equity or development capital alignment for residential and modular build projects."
            }
          ],
          "typesTagline": "Opportunity Scope",
          "opportunityTypesTitle": "What Opportunities May Exist",
          "opportunityTypesItems": [
            {
              "title": "Equity Partnerships",
              "desc": "Co-development structures for residential projects with clear milestone governance.",
              "tag": "Equity Alignment"
            },
            {
              "title": "Development Funding",
              "desc": "Project-level funding alignment for modular manufacturing and residential site delivery.",
              "tag": "Funding Structure"
            },
            {
              "title": "Asset-Backed Projects",
              "desc": "Property development opportunities backed by physical real estate assets.",
              "tag": "Asset-Backed"
            },
            {
              "title": "Professionally Managed Build Projects",
              "desc": "Opportunities for investors seeking projects fully managed by experienced delivery teams.",
              "tag": "Managed Delivery"
            }
          ],
          "facTagline": "Facilitated Execution",
          "facilitationTitle": "How Le Sector Facilitates",
          "facilitationSteps": [
            {
              "step": "01",
              "title": "Opportunity Vetting",
              "desc": "Initial due diligence on project parameters, zoning, and delivery feasibility."
            },
            {
              "step": "02",
              "title": "Structure Alignment",
              "desc": "Structuring engagement models that suit project scale and risk parameters."
            },
            {
              "step": "03",
              "title": "Discreet Introduction",
              "desc": "Connecting capital providers directly with project delivery principals."
            },
            {
              "step": "04",
              "title": "Governance & Reporting",
              "desc": "Providing project oversight and delivery tracking throughout the build cycle."
            }
          ],
          "scenariosTagline": "Practical Applications",
          "scenariosTitle": "Example Project Scenarios",
          "scenariosItems": [
            {
              "title": "Subdivision Development Capital",
              "type": "Equity Alignment",
              "description": "An approved 4-lot custom residential subdivision seeking equity capital alignment for site works and modular construction.",
              "targetMatch": "Project Developer + Equity Partner"
            },
            {
              "title": "Modular Project Funding",
              "type": "Development Funding",
              "description": "High-yield residential build proposal requiring structured funding for factory prefabrication and site installation.",
              "targetMatch": "Delivery Team + Funding Partner"
            }
          ],
          "resourcesTagline": "Information & References",
          "futureResourcesTitle": "Resources & Investment Information",
          "futureResourcesDesc": "Access governance guidelines, capital alignment briefs, and related advisory services.",
          "futureResourcesItems": [
            {
              "title": "Capital Vetting Framework",
              "description": "Overview of criteria used by Le Sector to evaluate project feasibility and delivery readiness.",
              "status": "Framework Overview",
              "linkText": "View Vetting Guidelines"
            },
            {
              "title": "Project Brief Structure",
              "description": "Standardized structure for presenting capital requirements to potential partners.",
              "status": "Structure Brief",
              "linkText": "Request Brief Format"
            },
            {
              "title": "Le Sector Advisory & Delivery Support",
              "description": "Learn how our team oversees project execution to safeguard project delivery milestones.",
              "status": "Service Overview",
              "linkText": "Explore Advisory Services"
            }
          ],
          "ctaBox": {
            "title": "Looking for Capital or Investment Alignment?",
            "desc": "Connect with a Le Sector advisor to discuss capital requirements or project alignment confidentially.",
            "buttonText": "Enquire About Capital & Investment"
          }
        },
        "projectsDelivery": {
          "badge": "LE SECTOR ECOSYSTEM • PROJECTS & DELIVERY",
          "title": "Projects & Delivery Pathway",
          "headline": "Delivery Capability & Strategic Partners",
          "subheadline": "Introduce active or emerging projects that require construction delivery capability, modular manufacturing, or strategic project partners.",
          "heroCta": "Enquire About Projects & Delivery",
          "secondaryCta": "View Opportunity Directory",
          "backToAll": "Back to All Opportunities",
          "highlights": [
            "Licensed Builder Integration",
            "Modular Factory Alignment",
            "Project Management Support",
            "Specialist Trade Capability"
          ],
          "overviewTagline": "Delivery Ecosystem",
          "overviewTitle": "What This Pathway Is About",
          "overviewDesc": "The Projects & Delivery pathway connects active or emerging residential developments with licensed builders, modular manufacturing facilities, specialist trade capability, and project management experts. Le Sector facilitates delivery coordination to ensure projects execute efficiently, on budget, and to precise technical standards.",
          "overviewSubText": "Whether a project requires traditional custom construction or offsite volumetric modular manufacturing, Le Sector connects project owners with the right execution partners.",
          "cardTitle": "Integrated Delivery Coordination",
          "cardDesc": "Le Sector matches project specifications with qualified builders, modular plants, and specialized trade capability.",
          "whoTagline": "Target Participants",
          "whoIsItForTitle": "Who This Is For",
          "whoIsItForItems": [
            {
              "title": "Licensed Builders & Contractors",
              "desc": "Seeking pipeline access to vetted residential and modular construction projects."
            },
            {
              "title": "Modular Manufacturers",
              "desc": "Factory plants seeking production pipeline volume and site assembly partnerships."
            },
            {
              "title": "Specialist Trades & Delivery Partners",
              "desc": "Trade contractors, site managers, and technical specialists looking for project involvement."
            }
          ],
          "typesTagline": "Opportunity Scope",
          "opportunityTypesTitle": "What Opportunities May Exist",
          "opportunityTypesItems": [
            {
              "title": "Projects Seeking Licensed Builders",
              "desc": "Residential developments requiring head contractor build capability and site management.",
              "tag": "Builder Required"
            },
            {
              "title": "Modular Manufacturing Alignment",
              "desc": "Projects seeking factory prefabrication capacity for wall panels, volumetric pods, or steel frames.",
              "tag": "Modular Capacity"
            },
            {
              "title": "Construction Delivery Partnerships",
              "desc": "Joint delivery models combining site preparation with offsite modular installation.",
              "tag": "Delivery Partner"
            },
            {
              "title": "Project Management Support",
              "desc": "Developments requiring specialized client-side project management and quality control.",
              "tag": "Project Support"
            }
          ],
          "facTagline": "Facilitated Execution",
          "facilitationTitle": "How Le Sector Facilitates",
          "facilitationSteps": [
            {
              "step": "01",
              "title": "Project Scoping",
              "desc": "Evaluating project drawings, engineering specs, and delivery schedule requirements."
            },
            {
              "step": "02",
              "title": "Capability Matching",
              "desc": "Identifying builders or factories with matching capacity, licencing, and geographic scope."
            },
            {
              "step": "03",
              "title": "Facilitated Introduction",
              "desc": "Conducting structured introductions between project owners and delivery teams."
            },
            {
              "step": "04",
              "title": "On-Site Coordination",
              "desc": "Supporting factory-to-site logistics and installation quality standards."
            }
          ],
          "scenariosTagline": "Practical Applications",
          "scenariosTitle": "Example Project Scenarios",
          "scenariosItems": [
            {
              "title": "Multi-Unit Modular Build",
              "type": "Modular Capacity",
              "description": "Suburban townhouse project requiring a licensed modular factory partner for 6 two-storey residential modules.",
              "targetMatch": "Developer + Modular Factory"
            },
            {
              "title": "Custom Architectural Home Build",
              "type": "Builder Required",
              "description": "Pre-designed high-end custom residential project seeking a registered builder with modern materials experience.",
              "targetMatch": "Owner + Head Contractor"
            }
          ],
          "resourcesTagline": "Information & References",
          "futureResourcesTitle": "Resources & Delivery Case Studies",
          "futureResourcesDesc": "Access delivery frameworks, modular manufacturing specs, and case studies.",
          "futureResourcesItems": [
            {
              "title": "Modular Assembly & Logistics Guide",
              "description": "Technical guidelines for factory transport, crane positioning, and site connections.",
              "status": "Technical Guide",
              "linkText": "View Logistics Guide"
            },
            {
              "title": "Builder Capability Registration",
              "description": "Parameters required for licensed builders to join the Le Sector delivery network.",
              "status": "Registration Slot",
              "linkText": "Register Capability"
            },
            {
              "title": "Le Sector Delivery Case Studies",
              "description": "Explore completed residential projects delivered through custom and modular methods.",
              "status": "Case Studies",
              "linkText": "Explore Case Studies"
            }
          ],
          "ctaBox": {
            "title": "Have a Project or Delivery Capability?",
            "desc": "Contact a Le Sector advisor to discuss project requirements or join our delivery partner network.",
            "buttonText": "Enquire About Projects & Delivery"
          }
        },
        "professionalExpertise": {
          "badge": "LE SECTOR ECOSYSTEM • PROFESSIONAL EXPERTISE",
          "title": "Professional Expertise Pathway",
          "headline": "Specialist Capabilities & Consultants",
          "subheadline": "Introduce opportunities involving professional and specialist capabilities across design, engineering, planning, and advisory.",
          "heroCta": "Enquire About Professional Expertise",
          "secondaryCta": "View Opportunity Directory",
          "backToAll": "Back to All Opportunities",
          "highlights": [
            "Architects & Designers",
            "Structural & Civil Engineers",
            "Town Planners & Surveyors",
            "Specialist Consultants"
          ],
          "overviewTagline": "Expertise Ecosystem",
          "overviewTitle": "What This Pathway Is About",
          "overviewDesc": "The Professional Expertise pathway connects registered architects, structural engineers, civil engineers, town planners, surveyors, building consultants, and financial advisors with early-stage residential projects. Le Sector incorporates specialist expertise early in the project lifecycle to optimize design, zoning compliance, and modular buildability.",
          "overviewSubText": "By involving professionals early, project outcomes achieve higher architectural quality, faster planning approvals, and seamless integration with modern construction methods.",
          "cardTitle": "Structured Consultant Network",
          "cardDesc": "Le Sector connects specialist consultants with residential project opportunities through a structured, confidential introduction process.",
          "whoTagline": "Target Participants",
          "whoIsItForTitle": "Who This Is For",
          "whoIsItForItems": [
            {
              "title": "Architects & Building Designers",
              "desc": "Seeking custom and modular residential design opportunities."
            },
            {
              "title": "Engineers & Town Planners",
              "desc": "Structural, civil, and planning consultants specializing in residential subdivisions and modular integration."
            },
            {
              "title": "Specialist Advisory Consultants",
              "desc": "Surveyors, certifiers, quantity surveyors, and project finance specialists."
            }
          ],
          "typesTagline": "Opportunity Scope",
          "opportunityTypesTitle": "What Opportunities May Exist",
          "opportunityTypesItems": [
            {
              "title": "Architectural & DfMA Design",
              "desc": "Design engagements tailored for Design for Manufacture and Assembly (DfMA).",
              "tag": "Design Integration"
            },
            {
              "title": "Engineering & Structural Review",
              "desc": "Civil and structural engineering for modular foundations and site works.",
              "tag": "Engineering"
            },
            {
              "title": "Planning & Zoning Approvals",
              "desc": "Town planning advisory for complex residential subdivisions and infill sites.",
              "tag": "Town Planning"
            },
            {
              "title": "Building Certification & Compliance",
              "desc": "Consultancy for building code compliance, energy rating, and certification.",
              "tag": "Compliance"
            }
          ],
          "facTagline": "Facilitated Execution",
          "facilitationTitle": "How Le Sector Facilitates",
          "facilitationSteps": [
            {
              "step": "01",
              "title": "Expertise Scoping",
              "desc": "Identifying project technical requirements during early design phases."
            },
            {
              "step": "02",
              "title": "Consultant Alignment",
              "desc": "Matching project requirements with specialized consultant expertise."
            },
            {
              "step": "03",
              "title": "Structured Introduction",
              "desc": "Facilitating confidential briefs between project owners and consultants."
            },
            {
              "step": "04",
              "title": "Integrated Collaboration",
              "desc": "Supporting collaborative design workflows with manufacturing and site teams."
            }
          ],
          "scenariosTagline": "Practical Applications",
          "scenariosTitle": "Example Project Scenarios",
          "scenariosItems": [
            {
              "title": "DfMA Architectural Design Brief",
              "type": "Design Integration",
              "description": "A custom residential project seeking an architect experienced in modular manufacturing constraints to refine early concept designs.",
              "targetMatch": "Project Owner + Modular Architect"
            },
            {
              "title": "Subdivision Town Planning Approval",
              "type": "Town Planning",
              "description": "Landowner requiring town planning consultancy to navigate multi-lot subdivision approval with local council.",
              "targetMatch": "Landowner + Town Planner"
            }
          ],
          "resourcesTagline": "Information & References",
          "futureResourcesTitle": "Resources & Professional Reference Links",
          "futureResourcesDesc": "Access DfMA design frameworks, planning guidelines, and consultant network information.",
          "futureResourcesItems": [
            {
              "title": "Modular Design (DfMA) Guidelines",
              "description": "Technical handbook on module transport dimensions, connection joints, and structural grids.",
              "status": "Technical Handbook",
              "linkText": "View DfMA Guidelines"
            },
            {
              "title": "Consultant Network Registration",
              "description": "Join the Le Sector professional register for early-stage project introductions.",
              "status": "Registration Slot",
              "linkText": "Join Consultant Network"
            },
            {
              "title": "Integrated Case Studies",
              "description": "Examples of successful architect-engineer-builder collaborations in custom residential delivery.",
              "status": "Case Studies",
              "linkText": "Explore Integrated Projects"
            }
          ],
          "ctaBox": {
            "title": "Are You a Professional Specialist or Looking for Expertise?",
            "desc": "Connect with Le Sector to discuss project consulting opportunities or join our professional network.",
            "buttonText": "Enquire About Professional Expertise"
          }
        }
      }
    }
  },
  "zh": {
    "promo": {
      "open": "打开促销优惠",
      "latestUpdates": "最新消息",
      "close": "关闭促销弹窗",
      "prev": "上一张",
      "next": "下一张",
      "goto": "转到",
      "dontShowAgain": "不再显示"
    },
    "nav": {
      "home": "首页",
      "about": "关于我们",
      "services": "服务项目",
      "howItWorks": "运作流程",
      "faq": "常见问题",
      "contact": "联系我们",
      "hub": "广告中心"
    },
    "footer": {
      "tagline": "传统与模块化定制住宅。可靠交付。",
      "mission": "我们提供传统定制住宅与模块化住宅的专业项目管理和一站式工程交付，为客户图纸提供透明、严谨的流程管理与绝对确定性。",
      "col1": "小工具",
      "col2": "服务",
      "col3": "联系方式",
      "col4": "合作伙伴与法律",
      "gadgets": {
        "calculator": "成本计算器",
        "measurer": "现场测量员",
        "design": "设计工作室"
      },
      "serviceList": [
        "模块化住宅交付",
        "设计可行性",
        "模块化优化",
        "施工前协调"
      ],
      "contactDetails": {
        "phone": "0432 866 168",
        "area": "澳大利亚 (维多利亚)",
        "hours": "周一至周五, 上午9:00 – 下午5:00"
      },
      "partners": [
        {
          "name": "1st Mortgage Group",
          "role": "抵押贷款经纪人",
          "url": "https://www.1stmg.com.au"
        },
        {
          "name": "8 Miles Estate",
          "role": "房地产中介",
          "url": "https://www.8milesestate.com.au"
        },
        {
          "name": "Core-Base Construction",
          "role": "地基与土方工程",
          "url": "https://www.corebasecons.com.au"
        }
      ],
      "legal": [
        "隐私政策",
        "条款与条件"
      ],
      "copyright": "© Le Sector 2025. 版权所有。",
      "disclaimer": "Le Sector 提供模块化住房交付和协调服务。设计服务由外部建筑师和设计师提供。受当地市政审批和现场条件限制。",
      "cta": {
        "title": "面临土地或财务挑战？",
        "sub": "与我们的战略合作伙伴沟通，解决您的施工前难题。",
        "btn": "联系合作伙伴"
      }
    },
    "home": {
      "heroTitle": "Le Sector 项目交付。传统与模块化定制住宅的管理与可靠交付。",
      "heroSub": "我们帮助客户为设计项目选择最适合的建造途径——通过专业严谨的项目管理，将传统定制住宅与模块化住宅从规划到竣工安全交付。",
      "ctaStart": "开启您的建造之旅",
      "ctaSpeak": "与我们的团队沟通",
      "whoTitle": "我们是谁",
      "whoMain": "Le Sector 是一家专业的项目管理与工程交付机构，专注于传统定制住宅与模块化住宅。",
      "whoSub1": "我们统筹规划、采购、专业顾问、现场施工与模块化生产——让业主、建筑师与开发商能根据项目愿景和预算，自主选择最匹配的建造模式。",
      "whoSub2": "无论是传统定制住宅还是模块化住宅，所有项目均遵循我们统一严谨的项目管理体系—以 ISO 管理框架为基础，确保全过程的高标准质量保证、透明文档和严谨的工程交付。",
      "salesPoints": {
        "warranty": {
          "title": "受监管的保修",
          "desc": "完全标准的建筑保修保护（7年），就像受监管的传统建筑一样。"
        },
        "delays": {
          "title": "无现场延误",
          "desc": "现场绝无劳工阶段。工厂制造消除了因雨天延误和工种衔接的问题。"
        },
        "fixedPrice": {
          "title": "固定价格合同",
          "desc": "保持固定价格。我们在制造开始前锁定成本，提供完全的财务确定性。"
        },
        "costRises": {
          "title": "无成本上涨",
          "desc": "绝无材料成本上涨。我们预先采购材料，保护您免受通货膨胀影响。"
        }
      },
      "lowRisk": {
        "badge": "财务优势",
        "title": "低风险模块化建筑模型",
        "desc": "Le Sector 提供一种旨在降低房主财务风险的模块化住宅交付模式。与通常需要逐步发放建筑贷款并持续支付利息的传统建筑项目不同，我们的模块化交付方法允许客户在施工阶段以显着降低的财务风险推进项目。",
        "cardTitle": "竣工后付款",
        "highlight": "从地基（楼板）开始施工，随后进行模块化房屋的组装和安装。在此过程中，客户仅需支付总合同价值5%起的押金。剩余的尾款将在房屋完工并准备移交时结清。",
        "keyAdvantages": "主要优势",
        "bullets": [
          "在许多情况下无需传统的建筑贷款",
          "减少项目延期和成本不确定性的风险",
          "在施工期间无需持续支付利息",
          "从项目开始到完工拥有更高的确定性",
          "付款与房屋最终交付保持一致"
        ],
        "conclusion": "通过将更多的建筑风险从房主转移到可控的交付过程中，Le Sector 为建造新房提供了一条更安全、更可预测的途径。"
      }
    },
    "ecosystem": {
      "tag": "集成生态系统",
      "title": "Le Sector + CoreBase",
      "desc": "Le Sector 与 CoreBase Construction（地基和土方工程专家）紧密合作，确保施工准备与结构集成从第一天起就保持一致。",
      "quote": "这种合作消除了模块化制造与现场施工之间常见的断层——从而实现更顺畅的交付并减少意外。",
      "leSectorSubtitle": "设计与模块化",
      "coreBaseSubtitle": "地基工程"
    },
    "process": {
      "tag": "我们的流程",
      "title": "流程驱动的信心",
      "steps": [
        {
          "num": "01",
          "title": "设计审查与可行性",
          "desc": "我们评估图纸的模块化适用性和合规性——在不影响设计的前提下识别效率。"
        },
        {
          "num": "02",
          "title": "模块化优化",
          "desc": "为离岸建造精炼方案，提高速度、成本确定性和质量控制。"
        },
        {
          "num": "03",
          "title": "施工前协调",
          "desc": "我们协调顾问、制造和现场要求，消除断层。"
        },
        {
          "num": "04",
          "title": "模块化交付",
          "desc": "模块在工厂生产并精准交付，由地基工程提供支持。"
        }
      ],
      "finalCta": "准备好探索模块化交付了吗？",
      "bookBtn": "预约咨询"
    },
    "about": {
      "tag": "我们的方法",
      "title": "建筑至上。交付始终。",
      "sub1": "Le Sector 的成立是为了满足对设计驱动型模块化住宅日益增长的需求——在不牺牲建筑质量的前提下，满足对速度和确定性的要求。",
      "sub2": "我们不提供预设计的套件，而是专门通过模块化建筑方法交付客户设计的图纸。",
      "approachTitle": "我们对现代模块化住宅的方法",
      "approachP1": "在 Le Sector，我们专注于交付由工程钢框架结构和先进轻质混凝土墙板建造的现代模块化住宅。",
      "approachP2": "我们的建筑系统旨在提供永久性住宅所期望的耐用性、安全性和舒适性，同时受益于模块化建筑的高效性。",
      "approachP3": "与集装箱住宅不同，我们的模块化住宅不是由海运集装箱改装而成的。每栋房屋都是使用结构钢框架和带有集成锁定系统的精密制造墙板专门建造的。这确保了强大的结构完整性、出色的隔热性能以及高效的现场安装。",
      "approachP4": "一旦地基板准备就绪，主要的模块化结构就可以在现场快速安装，从而大大减少了传统建筑通常需要的时间。",
      "approachP5": "我们的目标是将现代建筑技术与周到的项目协调相结合，为房主提供一条从设计到完工的可靠且高效的路径。",
      "roleTitle": "我们的角色",
      "roleSub": "我们不是量产建筑商。我们是交付合作伙伴。",
      "roles": [
        {
          "title": "转化",
          "desc": "将复杂的建筑设计转化为模块化执行。"
        },
        {
          "title": "管理",
          "desc": "管理顾问、制造和现场施工之间的衔接。"
        },
        {
          "title": "清晰",
          "desc": "在整个过程中提供清晰、结构化和确定性的指引。"
        }
      ],
      "integrity": "设计完整性",
      "builtTitle": "为强度、舒适和长寿而建",
      "builtP1": "Le Sector 模块化住宅专为长期居住而设计。我们的结构系统结合了钢框架和先进的轻质混凝土墙板，具有防火、防水功能，并提供强大的隔音和隔热性能。",
      "builtP2": "这种方法确保了模块化建筑不是在质量上的妥协，而是以更高的效率和确定性交付现代住宅的更聪明的方式。"
    },
    "services": {
      "tag": "我们的专业知识",
      "title": "设计驱动的模块化交付",
      "desc": "从第一次设计审查到最终模块安装，对模块化全生命周期的完整监管。",
      "list": [
        "设计可行性评估",
        "模块化优化与协调",
        "顾问与工程对齐",
        "施工前规划",
        "模块化制造协调",
        "交付与安装监管"
      ],
      "feasibility": {
        "title": "模块化可行性评估方案",
        "price": "$490 - $990",
        "tag": "第一步",
        "desc": "我们卖的不是建房，而是“清晰度”。在您签署任何大额合同之前，我们帮您看清这件事是否值得做。",
        "features": [
          "地块可行性初步判断",
          "预算区间分析",
          "时间线预测",
          "风险清单",
          "资金结构建议",
          "成本对比（传统 vs 模块化）"
        ]
      },
      "modelsTitle": "轻资产操作模式",
      "models": [
        {
          "title": "模式 A — 设计协调 + 项目代理",
          "tag": "不承担施工风险",
          "fee": "固定管理费 8–12%",
          "features": [
            "您持有施工合同",
            "我们只做整合与管理",
            "材料零加价",
            "适合自建房主 (Owner-Builders)"
          ]
        },
        {
          "title": "模式 B — 投资人连接",
          "tag": "连接土地与资本",
          "fee": "结构费 + 管理费",
          "features": [
            "找有地但没钱的人",
            "找有钱但没项目的人",
            "中间结构设计与撮合",
            "全流程项目交付管理"
          ]
        }
      ],
      "groundTitle": "地基与现场准备",
      "groundTag": "由 CoreBase 交付",
      "groundList": [
        {
          "title": "地基",
          "desc": "根据模块重量和现场条件优化的专业工程地基。"
        },
        {
          "title": "土方工程",
          "desc": "完整的土建工程、排水和公用设施开槽。"
        },
        {
          "title": "结构准备",
          "desc": "精确放样，确保模块与地面的接口毫米级完美。"
        }
      ]
    },
    "faq": {
      "tag": "信任与清晰",
      "title": "常见问题",
      "items": [
        {
          "q": "你们提供设计吗？",
          "a": "不提供。我们根据客户设计的图纸或外部建筑师的方案工作。我们的价值在于执行和交付您的特定愿景。"
        },
        {
          "q": "模块化更便宜吗？",
          "a": "模块化关乎确定性和效率，而非偷工减料。它提供可预测的成本和显著缩短的工期，但质量保持高端。"
        },
        {
          "q": "设计可以定制吗？",
          "a": "可以——我们的流程专门为支持定制化建筑成果而构建。我们不会强迫您选择“套娃式”住宅。"
        },
        {
          "q": "谁负责地基？",
          "a": "地基由 CoreBase Construction 协调交付，以确保地面与模块之间的无缝衔接。"
        }
      ]
    },
    "contact": {
      "tag": "让我们开始讨论",
      "title": "您的项目启动",
      "desc": "在墨尔本及维多利亚周边地区提供设计驱动的模块化交付服务。",
      "labels": {
        "phone": "直线电话",
        "email": "电子邮件",
        "area": "服务区域",
        "formTitle": "咨询申请",
        "name": "全名",
        "brief": "项目简介",
        "placeholder": "简要描述您的愿景...",
        "submit": "预约咨询"
      }
    },
    "calculator": {
      "disclaimer": "* 免责声明：所有价钱要以合同价为准。"
    },
    "ownerPartnership": {
      "hero": {
        "tagline": "战略建筑咨询",
        "title1": "战略交付。",
        "title2": "绝对财务控制。",
        "desc": "通过您身边的专家级项目管理办公室（PMO），应对复杂的定制建筑项目。您将保留完全的财务控制权，而我们负责推动日常执行。",
        "cta": "预约咨询"
      },
      "why": {
        "tagline": "咨询优势",
        "title": "重塑客户与建筑商的关系",
        "p1": "传统的固定价格合同本质上将建筑商的利润置于房主的预算之上。变更变成纠纷，不透明成为经济盾牌。我们创建业主合伙模式来完全消除这种摩擦。",
        "p2": "通过透明的管理费运作，我们的经济激励与您完全一致：在不妥协的情况下，尽可能高效地交付卓越的建筑资产。",
        "cardTitle": "真正的一致性",
        "cardDesc": "共同的目标取代传统的冲突。",
        "b1Title": "100%成本透明",
        "b1Desc": "每项贸易折扣和节省的资金都直接返还给您。",
        "b2Title": "绝对安全",
        "b2Desc": "您持有项目资金。在您批准工作之前，资金绝不会离开您的账户。"
      },
      "process": {
        "tagline": "战略分期",
        "title": "交付方法论",
        "steps": [
          {
            "title": "战略对接",
            "desc": "我们分析您的建筑图纸，建立现实的预算框架，并制定总体交付战略。"
          },
          {
            "title": "透明采购",
            "desc": "我们将您的项目招标给经过我们严格审查的优质分包商网络，确保将具有竞争力的市场价格直接传递给您。"
          },
          {
            "title": "精准执行",
            "desc": "我们专业的现场管理团队协调日常运营，执行严格的质量控制并遵守总体时间表。"
          },
          {
            "title": "高质量移交",
            "desc": "我们进行详尽的缺陷审查，最终确定认证，并正式移交您已完成的建筑资产。"
          }
        ]
      },
      "matrix": {
        "tagline": "明确范围",
        "title": "执行的清晰度",
        "ownerTitle": "业主",
        "ownerItems": [
          "在专用私人账户中保留资金",
          "授权所有主要设计和范围变更",
          "批准并向分包商发放里程碑付款",
          "保持最终的执行监督"
        ],
        "sharedTitle": "共同责任",
        "sharedItems": [
          "持续的预算优化和审查",
          "主动的价值工程决策",
          "建筑饰面和材料选择",
          "全面的每月进度评估"
        ],
        "lsTitle": "Le Sector (PMO)",
        "lsItems": [
          "全面的现场管理和WHS合规",
          "严格的分包商采购和微观调度",
          "毫不妥协的质量保证和缺陷解决",
          "端到端的市政和认证协调"
        ]
      },
      "flow": {
        "tagline": "资本保护",
        "title": "毫不妥协的财务安全",
        "p1": "定制建筑中最大的风险是在没有透明度的情况下失去对资金的控制。",
        "p2": "我们的模式完全降低了这种风险。Le Sector 从不持有您的建设资金。我们严格认证工作，但您保留释放付款的权力。",
        "s1Title": "专家认证",
        "s1Desc": "我们的现场经理严格验证分包商的工作是否符合我们的质量标准。",
        "s2Title": "财务验证",
        "s2Desc": "PMO根据批准的招标和采购时间表核对发票。",
        "s3Title": "执行批准",
        "s3Desc": "您授权直接从您的安全账户中向分包商释放资金。"
      },
      "dash": {
        "tagline": "实时情报",
        "title": "全维度的可见性",
        "desc": "消除未知的焦虑。我们的数字基础设施为您提供对项目健康状况、进度和财务状况的执行级可见性。",
        "items": [
          "动态时间表预测",
          "全面的每日现场日志",
          "实时预算差异分析",
          "实时采购跟踪"
        ],
        "hub": "项目中心",
        "budget": "已部署资本",
        "schedule": "总体时间表",
        "onTrack": "进度正常",
        "week": "第14周 (共32周)",
        "cashflow": "预测现金流"
      },
      "suit": {
        "tagline": "客户概况",
        "title": "此模式适合您吗？",
        "idealTitle": "非常适合",
        "idealItems": [
          "正在进行复杂的优质建筑项目的客户",
          "要求绝对财务透明度的房主",
          "寻求执行监督而不希望承担日常管理负担的人",
          "优先考虑成本效率和直接获取分包商资源的开发商"
        ],
        "notTitle": "不适合",
        "notItems": [
          "寻求传统'设定后不管'固定价格合同的客户",
          "不愿意或无法授权每周里程碑付款的人",
          "依赖建筑商提供建设融资的项目",
          "标准化的批量住宅建设"
        ]
      },
      "faq": {
        "tagline": "咨询见解",
        "title": "常见问题",
        "q1": "资本控制机制究竟如何运作？",
        "a1": "您的项目资金安全地保留在您指定的银行账户中。虽然Le Sector专业管理采购、调度和质量认证，但我们无法直接访问您的资金。您作为最终财务授权人，仅在成功达到里程碑时才直接向经验证的分包商发放付款。",
        "q2": "如果Le Sector不持有资金，你们的责任范围是什么？",
        "a2": "我们作为您专属的项目管理办公室(PMO)和精英现场团队。我们承担战略规划、严格采购、日常现场管理、毫不妥协的质量控制和进度协调的全部责任。我们执行建筑的复杂现实，让您专注于执行监督。",
        "q3": "这种模式需要我具备先前的施工经验吗？",
        "a3": "完全不需要。事实上，与传统合同相比，我们的模式提供了更多的指导和保护。通过将我们透明的报告基础设施与我们的专家咨询团队相结合，您能够在不需要专业施工知识的情况下做出明智的决定。"
      },
      "cta": {
        "title": "开始对话",
        "desc": "安排私人咨询，探索我们的咨询模式如何保护您的资金并提升您的建筑项目的交付。"
      },
      "form": {
        "step": "步骤",
        "of": "/",
        "s1Title": "项目类型",
        "s1Opts": [
          "单户住宅",
          "多单元开发",
          "商业项目",
          "其他"
        ],
        "s2Title": "项目阶段",
        "s2Opts": [
          "概念/想法",
          "规划/设计",
          "市政审批",
          "准备建设"
        ],
        "s3Title": "项目地点",
        "s3Ph": "郊区或邮政编码",
        "s4Title": "预计建设预算",
        "s4Opts": [
          "50万以下",
          "50万-100万",
          "100万-250万",
          "250万以上"
        ],
        "s5Title": "所需服务",
        "s5Opts": [
          "可行性研究",
          "建筑设计",
          "项目管理",
          "施工交付"
        ],
        "s6Title": "客户优先事项",
        "s6Opts": [
          "成本效率",
          "卓越品质",
          "交付速度",
          "最大透明度"
        ],
        "s7Title": "时间表",
        "s7Opts": [
          "尽快",
          "3-6个月",
          "6-12个月",
          "12个月以上"
        ],
        "s8Title": "附加信息",
        "s8Ph": "告诉我们更多关于您的项目愿景...",
        "s9Title": "联系方式",
        "s9Ph1": "全名",
        "s9Ph2": "电子邮件",
        "s9Ph3": "电话号码",
        "back": "返回",
        "continue": "继续",
        "submit": "预约咨询",
        "submitting": "提交中...",
        "error": "请填写所有必填字段。",
        "emailError": "请输入有效的电子邮件地址。",
        "submitError": "发生错误，请重试。"
      },
      "confirm": {
        "title": "申请已收到",
        "desc": "感谢您的关注。我们将审查您的项目详情并在24小时内与您联系。",
        "nextSteps": "下一步",
        "bookTitle": "初步咨询",
        "bookDesc": "我们的顾问将与您联系以安排您的介绍性通话。",
        "dlTitle": "项目审查",
        "dlDesc": "我们将对您的项目细节进行初步评估。"
      }
    },
    "exploreOpportunities": {
      "hero": {
        "badge": "LE SECTOR 生态系统 • 专业撮合与引荐",
        "headline1": "每一个伟大的项目，都始于正确的链接。",
        "subheadline": "探索项目合作与机遇，由 Le Sector 提供专业管理与引荐服务。",
        "primaryCta": "探索合作机遇",
        "secondaryCta": "告诉我们您的需求"
      },
      "pathways": {
        "tagline": "生态支柱",
        "title": "您能探索什么？",
        "desc": "发掘住宅地产、开发资金和模块化项目交付方面的活跃机会。",
        "items": [
          {
            "title": "土地与地产",
            "desc": "适合定制住宅或模块化交付的土地机遇、开发潜力及地产相关项目。"
          },
          {
            "title": "投资与资本",
            "desc": "经审核的住宅建筑方案的投资机遇、资金需求及潜在资本匹配。"
          },
          {
            "title": "项目与交付",
            "desc": "寻找持牌建造商、开发商、专业制造能力或交付合作伙伴的活跃项目。"
          },
          {
            "title": "专业技术",
            "desc": "寻求项目前期参与的建筑师、结构工程师、城市规划师及专业顾问。"
          }
        ]
      },
      "howItWorks": {
        "tagline": "引荐流程",
        "title": "运作流程",
        "steps": [
          {
            "step": "01",
            "title": "探索",
            "desc": "浏览潜在合作与机遇，无需公开个人联系方式或具体地块地址。"
          },
          {
            "step": "02",
            "title": "表达意向",
            "desc": "告诉 Le Sector 哪个项目或合作方向契合您的目标与能力。"
          },
          {
            "step": "03",
            "title": "专业审核",
            "desc": "Le Sector 团队审核意向，验证真实意图，评估项目可行性。"
          },
          {
            "step": "04",
            "title": "双向引荐",
            "desc": "在适当情况下，Le Sector 安排双重同意的专业对接与引荐。"
          },
          {
            "step": "05",
            "title": "深入探讨",
            "desc": "在 Le Sector 项目管理专业能力支持下，各方共同评估合作协同效益。"
          }
        ]
      },
      "why": {
        "tagline": "Le Sector 核心优势",
        "title": "为什么选择 Le Sector？",
        "desc": "专业管理的合作网络，远离未经审核的论坛、社交媒体噪音和冷不防的推销。",
        "features": [
          {
            "title": "隐私第一",
            "desc": "绝不公开电话号码、电子邮件或具体物业地址。"
          },
          {
            "title": "专业审核",
            "desc": "所有项目与意向在引荐前均经过严谨评估。"
          },
          {
            "title": "结构化对接",
            "desc": "Le Sector 全程管理并调和首次引荐，而非简单抛出联系方式。"
          },
          {
            "title": "项目交付专业能力",
            "desc": "直接由 Le Sector 规范的定制住宅与模块化交付框架提供依托。"
          },
          {
            "title": "符合 ISO 的质量管理",
            "desc": "以结构化管理框架和 ISO 对齐的质量保证原则为指导。"
          },
          {
            "title": "零商业噪音",
            "desc": "绝无垃圾广告、冷推销电话或未经审核的集市广告。"
          }
        ]
      },
      "scenarios": {
        "tagline": "典型场景",
        "title": "合作场景示例",
        "desc": "展示利益相关者如何在我们的生态系统中建立联系的真实示例。（示意场景 — 与实时目录列表区分）。",
        "badge": "案例场景",
        "items": [
          {
            "title": "我有土地",
            "quote": "“我想在不清公开挂牌的情况下，了解我的物业存在哪些潜在的开发合作机会。”",
            "targetMatch": "开发商、股权投资者、模块化专家"
          },
          {
            "title": "我有资本",
            "quote": "“我在寻找有结构化项目管理保障的地产或开发投资机遇。”",
            "targetMatch": "地主、合规开发商、定制项目"
          },
          {
            "title": "我有项目",
            "quote": "“我在寻找合适的交付团队、建造商或投资伙伴，以推动项目进入施工阶段。”",
            "targetMatch": "持牌建造商、股权伙伴、项目总监"
          },
          {
            "title": "我有专业能力",
            "quote": "“我在寻找合适的住宅项目，可以发挥我专长设计或工程技术。”",
            "targetMatch": "项目业主、开发商、规划顾问"
          }
        ]
      },
      "directory": {
        "tagline": "活跃目录",
        "title": "当前项目与合作机遇",
        "filterAllCategories": "所有类别",
        "filterAllModes": "所有交付模式",
        "modeTraditional": "传统定制",
        "modeModular": "模块化组装",
        "modeHybrid": "混合交付",
        "emptyState": {
          "title": "新项目目前正在审核中。",
          "desc": "告诉我们您的需求，我们将帮助确认 Le Sector 网络中是否有合适的合作机会。",
          "cta": "告诉我们您的需求"
        },
        "card": {
          "ref": "编号",
          "status": "状态",
          "location": "位置",
          "stage": "阶段",
          "type": "项目类型",
          "budget": "预算范围",
          "seeking": "寻求",
          "timeframe": "时间线",
          "expressInterest": "表达意向 (EOI)",
          "viewDetails": "查看摘要"
        }
      },
      "privacy": {
        "tagline": "保密与安全",
        "title": "隐私、保密与信任保障",
        "desc": "我们在每一步都保护您的身份、项目详情和商业利益。",
        "items": [
          {
            "title": "无公开联系信息",
            "desc": "您的个人身份、电话和电子邮件绝不会被公开或自动共享。"
          },
          {
            "title": "审慎引荐",
            "desc": "所有初始咨询在正式引荐前均由 Le Sector 顾问进行筛选。"
          },
          {
            "title": "双重同意",
            "desc": "仅在初始筛选后双方均明确同意的情况下才会进行正式引荐。"
          }
        ]
      },
      "cta": {
        "title": "心中已有合作想法？",
        "desc": "告诉我们您的需求，让 Le Sector 帮助您在我们的网络中寻找合适的链接。",
        "button": "告诉我们您的需求"
      },
      "formModal": {
        "title": "保密合作意向咨询",
        "sub": "请告诉我们您的需求或合作想法。Le Sector 顾问将全程高度保密地审阅您的咨询。",
        "roleLabel": "您的主要身份",
        "roles": {
          "landOwner": "地产 / 土地业主",
          "investor": "私人 / 股权投资者",
          "projectOwner": "项目开发商 / 业主",
          "expert": "专业人士 / 行业专家"
        },
        "name": "姓名",
        "email": "电子邮箱",
        "phone": "电话号码",
        "region": "目标区域 / 位置",
        "category": "感兴趣类别",
        "details": "项目参数及您的诉求",
        "disclaimer": "您的信息将被严格保密。未经您的明确同意，绝不会公开或向第三方分享任何联系方式或确切物业地址。",
        "submit": "提交咨询",
        "successTitle": "咨询已收到",
        "successDesc": "感谢您的联系。参考编号：",
        "successSub": "Le Sector 顾问将严格保密地审阅您的咨询，并在 24-48 小时内与您联系。",
        "close": "关闭"
      },
      "pathwayDetails": {
        "propertySites": {
          "badge": "LE SECTOR 生态系统 • 土地与物业",
          "title": "土地与物业通道",
          "headline": "土地开发与细分分割潜能",
          "subheadline": "探索涉及土地、开发地块以及适合定制住宅或模块化建造的物业合作与开发机会。",
          "heroCta": "咨询土地与物业合作",
          "secondaryCta": "查看机会目录",
          "backToAll": "返回所有合作机会",
          "highlights": [
            "土地细分评估",
            "开发潜能诊断",
            "模块化建造场地物流",
            "全程保密撮合"
          ],
          "overviewTagline": "土地与物业生态",
          "overviewTitle": "本通道的核心定位",
          "overviewDesc": "土地与物业通道将地主、不动产持有者及物业合作伙伴与结构化的开发和建造能力对接。无论是评估绿地分割潜能、城市填充式再开发，还是模块化定制住宅的适用性，Le Sector 均提供早期协调与可行性匹配。",
          "overviewSubText": "Le Sector 充当专业撮合引荐者——协助业主评估开发潜能，并协调经验丰富的交付伙伴，无需承担公开挂牌的市场压力。",
          "cardTitle": "保密场地协调",
          "cardDesc": "Le Sector 进行初步场地评估，并在严格保密的前提下将土地所有者与合适的交付团队进行对接。",
          "whoTagline": "目标参与者",
          "whoIsItForTitle": "适用对象",
          "whoIsItForItems": [
            {
              "title": "地主与不动产持有者",
              "desc": "探索土地细分分割潜能或用于定制住宅/模块化开发的土地价值最大化。"
            },
            {
              "title": "土地开发合作伙伴",
              "desc": "寻求区位优越的分割地块或有兴趣进行结构化住宅联合开发的地主。"
            },
            {
              "title": "再开发项目业主",
              "desc": "拥有现有住宅物业并寻求旧房翻建或模块化重建合作的业主。"
            }
          ],
          "typesTagline": "合作机会范围",
          "opportunityTypesTitle": "潜在的机会类型",
          "opportunityTypesItems": [
            {
              "title": "住宅细分分割地块",
              "desc": "适合多地块住宅分割或现代模块化总体规划的土地。",
              "tag": "土地分割"
            },
            {
              "title": "填充式开发地块",
              "desc": "适合定制建筑住宅或预制模块化建房的城市及郊区住宅地块。",
              "tag": "旧房再开发"
            },
            {
              "title": "合资开发合作",
              "desc": "地主与经验丰富的开发商及建造网络合作的机会。",
              "tag": "合资开发"
            },
            {
              "title": "模块化兼容地块",
              "desc": "经评估符合吊车入场、工厂组装物流及快速模块化吊装条件的地块。",
              "tag": "模块化就绪"
            }
          ],
          "facTagline": "专业撮合流程",
          "facilitationTitle": "Le Sector 如何协助您",
          "facilitationSteps": [
            {
              "step": "01",
              "title": "场地评估",
              "desc": "初步评估场地朝向、交通入场、规划法规及模块化建造可行性。"
            },
            {
              "step": "02",
              "title": "结构与策略",
              "desc": "确定最佳项目路径——定制建造、地块分割或联合交付。"
            },
            {
              "step": "03",
              "title": "保密匹配",
              "desc": "在不公开暴露信息的前提下引荐经过审核的交付伙伴或资金。"
            },
            {
              "step": "04",
              "title": "执行协调",
              "desc": "在整个交付过程中协调建筑设计、工程和制造团队。"
            }
          ],
          "scenariosTagline": "实际应用场景",
          "scenariosTitle": "典型项目场景示例",
          "scenariosItems": [
            {
              "title": "3地块住宅分割项目",
              "type": "分割潜能",
              "description": "郊区大占地地主希望分割为3个住宅地块，采用工厂制造的模块化住宅快速完成项目交付。",
              "targetMatch": "地主 + 模块化交付网络"
            },
            {
              "title": "旧房填充式再开发",
              "type": "再开发地块",
              "description": "业主希望拆除陈旧住宅，新建现代双联体定制住宅。",
              "targetMatch": "地主 + 定制建造商 / 设计师"
            }
          ],
          "resourcesTagline": "信息与参考",
          "futureResourcesTitle": "项目资源与参考链接",
          "futureResourcesDesc": "获取场地规划清单、模块化场地可行性指南及相关 Le Sector 专业服务。",
          "futureResourcesItems": [
            {
              "title": "模块化场地可行性清单",
              "description": "关于吊车通道、退界要求、市政管线连接和坡度评估的指南。",
              "status": "参考指南",
              "linkText": "查看可行性框架"
            },
            {
              "title": "住宅分割信息简报模板",
              "description": "向开发与交付合作伙伴展示土地地块的标准化参数格式。",
              "status": "信息模板",
              "linkText": "索取简报格式"
            },
            {
              "title": "Le Sector 场地咨询服务",
              "description": "了解我们的咨询团队如何开展前期场地与制造兼容性评估。",
              "status": "服务概览",
              "linkText": "探索场地咨询"
            }
          ],
          "ctaBox": {
            "title": "拥有土地或物业合作意向？",
            "desc": "与 Le Sector 顾问沟通，评估场地潜能并探讨保密撮合方案。",
            "buttonText": "咨询土地与物业合作"
          }
        },
        "investmentCapital": {
          "badge": "LE SECTOR 生态系统 • 投资与资本",
          "title": "投资与资本通道",
          "headline": "资本与开发项目精准对接",
          "subheadline": "将开发资本与优质的住宅开发、土地项目及建筑交付机会进行结构化对接。",
          "heroCta": "咨询投资与资本合作",
          "secondaryCta": "查看机会目录",
          "backToAll": "返回所有合作机会",
          "highlights": [
            "开发资本对接",
            "实物资产抵押项目",
            "专业项目风控治理",
            "结构化撮合合作"
          ],
          "overviewTagline": "资本生态",
          "overviewTitle": "本通道的核心定位",
          "overviewDesc": "投资与资本通道将资本与经过严格审核的土地、开发和建筑项目连接起来。Le Sector 扮演专业撮合者和项目交付合作伙伴的角色，确保严谨的前期审核、项目治理与执行结构。",
          "overviewSubText": "Le Sector 并非公开投资市场或公开基金，而是在严格保密的情况下将资本与结构化的住宅及模块化建造项目进行匹配。",
          "cardTitle": "专业撮合与交付伙伴",
          "cardDesc": "Le Sector 在严格的保密与风险治理框架下，协调资本与项目交付团队的精准对接。",
          "whoTagline": "目标参与者",
          "whoIsItForTitle": "适用对象",
          "whoIsItForItems": [
            {
              "title": "私人及股权投资者",
              "desc": "寻求参与经过审核的住宅建造及开发项目的结构化投资。"
            },
            {
              "title": "开发资金合作伙伴",
              "desc": "寻求管理规范、有实物资产保障且交付节点清晰的住宅项目。"
            },
            {
              "title": "项目开发商",
              "desc": "为其住宅及模块化建造项目寻求股权或开发资金对接。"
            }
          ],
          "typesTagline": "合作机会范围",
          "opportunityTypesTitle": "潜在的机会类型",
          "opportunityTypesItems": [
            {
              "title": "股权合作架构",
              "desc": "具备清晰节点治理的住宅项目联合开发架构。",
              "tag": "股权匹配"
            },
            {
              "title": "开发资金对接",
              "desc": "针对模块化工厂制造和场地交付的项目级资金对接。",
              "tag": "资金结构"
            },
            {
              "title": "实物资产保障项目",
              "desc": "由真实房地产实物资产作为保障的开发机会。",
              "tag": "资产保障"
            },
            {
              "title": "专业管理的建造项目",
              "desc": "由经验丰富的交付团队全程管理的投资合作项目。",
              "tag": "托管交付"
            }
          ],
          "facTagline": "专业撮合流程",
          "facilitationTitle": "Le Sector 如何协助您",
          "facilitationSteps": [
            {
              "step": "01",
              "title": "项目审核",
              "desc": "对项目参数、规划法规和交付可行性进行初步尽职调查。"
            },
            {
              "step": "02",
              "title": "架构匹配",
              "desc": "构建符合项目规模和风险偏好的合作参与模式。"
            },
            {
              "step": "03",
              "title": "保密引荐",
              "desc": "将资金方直接引荐给项目交付主理人。"
            },
            {
              "step": "04",
              "title": "风控与监管",
              "desc": "在整个建造周期内提供项目监督与交付进度跟踪。"
            }
          ],
          "scenariosTagline": "实际应用场景",
          "scenariosTitle": "典型项目场景示例",
          "scenariosItems": [
            {
              "title": "住宅分割开发资本",
              "type": "股权匹配",
              "description": "已获批的4地块定制住宅分割项目，寻求场地工程和模块化建造的股权资本匹配。",
              "targetMatch": "项目开发商 + 股权伙伴"
            },
            {
              "title": "模块化项目资金",
              "type": "开发资金",
              "description": "高收益住宅建造方案，需要结构化资金用于工厂预制与现场安装。",
              "targetMatch": "交付团队 + 资金伙伴"
            }
          ],
          "resourcesTagline": "信息与参考",
          "futureResourcesTitle": "投资资源与信息链接",
          "futureResourcesDesc": "获取风控治理指南、资本对接简报及相关咨询服务。",
          "futureResourcesItems": [
            {
              "title": "资本审核风控框架",
              "description": "Le Sector 用于评估项目可行性及交付就绪度的标准概览。",
              "status": "框架指南",
              "linkText": "查看风控指南"
            },
            {
              "title": "项目简报标准化结构",
              "description": "向潜在合作伙伴展示资金需求的标准化格式。",
              "status": "简报结构",
              "linkText": "索取简报格式"
            },
            {
              "title": "Le Sector 咨询与交付支持",
              "description": "了解我们的团队如何监督项目执行以保障项目交付节点。",
              "status": "服务概览",
              "linkText": "探索咨询服务"
            }
          ],
          "ctaBox": {
            "title": "寻求资本或投资项目对接？",
            "desc": "与 Le Sector 顾问联系，保密沟通资金需求或项目匹配。",
            "buttonText": "咨询投资与资本合作"
          }
        },
        "projectsDelivery": {
          "badge": "LE SECTOR 生态系统 • 项目与交付",
          "title": "项目与交付通道",
          "headline": "交付能力与战略合作伙伴",
          "subheadline": "引荐需要施工交付能力、模块化制造工厂或战略项目合作伙伴的在建与新兴项目。",
          "heroCta": "咨询项目与交付合作",
          "secondaryCta": "查看机会目录",
          "backToAll": "返回所有合作机会",
          "highlights": [
            "持牌建造商对接",
            "模块化工厂匹配",
            "项目管理及技术支持",
            "专业工种团队能力"
          ],
          "overviewTagline": "交付生态",
          "overviewTitle": "本通道的核心定位",
          "overviewDesc": "项目与交付通道将活跃或新兴的住宅开发项目与持牌建造商、模块化制造工厂、专业工种能力及项目管理专家连接起来。Le Sector 协调交付执行，确保项目高效、按预算并符合精准的技术标准完成。",
          "overviewSubText": "无论项目需要传统定制施工还是非现场模块化预制制造，Le Sector 都能为项目业主匹配合适的执行团队。",
          "cardTitle": "一体化交付协调",
          "cardDesc": "Le Sector 将项目规范与具备资质的建造商、模块化工厂及专业团队进行精准匹配。",
          "whoTagline": "目标参与者",
          "whoIsItForTitle": "适用对象",
          "whoIsItForItems": [
            {
              "title": "持牌建造商与承包商",
              "desc": "寻求接入经过审核的住宅及模块化建造项目案源。"
            },
            {
              "title": "模块化制造厂商",
              "desc": "寻求生产管线产能及现场组装合作的预制工厂。"
            },
            {
              "title": "专业工种及交付伙伴",
              "desc": "寻求参与优质项目的分包商、工地经理及技术专家。"
            }
          ],
          "typesTagline": "合作机会范围",
          "opportunityTypesTitle": "潜在的机会类型",
          "opportunityTypesItems": [
            {
              "title": "寻求持牌建造商的项目",
              "desc": "需要总承包施工能力和工地管理的住宅开发项目。",
              "tag": "需建造商"
            },
            {
              "title": "模块化制造产能匹配",
              "desc": "寻求工厂预制墙板、模块化房舱或钢结构框架产能的项目。",
              "tag": "模块化产能"
            },
            {
              "title": "建筑交付联合模式",
              "desc": "结合场地平整与非现场模块化安装的联合交付模式。",
              "tag": "交付伙伴"
            },
            {
              "title": "项目管理及风控支持",
              "desc": "需要专业业主方项目管理和质量控制的项目。",
              "tag": "项目管理"
            }
          ],
          "facTagline": "专业撮合流程",
          "facilitationTitle": "Le Sector 如何协助您",
          "facilitationSteps": [
            {
              "step": "01",
              "title": "项目范围评估",
              "desc": "评估项目图纸、工程规范及交付工期要求。"
            },
            {
              "step": "02",
              "title": "能力匹配",
              "desc": "筛选资质、产能及地理覆盖范围完全匹配的建造商或工厂。"
            },
            {
              "step": "03",
              "title": "保密引荐",
              "desc": "在项目业主与交付团队之间开展结构化保密引荐。"
            },
            {
              "step": "04",
              "title": "现场交付协调",
              "desc": "协助工厂至现场的物流运输及安装质量控制。"
            }
          ],
          "scenariosTagline": "实际应用场景",
          "scenariosTitle": "典型项目场景示例",
          "scenariosItems": [
            {
              "title": "多单元模块化建造项目",
              "type": "模块化产能",
              "description": "郊区联排别墅项目，需要持牌模块化工厂合作伙伴完成6个双层住宅模块的预制与吊装。",
              "targetMatch": "开发商 + 模块化工厂"
            },
            {
              "title": "定制建筑师住宅建造",
              "type": "需建造商",
              "description": "已完成设计的住宅项目，寻求具备现代材料施工经验的注册建造商。",
              "targetMatch": "业主 + 总承包商"
            }
          ],
          "resourcesTagline": "信息与参考",
          "futureResourcesTitle": "交付资源与案例链接",
          "futureResourcesDesc": "获取交付框架、模块化制造规范及案例研究。",
          "futureResourcesItems": [
            {
              "title": "模块化吊装与物流指南",
              "description": "工厂运输、吊车定位及现场管线连接的技术指南。",
              "status": "技术指南",
              "linkText": "查看物流指南"
            },
            {
              "title": "建造商交付能力入库登记",
              "description": "持牌建造商加入 Le Sector 交付网络所需的标准与参数。",
              "status": "登记通道",
              "linkText": "登记交付能力"
            },
            {
              "title": "Le Sector 交付项目案例集",
              "description": "探索通过定制与模块化方式完成的住宅项目案例。",
              "status": "案例集",
              "linkText": "探索项目案例"
            }
          ],
          "ctaBox": {
            "title": "拥有项目需求或交付能力？",
            "desc": "联系 Le Sector 顾问探讨项目需求或加入我们的交付伙伴网络。",
            "buttonText": "咨询项目与交付合作"
          }
        },
        "professionalExpertise": {
          "badge": "LE SECTOR 生态系统 • 专业技能",
          "title": "专业技能通道",
          "headline": "专家能力与顾问团队",
          "subheadline": "引荐涵盖建筑设计、结构工程、城市规划及专业咨询领域的专家与机构。",
          "heroCta": "咨询专业技能合作",
          "secondaryCta": "查看机会目录",
          "backToAll": "返回所有合作机会",
          "highlights": [
            "注册建筑师与设计师",
            "结构及土木工程师",
            "城市规划师与测量师",
            "专业咨询顾问团队"
          ],
          "overviewTagline": "专家生态",
          "overviewTitle": "本通道的核心定位",
          "overviewDesc": "专业技能通道将注册建筑师、结构工程师、土木工程师、城市规划师、测量师、建筑顾问及财务顾问与早期住宅项目无缝对接。Le Sector 在项目早期引入专家技能，以优化设计、规划合规性及模块化建造可行性。",
          "overviewSubText": "通过早期引入专业力量，项目能够实现更高的建筑品质、更快的规划审批以及与现代建造方法的完美融合。",
          "cardTitle": "结构化顾问网络",
          "cardDesc": "Le Sector 通过结构化保密引荐流程，将专业顾问与住宅项目对接。",
          "whoTagline": "目标参与者",
          "whoIsItForTitle": "适用对象",
          "whoIsItForItems": [
            {
              "title": "建筑师与住宅设计师",
              "desc": "寻求高端定制及模块化住宅的设计合作机会。"
            },
            {
              "title": "工程师与城市规划师",
              "desc": "专注于住宅分割及模块化集成的结构、土木及规划顾问。"
            },
            {
              "title": "专业咨询与认证团队",
              "desc": "测量师、认证师、造价师及项目融资专家。"
            }
          ],
          "typesTagline": "合作机会范围",
          "opportunityTypesTitle": "潜在的机会类型",
          "opportunityTypesItems": [
            {
              "title": "建筑与 DfMA 制造设计",
              "desc": "针对面向制造和装配的设计（DfMA）定制的设计委托。",
              "tag": "设计集成"
            },
            {
              "title": "结构与土木工程审查",
              "desc": "针对模块化基础和场地工程的土木及结构工程顾问。",
              "tag": "工程顾问"
            },
            {
              "title": "城市规划与审批咨询",
              "desc": "针对复杂住宅分割及填充地块的城市规划咨询。",
              "tag": "城市规划"
            },
            {
              "title": "建筑认证与合规审查",
              "desc": "关于建筑法规合规性、能源评级及认证的咨询服务。",
              "tag": "法规合规"
            }
          ],
          "facTagline": "专业撮合流程",
          "facilitationTitle": "Le Sector 如何协助您",
          "facilitationSteps": [
            {
              "step": "01",
              "title": "专业需求评估",
              "desc": "在早期设计阶段梳理项目的技术与规范要求。"
            },
            {
              "step": "02",
              "title": "顾问精准匹配",
              "desc": "将项目需求与具备专项能力的顾问专家进行匹配。"
            },
            {
              "step": "03",
              "title": "结构化保密沟通",
              "desc": "在项目业主与专业顾问之间开展保密简报对接。"
            },
            {
              "step": "04",
              "title": "跨团队集成协同",
              "desc": "支持与制造及现场施工团队的协同设计工作流。"
            }
          ],
          "scenariosTagline": "实际应用场景",
          "scenariosTitle": "典型项目场景示例",
          "scenariosItems": [
            {
              "title": "DfMA 模块化建筑设计委托",
              "type": "设计集成",
              "description": "定制住宅项目，寻求熟悉模块化制造限制条件的建筑师进行早期方案优化。",
              "targetMatch": "项目业主 + 模块化建筑师"
            },
            {
              "title": "住宅分割城市规划审批",
              "type": "城市规划",
              "description": "地主需要城市规划顾问向当地议会申请多地块分割审批。",
              "targetMatch": "地主 + 城市规划师"
            }
          ],
          "resourcesTagline": "信息与参考",
          "futureResourcesTitle": "专业资源与参考链接",
          "futureResourcesDesc": "获取 DfMA 设计框架、规划指南及顾问网络资讯。",
          "futureResourcesItems": [
            {
              "title": "模块化设计（DfMA）技术指南",
              "description": "关于模块运输尺寸、连接节点及结构网格的技术手册。",
              "status": "技术手册",
              "linkText": "查看 DfMA 指南"
            },
            {
              "title": "专业顾问网络入驻通道",
              "description": "加入 Le Sector 专业专家名册，获取早期项目引荐机会。",
              "status": "入驻通道",
              "linkText": "加入专家网络"
            },
            {
              "title": "集成式交付成功案例",
              "description": "建筑师-工程师-建造商在定制住宅交付中的成功合作示例。",
              "status": "案例集",
              "linkText": "探索集成案例"
            }
          ],
          "ctaBox": {
            "title": "您是专业专家或正在寻求专家团队？",
            "desc": "联系 Le Sector 探讨项目咨询合作或加入我们的专业专家网络。",
            "buttonText": "咨询专业技能合作"
          }
        }
      }
    }
  },
  "vi": {
    "promo": {
      "open": "Mở ưu đãi khuyến mãi",
      "latestUpdates": "Cập nhật mới nhất",
      "close": "Đóng cửa sổ khuyến mãi",
      "prev": "Trang trước",
      "next": "Trang tiếp theo",
      "goto": "Đi đến trang",
      "dontShowAgain": "Không hiển thị lại"
    },
    "nav": {
      "home": "Trang chủ",
      "about": "Về chúng tôi",
      "services": "Dịch vụ",
      "howItWorks": "Quy trình",
      "faq": "Hỏi đáp",
      "contact": "Liên hệ",
      "hub": "Trung tâm quảng cáo"
    },
    "footer": {
      "tagline": "Nhà ở theo Yêu cầu: Truyền thống & Lắp ghép. Bàn giao Chắc chắn.",
      "mission": "Chúng tôi cung cấp dịch vụ Quản lý & Bàn giao Dự án cho Nhà ở Truyền thống theo yêu cầu và Nhà ở Lắp ghép, mang lại sự minh bạch, kiểm soát chặt chẽ cho mọi bản thiết kế.",
      "col1": "Tiện ích",
      "col2": "Dịch vụ",
      "col3": "Liên hệ",
      "col4": "Đối tác & Pháp lý",
      "gadgets": {
        "calculator": "Tính toán chi phí",
        "measurer": "Đo lường hiện trường",
        "design": "Studio Thiết kế"
      },
      "serviceList": [
        "Giao nhận nhà lắp ghép",
        "Khả thi thiết kế",
        "Tối ưu hóa lắp ghép",
        "Điều phối tiền xây dựng"
      ],
      "contactDetails": {
        "phone": "0432 866 168",
        "area": "Australia (Victoria)",
        "hours": "Thứ Hai – Thứ Sáu, 9:00 – 17:00"
      },
      "partners": [
        {
          "name": "1st Mortgage Group",
          "role": "Môi giới thế chấp",
          "url": "https://www.1stmg.com.au"
        },
        {
          "name": "8 Miles Estate",
          "role": "Đại lý bất động sản",
          "url": "https://www.8milesestate.com.au"
        },
        {
          "name": "Core-Base Construction",
          "role": "Móng & Công trình đất",
          "url": "https://www.corebasecons.com.au"
        }
      ],
      "legal": [
        "Chính sách bảo mật",
        "Điều khoản & Điều kiện"
      ],
      "copyright": "© Le Sector 2025. Đã đăng ký bản quyền.",
      "disclaimer": "Le Sector cung cấp dịch vụ giao hàng và điều phối nhà ở lắp ghép. Dịch vụ thiết kế được cung cấp bởi các kiến trúc sư và nhà thiết kế bên ngoài. Tùy thuộc vào sự phê duyệt của hội đồng và điều kiện địa điểm.",
      "cta": {
        "title": "Gặp khó khăn về Đất đai hoặc Tài chính?",
        "sub": "Trao đổi với đối tác chiến lược của chúng tôi để giải quyết các thách thức trước khi xây dựng.",
        "btn": "Kết nối với Đối tác"
      }
    },
    "home": {
      "heroTitle": "Le Sector Bàn giao Dự án. Nhà ở Truyền thống & Lắp ghép theo Yêu cầu được Quản lý Chắc chắn.",
      "heroSub": "Chúng tôi giúp Quý khách lựa chọn giải pháp thi công tối ưu cho dự án—cung cấp dịch vụ Quản lý & Bàn giao Dự án chuyên nghiệp cho cả Nhà ở Truyền thống theo yêu cầu và Nhà ở Lắp ghép từ giai đoạn quy hoạch đến hoàn thiện.",
      "ctaStart": "Bắt đầu Hành trình Xây dựng",
      "ctaSpeak": "Trao đổi với chúng tôi",
      "whoTitle": "Chúng tôi là ai",
      "whoMain": "Le Sector là đơn vị Quản lý Dự án & Bàn giao Dự án chuyên nghiệp, thực hiện trọn gói cả Nhà ở Truyền thống theo yêu cầu và Nhà ở Lắp ghép.",
      "whoSub1": "Chúng tôi điều phối toàn bộ quy trình: quy hoạch, mua sắm, tư vấn chuyên môn, thi công thực địa và sản xuất mô-đun—giúp chủ nhà và kiến trúc sư lựa chọn giải pháp xây dựng phù hợp nhất với tầm nhìn và ngân sách.",
      "whoSub2": "Dù là Nhà ở Truyền thống hay Nhà ở Lắp ghép, mọi dự án đều áp dụng hệ thống quản lý dự án tiêu chuẩn—được hỗ trợ bởi khung quản lý ISO nhằm đảm bảo kiểm soát chất lượng chặt chẽ, hồ sơ minh bạch và quy trình bàn giao kỷ luật từ ý tưởng đến hoàn thiện.",
      "salesPoints": {
        "warranty": {
          "title": "Bảo hành quy định",
          "desc": "Bảo hành xây dựng tiêu chuẩn đầy đủ (7 năm), giống hệt như xây dựng truyền thống."
        },
        "delays": {
          "title": "Không trễ tiến độ",
          "desc": "Không có giai đoạn nhân công tại chỗ. Sản xuất nhà máy loại bỏ chậm trễ do thời tiết."
        },
        "fixedPrice": {
          "title": "Hợp đồng giá cố định",
          "desc": "Giữ giá cố định. Chúng tôi chốt chi phí trước khi sản xuất để đảm bảo tài chính."
        },
        "costRises": {
          "title": "Không tăng chi phí",
          "desc": "Không tăng giá vật liệu. Chúng tôi mua vật liệu trước để tránh lạm phát."
        }
      },
      "lowRisk": {
        "badge": "Lợi thế Tài chính",
        "title": "Mô hình Xây dựng Mô-đun Ít Rủi ro",
        "desc": "Le Sector cung cấp mô hình bàn giao nhà mô-đun được thiết kế để giảm rủi ro tài chính cho chủ nhà. Không giống như các dự án xây dựng truyền thống thường yêu cầu các khoản vay xây dựng theo tiến độ và trả lãi liên tục, phương pháp bàn giao mô-đun của chúng tôi cho phép khách hàng tiến hành với mức độ rủi ro tài chính thấp hơn đáng kể trong giai đoạn xây dựng.",
        "cardTitle": "Thanh toán Khi Hoàn thành",
        "highlight": "Quá trình xây dựng bắt đầu với nền móng (móng bè), tiếp theo là lắp ráp và lắp đặt nhà mô-đun. Trong quá trình này, khách hàng chỉ phải trả khoản tiền cọc từ 5% tổng giá trị hợp đồng. Số dư còn lại sẽ được thanh toán vào lúc quyết toán khi ngôi nhà đã hoàn thành và sẵn sàng bàn giao.",
        "keyAdvantages": "Lợi ích Chính",
        "bullets": [
          "Trong nhiều trường hợp, không cần khoản vay xây dựng truyền thống",
          "Giảm rủi ro về việc chậm tiến độ dự án và chi phí không chắc chắn",
          "Không phải trả tiền lãi liên tục trong thời gian xây dựng",
          "Sự chắc chắn cao hơn từ lúc bắt đầu cho đến khi hoàn thành dự án",
          "Thanh toán phù hợp với thời điểm bàn giao ngôi nhà hoàn thiện"
        ],
        "conclusion": "Bằng cách chuyển giao nhiều rủi ro xây dựng từ chủ nhà sang quy trình bàn giao được kiểm soát, Le Sector mang đến một lộ trình an toàn và dễ đoán hơn để xây dựng một ngôi nhà mới."
      }
    },
    "ecosystem": {
      "tag": "Hệ sinh thái tích hợp",
      "title": "Le Sector + CoreBase",
      "desc": "Le Sector hợp tác chặt chẽ với CoreBase Construction, chuyên gia về móng và hạ tầng, đảm bảo chuẩn bị mặt bằng đồng bộ từ ngày đầu tiên.",
      "quote": "Sự hợp tác này loại bỏ sự mất kết nối giữa sản xuất và lắp đặt tại hiện trường.",
      "leSectorSubtitle": "Thiết kế & Lắp ghép",
      "coreBaseSubtitle": "Thi công nền móng"
    },
    "process": {
      "tag": "Quy trình của chúng tôi",
      "title": "Sự tự tin từ quy trình",
      "steps": [
        {
          "num": "01",
          "title": "Đánh giá thiết kế",
          "desc": "Đánh giá tính khả thi và tuân thủ của bản vẽ cho phương pháp lắp ghép."
        },
        {
          "num": "02",
          "title": "Tối ưu hóa lắp ghép",
          "desc": "Tinh chỉnh bản vẽ để sản xuất tại nhà máy, tăng tốc độ và kiểm soát chất lượng."
        },
        {
          "num": "03",
          "title": "Phối hợp tiền xây dựng",
          "desc": "Kết nối các bên tư vấn, sản xuất và hiện trường."
        },
        {
          "num": "04",
          "title": "Giao hàng và Lắp đặt",
          "desc": "Các module được sản xuất và lắp đặt chính xác trên nền móng có sẵn."
        }
      ],
      "finalCta": "Sẵn sàng khám phá xây dựng lắp ghép?",
      "bookBtn": "Đặt lịch tư vấn"
    },
    "about": {
      "tag": "Cách tiếp cận",
      "title": "Kiến trúc đi đầu. Giao hàng luôn luôn.",
      "sub1": "Le Sector được thành lập để đáp ứng nhu cầu ngày càng tăng về nhà ở lắp ghép chất lượng cao.",
      "sub2": "Chúng tôi không bán các mẫu nhà có sẵn, chúng tôi thi công theo bản vẽ của khách hàng.",
      "approachTitle": "Cách tiếp cận của chúng tôi đối với nhà lắp ghép hiện đại",
      "approachP1": "Tại Le Sector, chúng tôi chuyên cung cấp những ngôi nhà lắp ghép hiện đại được xây dựng bằng cấu trúc khung thép kỹ thuật và các tấm bê tông nhẹ tiên tiến.",
      "approachP2": "Hệ thống xây dựng của chúng tôi được thiết kế để cung cấp độ bền, sự an toàn và sự thoải mái như mong đợi từ một ngôi nhà kiên cố, đồng thời hưởng lợi từ hiệu quả của xây dựng lắp ghép.",
      "approachP3": "Không giống như nhà container, nhà lắp ghép của chúng tôi không phải là container vận chuyển được cải tạo. Mỗi ngôi nhà được xây dựng có mục đích sử dụng khung thép kết cấu và các tấm tường được sản xuất chính xác với hệ thống khóa tích hợp. Điều này cho phép tính toàn vẹn cấu trúc mạnh mẽ, hiệu suất cách nhiệt tuyệt vời và lắp đặt tại chỗ hiệu quả.",
      "approachP4": "Khi nền móng đã được chuẩn bị, cấu trúc lắp ghép chính có thể được lắp đặt nhanh chóng tại chỗ, giảm đáng kể thời gian thường cần cho xây dựng truyền thống.",
      "approachP5": "Mục tiêu của chúng tôi là kết hợp công nghệ xây dựng hiện đại với sự phối hợp dự án chu đáo, mang đến cho chủ nhà một lộ trình đáng tin cậy và hiệu quả từ thiết kế đến ngôi nhà hoàn thiện.",
      "roleTitle": "Vai trò của chúng tôi",
      "roleSub": "Chúng tôi là đối tác triển khai dự án của bạn.",
      "roles": [
        {
          "title": "Chuyển đổi",
          "desc": "Chuyển đổi thiết kế kiến trúc sang thực thi lắp ghép."
        },
        {
          "title": "Quản lý",
          "desc": "Quản lý giao diện giữa sản xuất và hiện trường."
        },
        {
          "title": "Minh bạch",
          "desc": "Cung cấp cấu trúc và sự chắc chắn trong suốt quy trình."
        }
      ],
      "integrity": "Tính nguyên bản thiết kế",
      "builtTitle": "Được xây dựng cho sức mạnh, sự thoải mái và tuổi thọ",
      "builtP1": "Nhà lắp ghép Le Sector được thiết kế để ở lâu dài. Hệ thống cấu trúc của chúng tôi kết hợp khung thép và các tấm bê tông nhẹ tiên tiến có khả năng chống cháy, chống nước và mang lại hiệu suất cách âm và cách nhiệt mạnh mẽ.",
      "builtP2": "Cách tiếp cận này đảm bảo rằng xây dựng lắp ghép không phải là sự thỏa hiệp về chất lượng, mà là một cách thông minh hơn để cung cấp những ngôi nhà hiện đại với hiệu quả và sự chắc chắn cao hơn."
    },
    "services": {
      "tag": "Chuyên môn",
      "title": "Giao hàng lắp ghép theo thiết kế",
      "desc": "Giám sát toàn bộ vòng đời lắp ghép từ đánh giá đầu tiên đến lắp đặt cuối cùng.",
      "list": [
        "Đánh giá tính khả thi",
        "Tối ưu hóa và phối hợp",
        "Đồng bộ tư vấn kỹ thuật",
        "Lập kế hoạch tiền xây dựng",
        "Phối hợp sản xuất module",
        "Giám sát giao hàng và lắp đặt"
      ],
      "feasibility": {
        "title": "Gói Đánh giá Khả thi Lắp ghép",
        "price": "$490 - $990",
        "tag": "Bước đầu tiên",
        "desc": "Trước khi ký bất kỳ hợp đồng lớn nào, chúng tôi giúp bạn xác định xem dự án có đáng để thực hiện hay không.",
        "features": [
          "Kiểm tra khả thi địa điểm sơ bộ",
          "Phân tích khoảng ngân sách",
          "Dự báo tiến độ",
          "Danh mục rủi ro",
          "Tư vấn cấu trúc vốn",
          "So sánh chi phí (Truyền thống vs Lắp ghép)"
        ]
      },
      "modelsTitle": "Mô hình Hợp tác",
      "models": [
        {
          "title": "Mô hình A: Đại lý Dự án",
          "tag": "Điều phối Thiết kế + Quản lý",
          "fee": "Phí quản lý cố định: 8–12%",
          "features": [
            "Bạn giữ hợp đồng xây dựng",
            "Chúng tôi quản lý tích hợp",
            "Không kê giá vật liệu",
            "Lý tưởng cho Chủ nhà tự xây (Owner-Builders)"
          ]
        },
        {
          "title": "Mô hình B: Kết nối Đầu tư",
          "tag": "Kết nối Đất đai & Vốn",
          "fee": "Phí cấu trúc + Phí quản lý",
          "features": [
            "Kết nối chủ đất với nguồn vốn",
            "Kết nối nhà đầu tư với dự án",
            "Thiết kế cấu trúc trung gian",
            "Quản lý giao nhận dự án toàn diện"
          ]
        }
      ],
      "groundTitle": "Nền móng và Hiện trường",
      "groundTag": "Cung cấp bởi CoreBase",
      "groundList": [
        {
          "title": "Móng nhà",
          "desc": "Nền móng được kỹ thuật hóa tối ưu cho trọng lượng module."
        },
        {
          "title": "Hạ tầng",
          "desc": "Hoàn thiện hệ thống thoát nước và tiện ích."
        },
        {
          "title": "Chuẩn bị cấu trúc",
          "desc": "Đảm bảo giao diện móng-module chính xác từng milimet."
        }
      ]
    },
    "faq": {
      "tag": "Tin tưởng và Minh bạch",
      "title": "Câu hỏi thường gặp",
      "items": [
        {
          "q": "Các bạn có cung cấp thiết kế không?",
          "a": "Không. Chúng tôi làm việc theo bản vẽ của khách hàng hoặc kiến trúc sư bên ngoài."
        },
        {
          "q": "Xây nhà lắp ghép có rẻ hơn không?",
          "a": "Nó mang lại sự chắc chắn về chi phí và rút ngắn thời gian, nhưng chất lượng vẫn ở mức cao cấp."
        },
        {
          "q": "Thiết kế có thể tùy chỉnh không?",
          "a": "Có—quy trình của chúng tôi hỗ trợ các kết quả kiến trúc bespoke."
        },
        {
          "q": "Ai quản lý phần móng?",
          "a": "Phần móng được thực hiện bởi CoreBase Construction để đảm bảo khớp nối hoàn hảo."
        }
      ]
    },
    "contact": {
      "tag": "Trao đổi",
      "title": "Khởi đầu dự án",
      "desc": "Phục vụ Melbourne và các vùng lân cận Victoria.",
      "labels": {
        "phone": "Điện thoại",
        "email": "Email",
        "area": "Khu vực phục vụ",
        "formTitle": "Yêu cầu tư vấn",
        "name": "Họ và tên",
        "brief": "Sơ lược dự án",
        "placeholder": "Mô tả ngắn gọn tầm nhìn của bạn...",
        "submit": "Đặt lịch tư vấn"
      }
    },
    "calculator": {
      "disclaimer": "* Tuyên bố miễn trừ trách nhiệm: Tất cả giá cả chỉ là ước tính và phụ thuộc vào giá hợp đồng cuối cùng."
    },
    "ownerPartnership": {
      "hero": {
        "tagline": "Tư vấn Xây dựng Chiến lược",
        "title1": "Giao hàng Chiến lược.",
        "title2": "Kiểm soát Tài chính Tuyệt đối.",
        "desc": "Vượt qua những phức tạp của xây dựng tùy chỉnh với Văn phòng Quản lý Dự án (PMO) chuyên gia sát cánh cùng bạn. Bạn giữ toàn quyền kiểm soát tài chính trong khi chúng tôi thúc đẩy việc thực thi hàng ngày.",
        "cta": "Đặt lịch tư vấn"
      },
      "why": {
        "tagline": "Lợi thế Tư vấn",
        "title": "Tái hiện Mối quan hệ Khách hàng - Nhà thầu",
        "p1": "Các hợp đồng giá cố định truyền thống vốn dĩ đặt lợi nhuận của nhà thầu chống lại ngân sách của chủ nhà. Các biến đổi trở thành tranh chấp, và sự thiếu minh bạch đóng vai trò như một lá chắn tài chính. Chúng tôi tạo ra Quan hệ Đối tác Chủ sở hữu để loại bỏ hoàn toàn xích mích này.",
        "p2": "Bằng cách hoạt động trên một khoản phí quản lý minh bạch, các động lực tài chính của chúng tôi hoàn toàn phù hợp với bạn: cung cấp một tài sản kiến trúc xuất sắc hiệu quả nhất có thể, không có sự thỏa hiệp.",
        "cardTitle": "Sự liên kết thực sự",
        "cardDesc": "Các mục tiêu chung thay thế xung đột truyền thống.",
        "b1Title": "Minh bạch Chi phí 100%",
        "b1Desc": "Mọi khoản chiết khấu thương mại và tiền tiết kiệm được chuyển trực tiếp lại cho bạn.",
        "b2Title": "Bảo mật Tuyệt đối",
        "b2Desc": "Bạn nắm giữ vốn dự án. Quỹ không bao giờ rời khỏi tài khoản của bạn cho đến khi bạn phê duyệt công việc."
      },
      "process": {
        "tagline": "Phân chia Giai đoạn Chiến lược",
        "title": "Phương pháp Giao hàng",
        "steps": [
          {
            "title": "Liên kết Chiến lược",
            "desc": "Chúng tôi phân tích kế hoạch kiến trúc của bạn, thiết lập các khuôn khổ ngân sách thực tế và xác định chiến lược bàn giao tổng thể."
          },
          {
            "title": "Mua sắm Minh bạch",
            "desc": "Chúng tôi đấu thầu dự án của bạn cho mạng lưới các nhà thầu phụ cao cấp đã được kiểm định của chúng tôi, đảm bảo tỷ giá thị trường cạnh tranh được chuyển trực tiếp đến bạn."
          },
          {
            "title": "Thực thi Chính xác",
            "desc": "Đội ngũ quản lý công trường chuyên dụng của chúng tôi điều phối các hoạt động hàng ngày, thực thi kiểm soát chất lượng nghiêm ngặt và tuân thủ tiến độ tổng thể."
          },
          {
            "title": "Bàn giao Chất lượng",
            "desc": "Chúng tôi tiến hành các đánh giá khuyết điểm toàn diện, hoàn thiện các chứng nhận và chính thức bàn giao tài sản kiến trúc đã hoàn thành của bạn."
          }
        ]
      },
      "matrix": {
        "tagline": "Phạm vi Xác định",
        "title": "Sự rõ ràng trong Thực thi",
        "ownerTitle": "Chủ sở hữu",
        "ownerItems": [
          "Giữ lại vốn trong một tài khoản cá nhân chuyên dụng",
          "Ủy quyền cho tất cả các thay đổi về thiết kế và phạm vi lớn",
          "Phê duyệt và giải ngân các khoản thanh toán theo giai đoạn cho các nhà thầu phụ",
          "Duy trì giám sát điều hành tối cao"
        ],
        "sharedTitle": "Chia sẻ",
        "sharedItems": [
          "Tối ưu hóa và đánh giá ngân sách liên tục",
          "Quyết định kỹ thuật giá trị chủ động",
          "Lựa chọn vật liệu và hoàn thiện kiến trúc",
          "Đánh giá tiến độ hàng tháng toàn diện"
        ],
        "lsTitle": "Le Sector (PMO)",
        "lsItems": [
          "Quản lý công trường toàn diện và tuân thủ WHS",
          "Mua sắm nhà thầu phụ nghiêm ngặt và lên lịch vi mô",
          "Đảm bảo chất lượng không thỏa hiệp và giải quyết khuyết điểm",
          "Điều phối hội đồng và chứng nhận từ đầu đến cuối"
        ]
      },
      "flow": {
        "tagline": "Bảo vệ Vốn",
        "title": "An ninh Tài chính Không thỏa hiệp",
        "p1": "Rủi ro lớn nhất trong xây dựng tùy chỉnh là từ bỏ quyền kiểm soát vốn của bạn mà không có khả năng hiển thị về việc triển khai nó.",
        "p2": "Mô hình của chúng tôi giảm thiểu hoàn toàn điều này. Le Sector không bao giờ giữ quỹ xây dựng của bạn. Chúng tôi chứng nhận công việc một cách nghiêm ngặt, nhưng bạn giữ quyền giải ngân.",
        "s1Title": "Chứng nhận Chuyên gia",
        "s1Desc": "Các nhà quản lý công trường của chúng tôi xác minh nghiêm ngặt rằng công việc của nhà thầu đáp ứng các tiêu chuẩn chất lượng của chúng tôi.",
        "s2Title": "Xác thực Tài chính",
        "s2Desc": "PMO đối chiếu các hóa đơn với lịch trình mua sắm và đấu thầu đã được phê duyệt.",
        "s3Title": "Phê duyệt Điều hành",
        "s3Desc": "Bạn ủy quyền giải ngân tiền trực tiếp cho các nhà thầu phụ từ tài khoản an toàn của mình."
      },
      "dash": {
        "tagline": "Thông tin Thời gian thực",
        "title": "Khả năng Hiển thị Toàn diện",
        "desc": "Loại bỏ sự lo lắng của những điều chưa biết. Cơ sở hạ tầng kỹ thuật số của chúng tôi cung cấp cho bạn khả năng hiển thị ở cấp độ điều hành về tình trạng dự án, tiến độ và tình hình tài chính của bạn tại mọi thời điểm.",
        "items": [
          "Dự báo tiến độ động",
          "Nhật ký công trường hàng ngày toàn diện",
          "Phân tích phương sai ngân sách thời gian thực",
          "Theo dõi mua sắm trực tiếp"
        ],
        "hub": "Trung tâm Dự án",
        "budget": "Vốn đã Triển khai",
        "schedule": "Lịch trình Tổng thể",
        "onTrack": "Đúng tiến độ",
        "week": "Tuần 14 trên 32",
        "cashflow": "Dòng tiền Dự báo"
      },
      "suit": {
        "tagline": "Hồ sơ Khách hàng",
        "title": "Mô hình Này có Phù hợp với Bạn Không?",
        "idealTitle": "Lý tưởng cho",
        "idealItems": [
          "Khách hàng thực hiện các dự án xây dựng kiến trúc cao cấp, phức tạp",
          "Những chủ nhà yêu cầu sự minh bạch tài chính không khoan nhượng",
          "Những người tìm kiếm sự giám sát điều hành mà không phải gánh vác gánh nặng quản lý hàng ngày",
          "Các nhà phát triển ưu tiên hiệu quả chi phí và quyền tiếp cận trực tiếp nhà thầu phụ"
        ],
        "notTitle": "Không Lý tưởng cho",
        "notItems": [
          "Khách hàng tìm kiếm một hợp đồng giá cố định truyền thống 'thiết lập và quên đi'",
          "Những người không muốn hoặc không thể ủy quyền thanh toán theo giai đoạn hàng tuần",
          "Các dự án phụ thuộc vào tài chính xây dựng do nhà thầu cung cấp",
          "Xây dựng khu dân cư dựa trên khối lượng, tiêu chuẩn hóa"
        ]
      },
      "faq": {
        "tagline": "Thông tin Tư vấn",
        "title": "Câu hỏi Thường gặp",
        "q1": "Cơ chế kiểm soát vốn chính xác hoạt động như thế nào?",
        "a1": "Quỹ dự án của bạn được giữ an toàn trong tài khoản ngân hàng do bạn chỉ định. Trong khi Le Sector quản lý chuyên nghiệp việc mua sắm, lên lịch và chứng nhận chất lượng, chúng tôi không có quyền truy cập trực tiếp vào vốn của bạn. Bạn đóng vai trò là cơ quan tài chính cuối cùng, giải ngân trực tiếp cho các nhà thầu đã được xác minh chỉ khi đạt được các mốc thành công.",
        "q2": "Nếu Le Sector không giữ tiền, phạm vi trách nhiệm của bạn là gì?",
        "a2": "Chúng tôi phục vụ như Văn phòng Quản lý Dự án (PMO) chuyên dụng và đội ngũ công trường tinh hoa của bạn. Chúng tôi chịu toàn bộ trách nhiệm về lập kế hoạch chiến lược, mua sắm nghiêm ngặt, quản lý công trường hàng ngày, kiểm soát chất lượng không thỏa hiệp và điều phối tiến độ. Chúng tôi thực hiện thực tế phức tạp của việc xây dựng, cho phép bạn chỉ tập trung vào việc giám sát điều hành.",
        "q3": "Mô hình này có yêu cầu tôi phải có kinh nghiệm xây dựng trước đây không?",
        "a3": "Hoàn toàn không. Trên thực tế, mô hình của chúng tôi cung cấp sự hướng dẫn và bảo vệ tốt hơn đáng kể so với hợp đồng truyền thống. Bằng cách kết hợp cơ sở hạ tầng báo cáo minh bạch của chúng tôi với đội ngũ tư vấn chuyên gia của chúng tôi, bạn có quyền đưa ra quyết định sáng suốt mà không cần chuyên môn xây dựng kỹ thuật."
      },
      "cta": {
        "title": "Bắt đầu Cuộc trò chuyện",
        "desc": "Lên lịch tư vấn riêng để khám phá cách mô hình tư vấn của chúng tôi có thể bảo vệ vốn của bạn và nâng cao việc bàn giao dự án kiến trúc của bạn."
      },
      "form": {
        "step": "Bước",
        "of": "trên",
        "s1Title": "Loại dự án",
        "s1Opts": [
          "Nhà ở đơn lẻ",
          "Phát triển nhiều đơn vị",
          "Thương mại",
          "Khác"
        ],
        "s2Title": "Giai đoạn dự án",
        "s2Opts": [
          "Ý tưởng",
          "Lên kế hoạch / Thiết kế",
          "Phê duyệt của Hội đồng",
          "Sẵn sàng xây dựng"
        ],
        "s3Title": "Địa điểm dự án",
        "s3Ph": "Khu vực hoặc Mã bưu điện",
        "s4Title": "Ngân sách xây dựng ước tính",
        "s4Opts": [
          "Dưới $500k",
          "$500k - $1M",
          "$1M - $2.5M",
          "$2.5M+"
        ],
        "s5Title": "Dịch vụ Yêu cầu",
        "s5Opts": [
          "Nghiên cứu khả thi",
          "Thiết kế kiến trúc",
          "Quản lý dự án",
          "Bàn giao xây dựng"
        ],
        "s6Title": "Ưu tiên của Khách hàng",
        "s6Opts": [
          "Hiệu quả chi phí",
          "Chất lượng cao cấp",
          "Tốc độ bàn giao",
          "Minh bạch tối đa"
        ],
        "s7Title": "Tiến độ",
        "s7Opts": [
          "Càng sớm càng tốt",
          "3-6 Tháng",
          "6-12 Tháng",
          "Hơn 12 Tháng"
        ],
        "s8Title": "Thông tin bổ sung",
        "s8Ph": "Hãy cho chúng tôi biết thêm về tầm nhìn dự án của bạn...",
        "s9Title": "Thông tin liên hệ",
        "s9Ph1": "Họ và tên",
        "s9Ph2": "Địa chỉ Email",
        "s9Ph3": "Số điện thoại",
        "back": "Quay lại",
        "continue": "Tiếp tục",
        "submit": "Yêu cầu tư vấn",
        "submitting": "Đang gửi...",
        "error": "Vui lòng điền vào tất cả các trường bắt buộc.",
        "emailError": "Vui lòng nhập địa chỉ email hợp lệ.",
        "submitError": "Đã xảy ra lỗi. Vui lòng thử lại."
      },
      "confirm": {
        "title": "Đã nhận Đơn đăng ký",
        "desc": "Cảm ơn bạn đã quan tâm. Chúng tôi sẽ xem xét chi tiết dự án của bạn và liên hệ lại trong vòng 24 giờ.",
        "nextSteps": "Điều gì xảy ra tiếp theo?",
        "bookTitle": "Tư vấn ban đầu",
        "bookDesc": "Một trong những cố vấn của chúng tôi sẽ liên hệ để lên lịch cuộc gọi giới thiệu của bạn.",
        "dlTitle": "Đánh giá Dự án",
        "dlDesc": "Chúng tôi sẽ tiến hành đánh giá sơ bộ về các chi tiết dự án của bạn."
      }
    },
    "exploreOpportunities": {
      "hero": {
        "badge": "HỆ SINH THÁI LE SECTOR • KẾT NỐI ĐƯỢC HỖ TRỢ CHUYÊN NGHIỆP",
        "headline1": "MỌI DỰ ÁN TUYỆT VỜI ĐỀU BẮT ĐẦU TỪ SỰ KẾT NỐI PHÙ HỢP.",
        "subheadline": "Khám phá cơ hội dự án và đối tác tiềm năng, được quản lý và kết nối chuyên nghiệp bởi Le Sector.",
        "primaryCta": "Khám phá Cơ hội",
        "secondaryCta": "Cho chúng tôi biết nhu cầu của bạn"
      },
      "pathways": {
        "tagline": "Trụ cột Hệ sinh thái",
        "title": "Bạn có thể Khám phá Điều gì?",
        "desc": "Khám phá các định hướng hoạt động trong bất động sản nhà ở, vốn phát triển và bàn giao dự án lắp ghép.",
        "items": [
          {
            "title": "Bất động sản & Mặt bằng",
            "desc": "Cơ hội đất đai, tiềm năng phát triển và bất động sản phù hợp cho nhà ở tùy chỉnh hoặc lắp ghép."
          },
          {
            "title": "Đầu tư & Nguồn vốn",
            "desc": "Cơ hội đầu tư, yêu cầu nguồn vốn và kết nối tài chính cho các đề xuất xây dựng nhà ở đã kiểm duyệt."
          },
          {
            "title": "Dự án & Bàn giao",
            "desc": "Các dự án đang tìm kiếm nhà thầu hợp pháp, nhà phát triển, năng lực sản xuất chuyên biệt hoặc đối tác bàn giao."
          },
          {
            "title": "Chuyên môn Nghề nghiệp",
            "desc": "Kiến trúc sư, kỹ sư kết cấu, nhà quy hoạch đô thị và tư vấn chuyên sâu tìm kiếm cơ hội tham gia sớm."
          }
        ]
      },
      "howItWorks": {
        "tagline": "Hành trình Kết nối",
        "title": "Quy trình Hoạt động",
        "steps": [
          {
            "step": "01",
            "title": "Khám phá",
            "desc": "Duyệt tìm các cơ hội mà không công khai thông tin liên hệ cá nhân hay địa chỉ chi tiết."
          },
          {
            "step": "02",
            "title": "Bày tỏ Quan tâm",
            "desc": "Thông báo cho Le Sector cơ hội nào phù hợp với mục tiêu và năng lực của bạn."
          },
          {
            "step": "03",
            "title": "Chúng tôi Đánh giá",
            "desc": "Le Sector xem xét yêu cầu, xác minh thiện chí và kiểm tra tính khả thi dự án."
          },
          {
            "step": "04",
            "title": "Chúng tôi Giới thiệu",
            "desc": "Khi phù hợp, Le Sector tổ chức buổi giới thiệu chuyên nghiệp có sự đồng ý của cả hai bên."
          },
          {
            "step": "05",
            "title": "Thảo luận Chi tiết",
            "desc": "Các bên đánh giá tiềm năng hợp tác với sự hỗ trợ từ năng lực quản lý dự án của Le Sector."
          }
        ]
      },
      "why": {
        "tagline": "Lợi thế từ Le Sector",
        "title": "Tại sao Chọn Le Sector?",
        "desc": "Mạng lưới cơ hội được quản lý chuyên nghiệp, không có tin rác từ diễn đàn chưa kiểm duyệt hay quảng cáo phiền phức.",
        "features": [
          {
            "title": "Bảo mật Hàng đầu",
            "desc": "Không công khai số điện thoại, email hay địa chỉ chính xác của bất động sản."
          },
          {
            "title": "Đánh giá Chuyên nghiệp",
            "desc": "Các cơ hội và đề xuất quan tâm đều được đánh giá kỹ lưỡng trước khi kết nối."
          },
          {
            "title": "Kết nối Có Cấu trúc",
            "desc": "Le Sector trực tiếp điều phối buổi gặp đầu tiên thay vì chỉ chuyển giao thông tin."
          },
          {
            "title": "Chuyên môn Bàn giao Dự án",
            "desc": "Được hỗ trợ trực tiếp bởi quy chuẩn giao nhà lắp ghép và tùy chỉnh của Le Sector."
          },
          {
            "title": "Quản lý Chất lượng Tiêu chuẩn ISO",
            "desc": "Hướng dẫn bởi khung quản lý bài bản và nguyên tắc đảm bảo chất lượng ISO."
          },
          {
            "title": "Không Phiền nhiễu Thương mại",
            "desc": "Hoàn toàn không có tin rác, cuộc gọi chào mời hay quảng cáo chưa xác minh."
          }
        ]
      },
      "scenarios": {
        "tagline": "Kịch bản Mẫu",
        "title": "Kịch bản Cơ hội",
        "desc": "Ví dụ thực tế cho thấy các bên kết nối như thế nào trong hệ sinh thái của chúng tôi. (Kịch bản minh họa — phân biệt với danh mục thực tế).",
        "badge": "KỊCH BẢN MINH HỌA",
        "items": [
          {
            "title": "Tôi có Đất",
            "quote": "“Tôi muốn hiểu những cơ hội phát triển nào có thể có cho bất động sản của mình mà không cần đăng bán công khai.”",
            "targetMatch": "Nhà phát triển, Nhà đầu tư, Chuyên gia Lắp ghép"
          },
          {
            "title": "Tôi có Vốn",
            "quote": "“Tôi đang tìm kiếm cơ hội bất động sản hoặc phát triển được bảo chứng bởi quản lý dự án bài bản.”",
            "targetMatch": "Chủ đất, Nhà phát triển đủ năng lực, Dự án Tùy chỉnh"
          },
          {
            "title": "Tôi có Dự án",
            "quote": "“Tôi đang tìm kiếm đối tác thi công, nhà thầu hoặc đối tác đầu tư phù hợp để triển khai xây dựng.”",
            "targetMatch": "Nhà thầu được cấp phép, Đối tác Góp vốn, Giám đốc Dự án"
          },
          {
            "title": "Tôi có Chuyên môn",
            "quote": "“Tôi đang tìm kiếm các dự án nhà ở phù hợp để đóng góp thiết kế hoặc kỹ thuật chuyên sâu.”",
            "targetMatch": "Chủ dự án, Nhà phát triển, Tư vấn Quy hoạch"
          }
        ]
      },
      "directory": {
        "tagline": "Danh mục Hoạt động",
        "title": "Các Cơ hội Hiện có",
        "filterAllCategories": "Tất cả Phân loại",
        "filterAllModes": "Tất cả Hình thức",
        "modeTraditional": "Truyền thống Tùy chỉnh",
        "modeModular": "Lắp ghép Mô-đun",
        "modeHybrid": "Bàn giao Hỗn hợp",
        "emptyState": {
          "title": "Các cơ hội mới đang được xem xét.",
          "desc": "Cho chúng tôi biết bạn đang tìm kiếm điều gì và chúng tôi sẽ giúp xác định cơ hội phù hợp trong mạng lưới Le Sector.",
          "cta": "Cho chúng tôi biết nhu cầu của bạn"
        },
        "card": {
          "ref": "MÃ SỐ",
          "status": "Trạng thái",
          "location": "Vị trí",
          "stage": "Giai đoạn",
          "type": "Loại Dự án",
          "budget": "Ngân sách",
          "seeking": "Tìm kiếm",
          "timeframe": "Thời gian",
          "expressInterest": "Bày tỏ Quan tâm (EOI)",
          "viewDetails": "Xem Tóm tắt"
        }
      },
      "privacy": {
        "tagline": "Bảo mật & Trực quan",
        "title": "Cam kết Bảo mật, Cẩn trọng & Tin cậy",
        "desc": "Chúng tôi bảo vệ danh tính, thông tin dự án và lợi ích thương mại của bạn ở mọi bước.",
        "items": [
          {
            "title": "Không Công khai Thông tin",
            "desc": "Danh tính, số điện thoại và email cá nhân của bạn không bao giờ bị xuất bản công khai."
          },
          {
            "title": "Giới thiệu Đã Kiểm duyệt",
            "desc": "Mọi yêu cầu ban đầu đều được cố vấn Le Sector sàng lọc trước khi kết nối chính thức."
          },
          {
            "title": "Đồng ý Hai chiều",
            "desc": "Việc giới thiệu chỉ diễn ra khi cả hai bên cùng đồng ý sau bước đánh giá sơ bộ."
          }
        ]
      },
      "cta": {
        "title": "Bạn đang có Cơ hội trong đầu?",
        "desc": "Cho chúng tôi biết nhu cầu của bạn và để Le Sector hỗ trợ tìm kiếm kết nối phù hợp.",
        "button": "Cho chúng tôi biết nhu cầu của bạn"
      },
      "formModal": {
        "title": "Yêu cầu Tư vấn Cơ hội Bảo mật",
        "sub": "Cho chúng tôi biết yêu cầu hoặc cơ hội của bạn. Cố vấn Le Sector sẽ xem xét hoàn toàn bảo mật.",
        "roleLabel": "Vai trò Chính của bạn",
        "roles": {
          "landOwner": "Chủ sở hữu Bất động sản / Đất",
          "investor": "Nhà đầu tư Cá nhân / Nguồn vốn",
          "projectOwner": "Nhà phát triển / Chủ dự án",
          "expert": "Chuyên gia / Cố vấn Ngành"
        },
        "name": "Họ và Tên",
        "email": "Địa chỉ Email",
        "phone": "Số Điện thoại",
        "region": "Khu vực / Vị trí Mục tiêu",
        "category": "Lĩnh vực Quan tâm",
        "details": "Thông số Dự án & Nhu cầu của bạn",
        "disclaimer": "Thông tin của bạn được xử lý theo quy trình bảo mật nghiêm ngặt. Không có thông tin liên hệ hay địa chỉ cụ thể nào được chia sẻ công khai nếu chưa có sự đồng ý của bạn.",
        "submit": "Gửi Yêu cầu",
        "successTitle": "Đã nhận Yêu cầu",
        "successDesc": "Cảm ơn bạn đã liên hệ. Mã tham chiếu: ",
        "successSub": "Cố vấn Le Sector sẽ xem xét bảo mật và liên hệ lại trong vòng 24-48 giờ.",
        "close": "Đóng"
      },
      "pathwayDetails": {
        "propertySites": {
          "badge": "HỆ SINH THÁI LE SECTOR • BẤT ĐỘNG SẢN & QUỸ ĐẤT",
          "title": "Lĩnh vực Bất động sản & Quỹ đất",
          "headline": "Phát triển Đất & Tiềm năng Phân lô Sub-division",
          "subheadline": "Khám phá các cơ hội phát triển quỹ đất, tiềm năng phân lô và tái phát triển bất động sản phù hợp với xây dựng tùy chỉnh hoặc mô-đun.",
          "heroCta": "Tư vấn Bất động sản & Quỹ đất",
          "secondaryCta": "Xem Danh mục Cơ hội",
          "backToAll": "Quay lại Tất cả Cơ hội",
          "highlights": [
            "Đánh giá Tiềm năng Phân lô",
            "Phân tích Hợp chuẩn Quy hoạch",
            "Logistics Xây dựng Mô-đun",
            "Kết nối Bảo mật"
          ],
          "overviewTagline": "Hệ sinh thái Bất động sản",
          "overviewTitle": "Mục đích của Lĩnh vực này",
          "overviewDesc": "Lĩnh vực Bất động sản & Quỹ đất kết nối chủ đất, chủ sở hữu tài sản với năng lực phát triển và xây dựng bài bản. Dù là đánh giá tiềm năng phân lô đất trống, tái phát triển khu dân cư nội đô hay khả năng tương thích của nhà mô-đun, Le Sector cung cấp giải pháp điều phối và đánh giá khả thi giai đoạn đầu.",
          "overviewSubText": "Le Sector đóng vai trò là nhà kết nối chuyên nghiệp—giúp chủ sở hữu tài sản đánh giá tiềm năng và kết nối với các đối tác thực thi kinh nghiệm.",
          "cardTitle": "Điều phối Bảo mật Quỹ đất",
          "cardDesc": "Le Sector tiến hành đánh giá sơ bộ địa điểm và kết nối chủ đất với đối tác thực thi phù hợp trong điều kiện bảo mật tuyệt đối.",
          "whoTagline": "Đối tượng Tham gia",
          "whoIsItForTitle": "Dành cho Ai",
          "whoIsItForItems": [
            {
              "title": "Chủ đất & Chủ sở hữu Tài sản",
              "desc": "Khám phá tiềm năng phân lô hoặc tối ưu hóa giá trị quỹ đất cho nhà ở tùy chỉnh hoặc mô-đun."
            },
            {
              "title": "Đối tác Phân lô Bất động sản",
              "desc": "Tìm kiếm vị trí quỹ đất đẹp hoặc hợp tác với chủ đất theo mô hình phát triển bài bản."
            },
            {
              "title": "Chủ sở hữu Khu đất Tái phát triển",
              "desc": "Chủ sở hữu bất động sản hiện hữu tìm kiếm cơ hội xây mới hoặc tái thiết mô-đun."
            }
          ],
          "typesTagline": "Phạm vi Cơ hội",
          "opportunityTypesTitle": "Các Loại hình Cơ hội",
          "opportunityTypesItems": [
            {
              "title": "Quỹ đất Phân lô Nhà ở",
              "desc": "Các lô đất phù hợp phân lô nhiều căn hoặc quy hoạch tổng thể nhà ở mô-đun hiện đại.",
              "tag": "Phân lô Sub-division"
            },
            {
              "title": "Quỹ đất Tái thiết Nội đô",
              "desc": "Lô đất nội thành phù hợp xây dựng biệt thự tùy chỉnh hoặc nhà lắp ghép mô-đun.",
              "tag": "Tái thiết Nội đô"
            },
            {
              "title": "Hợp tác Hợp doanh Phát triển",
              "desc": "Cơ hội cho chủ đất hợp tác cùng nhà phát triển và mạng lưới nhà thầu uy tín.",
              "tag": "Hợp doanh"
            },
            {
              "title": "Địa điểm Tương thích Mô-đun",
              "desc": "Lô đất được đánh giá đủ điều kiện xe cẩu, logistics lắp ráp và thi công nhanh.",
              "tag": "Sẵn sàng Mô-đun"
            }
          ],
          "facTagline": "Quy trình Thực thi",
          "facilitationTitle": "Cách Le Sector Hỗ trợ",
          "facilitationSteps": [
            {
              "step": "01",
              "title": "Đánh giá Địa điểm",
              "desc": "Phân tích hướng đất, lối vào, quy hoạch và khả năng thi công mô-đun."
            },
            {
              "step": "02",
              "title": "Chiến lược & Cấu trúc",
              "desc": "Xác định mô hình tối ưu—xây tùy chỉnh, phân lô hoặc hợp doanh."
            },
            {
              "step": "03",
              "title": "Kết nối Bảo mật",
              "desc": "Giới thiệu đối tác thực thi hoặc nguồn vốn đã qua kiểm định mà không công khai thông tin."
            },
            {
              "step": "04",
              "title": "Điều phối Thực thi",
              "desc": "Đồng hành cùng đội ngũ kiến trúc, kỹ thuật và nhà máy sản xuất đến khi hoàn thành."
            }
          ],
          "scenariosTagline": "Kịch bản Thực tế",
          "scenariosTitle": "Ví dụ Kịch bản Dự án",
          "scenariosItems": [
            {
              "title": "Phân lô 3 Căn Nhà ở",
              "type": "Tiềm năng Phân lô",
              "description": "Chủ đất có diện tích lớn muốn phân thành 3 lô nhà ở kết hợp nhà mô-đun sản xuất tại nhà máy để hoàn thiện nhanh.",
              "targetMatch": "Chủ đất + Mạng lưới Xây dựng Mô-đun"
            },
            {
              "title": "Tái thiết Nhà cũ Nội thành",
              "type": "Tái thiết Quỹ đất",
              "description": "Chủ sở hữu muốn thay thế căn nhà cũ bằng công trình nhà ở đôi hiện đại.",
              "targetMatch": "Chủ đất + Nhà thầu / Thiết kế Tùy chỉnh"
            }
          ],
          "resourcesTagline": "Thông tin & Tham khảo",
          "futureResourcesTitle": "Tài nguyên & Liên kết Tham khảo",
          "futureResourcesDesc": "Truy cập danh mục kiểm tra quy hoạch, hướng dẫn tính khả thi địa điểm mô-đun và các dịch vụ Le Sector.",
          "futureResourcesItems": [
            {
              "title": "Danh mục Kiểm tra Khả thi Mô-đun",
              "description": "Hướng dẫn về lối vào xe cẩu, khoảng lùi, kết nối hạ tầng và độ dốc.",
              "status": "Hướng dẫn Tham khảo",
              "linkText": "Xem Khung Khả thi"
            },
            {
              "title": "Mẫu Báo cáo Phân lô Bất động sản",
              "description": "Thông số chuẩn hóa để trình bày lô đất với đối tác phát triển.",
              "status": "Mẫu Thông tin",
              "linkText": "Yêu cầu Mẫu Báo cáo"
            },
            {
              "title": "Dịch vụ Tư vấn Quỹ đất Le Sector",
              "description": "Tìm hiểu cách đội ngũ chuyên gia tư vấn đánh giá tính tương thích của quỹ đất.",
              "status": "Tổng quan Dịch vụ",
              "linkText": "Khám phá Tư vấn Quỹ đất"
            }
          ],
          "ctaBox": {
            "title": "Bạn có Cơ hội Bất động sản hoặc Quỹ đất?",
            "desc": "Trao đổi với cố vấn Le Sector để đánh giá tiềm năng và thảo luận phương án kết nối bảo mật.",
            "buttonText": "Tư vấn Bất động sản & Quỹ đất"
          }
        },
        "investmentCapital": {
          "badge": "HỆ SINH THÁI LE SECTOR • ĐẦU TƯ & NGUỒN VỐN",
          "title": "Lĩnh vực Đầu tư & Nguồn vốn",
          "headline": "Kết nối Nguồn vốn & Dự án Phát triển",
          "subheadline": "Kết nối nguồn vốn đầu tư với các cơ hội phát triển bất động sản nhà ở và thi công xây dựng phù hợp.",
          "heroCta": "Tư vấn Đầu tư & Nguồn vốn",
          "secondaryCta": "Xem Danh mục Cơ hội",
          "backToAll": "Quay lại Tất cả Cơ hội",
          "highlights": [
            "Kết nối Nguồn vốn Phát triển",
            "Dự án Đảm bảo bằng Tài sản",
            "Quản trị Dự án Chuyên nghiệp",
            "Cơ cấu Hợp tác Bài bản"
          ],
          "overviewTagline": "Hệ sinh thái Nguồn vốn",
          "overviewTitle": "Mục đích của Lĩnh vực này",
          "overviewDesc": "Lĩnh vực Đầu tư & Nguồn vốn giới thiệu các cơ hội kết nối dòng vốn với các dự án bất động sản và xây dựng đã qua thẩm định. Le Sector đóng vai trò là nhà kết nối chuyên nghiệp và đối tác thực thi, đảm bảo quy trình thẩm định, quản trị rủi ro và cấu trúc dự án nghiêm ngặt.",
          "overviewSubText": "Không hoạt động như một sàn giao dịch hay quỹ công khai, Le Sector âm thầm kết nối nguồn vốn với các dự án nhà ở và mô-đun được cấu trúc bài bản.",
          "cardTitle": "Đối tác Kết nối Chuyên nghiệp",
          "cardDesc": "Le Sector điều phối dòng vốn với các đối tác thực thi dự án theo khung quản trị rủi ro và bảo mật nghiêm ngặt.",
          "whoTagline": "Đối tượng Tham gia",
          "whoIsItForTitle": "Dành cho Ai",
          "whoIsItForItems": [
            {
              "title": "Nhà đầu tư Cá nhân & Tư nhân",
              "desc": "Tìm kiếm cơ hội tham gia bài bản vào các dự án phát triển nhà ở đã thẩm định."
            },
            {
              "title": "Đối tác Cấp vốn Phát triển",
              "desc": "Tìm kiếm dự án nhà ở được quản lý chuyên nghiệp, có tài sản đảm bảo và mốc tiến độ rõ ràng."
            },
            {
              "title": "Nhà phát triển Dự án",
              "desc": "Tìm kiếm nguồn vốn cổ phần hoặc vốn phát triển cho dự án nhà ở và mô-đun."
            }
          ],
          "typesTagline": "Phạm vi Cơ hội",
          "opportunityTypesTitle": "Các Loại hình Cơ hội",
          "opportunityTypesItems": [
            {
              "title": "Hợp tác Cổ phần Hợp doanh",
              "desc": "Cấu trúc đồng phát triển dự án nhà ở với quy trình quản trị mốc tiến độ minh bạch.",
              "tag": "Kết nối Cổ phần"
            },
            {
              "title": "Nguồn vốn Phát triển Dự án",
              "desc": "Kết nối vốn cấp dự án cho sản xuất mô-đun và thi công hạ tầng.",
              "tag": "Cấu trúc Nguồn vốn"
            },
            {
              "title": "Dự án Bảo đảm bằng Tài sản",
              "desc": "Cơ hội phát triển bất động sản được đảm bảo bằng tài sản thực tế.",
              "tag": "Bảo đảm Tài sản"
            },
            {
              "title": "Dự án Xây dựng Quản lý Chuyên nghiệp",
              "desc": "Cơ hội cho nhà đầu tư tìm kiếm dự án được vận hành toàn diện bởi đội ngũ chuyên gia.",
              "tag": "Quản lý Chuyên nghiệp"
            }
          ],
          "facTagline": "Quy trình Thực thi",
          "facilitationTitle": "Cách Le Sector Hỗ trợ",
          "facilitationSteps": [
            {
              "step": "01",
              "title": "Thẩm định Dự án",
              "desc": "Thẩm định sơ bộ thông số dự án, quy hoạch và khả năng thực thi."
            },
            {
              "step": "02",
              "title": "Cấu trúc Hợp tác",
              "desc": "Thiết lập mô hình hợp tác phù hợp với quy mô và mức độ rủi ro của dự án."
            },
            {
              "step": "03",
              "title": "Kết nối Bảo mật",
              "desc": "Giới thiệu trực tiếp chủ nguồn vốn với chủ trì thực thi dự án."
            },
            {
              "step": "04",
              "title": "Báo cáo & Quản trị",
              "desc": "Giám sát dự án và theo dõi tiến độ thi công trong suốt chu kỳ xây dựng."
            }
          ],
          "scenariosTagline": "Kịch bản Thực tế",
          "scenariosTitle": "Ví dụ Kịch bản Dự án",
          "scenariosItems": [
            {
              "title": "Vốn Phát triển Phân lô Nhà ở",
              "type": "Kết nối Cổ phần",
              "description": "Dự án phân lô 4 căn đã duyệt quy hoạch cần kết nối vốn cổ phần cho hạ tầng và thi công nhà mô-đun.",
              "targetMatch": "Nhà phát triển + Đối tác Cổ phần"
            },
            {
              "title": "Cấp vốn Dự án Nhà Mô-đun",
              "type": "Vốn Phát triển",
              "description": "Đề xuất xây dựng nhà ở hiệu quả cao cần cấu trúc vốn cho sản xuất nhà máy và lắp đặt.",
              "targetMatch": "Đội ngũ Thực thi + Đối tác Cấp vốn"
            }
          ],
          "resourcesTagline": "Thông tin & Tham khảo",
          "futureResourcesTitle": "Tài nguyên & Thông tin Đầu tư",
          "futureResourcesDesc": "Truy cập hướng dẫn quản trị, mẫu báo cáo nguồn vốn và dịch vụ tư vấn liên quan.",
          "futureResourcesItems": [
            {
              "title": "Khung Thẩm định Nguồn vốn",
              "description": "Tổng quan tiêu chí Le Sector sử dụng để đánh giá tính khả thi dự án.",
              "status": "Khung Thẩm định",
              "linkText": "Xem Khung Thẩm định"
            },
            {
              "title": "Mẫu Báo cáo Dự án Standard",
              "description": "Cấu trúc chuẩn hóa trình bày nhu cầu vốn với đối tác tiềm năng.",
              "status": "Mẫu Báo cáo",
              "linkText": "Yêu cầu Mẫu Báo cáo"
            },
            {
              "title": "Hỗ trợ Quản lý & Thực thi Le Sector",
              "description": "Tìm hiểu cách đội ngũ Le Sector giám sát thực thi bảo vệ tiến độ dự án.",
              "status": "Tổng quan Dịch vụ",
              "linkText": "Khám phá Dịch vụ"
            }
          ],
          "ctaBox": {
            "title": "Tìm kiếm Nguồn vốn hoặc Dự án Đầu tư?",
            "desc": "Liên hệ cố vấn Le Sector để trao đổi nhu cầu vốn hoặc kết nối dự án một cách bảo mật.",
            "buttonText": "Tư vấn Đầu tư & Nguồn vốn"
          }
        },
        "projectsDelivery": {
          "badge": "HỆ SINH THÁI LE SECTOR • DỰ ÁN & THỰC THI",
          "title": "Lĩnh vực Dự án & Thực thi",
          "headline": "Năng lực Thi công & Đối tác Chiến lược",
          "subheadline": "Giới thiệu các dự án đang triển khai hoặc mới nổi cần năng lực thi công, nhà máy sản xuất mô-đun hoặc đối tác chiến lược.",
          "heroCta": "Tư vấn Dự án & Thực thi",
          "secondaryCta": "Xem Danh mục Cơ hội",
          "backToAll": "Quay lại Tất cả Cơ hội",
          "highlights": [
            "Nhà thầu Xây dựng Chuyên nghiệp",
            "Nhà máy Sản xuất Mô-đun",
            "Hỗ trợ Quản lý Dự án",
            "Đội ngũ Thi công Chuyên môn"
          ],
          "overviewTagline": "Hệ sinh thái Thực thi",
          "overviewTitle": "Mục đích của Lĩnh vực này",
          "overviewDesc": "Lĩnh vực Dự án & Thực thi kết nối các dự án nhà ở với các nhà thầu được cấp phép, nhà máy sản xuất mô-đun, thợ chuyên môn và chuyên gia quản lý dự án. Le Sector điều phối thi công đảm bảo dự án triển khai hiệu quả, đúng ngân sách và đạt chuẩn kỹ thuật.",
          "overviewSubText": "Dù dự án yêu cầu thi công truyền thống hay sản xuất mô-đun lắp ghép tại nhà máy, Le Sector đều kết nối chủ dự án với đối tác thi công phù hợp.",
          "cardTitle": "Điều phối Thực thi Tích hợp",
          "cardDesc": "Le Sector ghép nối yêu cầu dự án với nhà thầu uy tín, nhà máy mô-đun và các đội thợ chuyên sâu.",
          "whoTagline": "Đối tượng Tham gia",
          "whoIsItForTitle": "Dành cho Ai",
          "whoIsItForItems": [
            {
              "title": "Nhà thầu & Xây dựng Cấp phép",
              "desc": "Tìm kiếm cơ hội tiếp cận danh mục dự án nhà ở và mô-đun đã qua thẩm định."
            },
            {
              "title": "Nhà máy Sản xuất Mô-đun",
              "desc": "Cơ sở sản xuất tìm kiếm đơn hàng công suất và đối tác thi công lắp đặt."
            },
            {
              "title": "Đội ngũ Kỹ thuật & Thực thi",
              "desc": "Nhà thầu phụ, quản lý công trường và chuyên gia kỹ thuật tìm kiếm cơ hội tham gia."
            }
          ],
          "typesTagline": "Phạm vi Cơ hội",
          "opportunityTypesTitle": "Các Loại hình Cơ hội",
          "opportunityTypesItems": [
            {
              "title": "Dự án Cần Nhà thầu Cấp phép",
              "desc": "Dự án nhà ở cần năng lực tổng thầu thi công và quản lý công trường.",
              "tag": "Cần Nhà thầu"
            },
            {
              "title": "Kết nối Nhà máy Mô-đun",
              "desc": "Dự án cần năng lực sản xuất khung thép, tấm tường hoặc khối nhà tại nhà máy.",
              "tag": "Công suất Mô-đun"
            },
            {
              "title": "Hợp tác Thực thi Thi công",
              "desc": "Mô hình kết nối chuẩn bị mặt bằng với thi công lắp ráp mô-đun.",
              "tag": "Đối tác Thực thi"
            },
            {
              "title": "Hỗ trợ Quản lý Dự án",
              "desc": "Dự án cần quản lý dự án đại diện chủ đầu tư và kiểm soát chất lượng.",
              "tag": "Quản lý Dự án"
            }
          ],
          "facTagline": "Quy trình Thực thi",
          "facilitationTitle": "Cách Le Sector Hỗ trợ",
          "facilitationSteps": [
            {
              "step": "01",
              "title": "Xác định Scope Dự án",
              "desc": "Đánh giá bản vẽ thiết kế, thông số kỹ thuật và tiến độ bàn giao."
            },
            {
              "step": "02",
              "title": "Khớp nối Năng lực",
              "desc": "Xác định nhà thầu hoặc nhà máy có công suất, chứng chỉ và phạm vi phù hợp."
            },
            {
              "step": "03",
              "title": "Kết nối Bảo mật",
              "desc": "Tổ chức các buổi làm việc bài bản giữa chủ dự án và đội ngũ thực thi."
            },
            {
              "step": "04",
              "title": "Điều phối Công trường",
              "desc": "Hỗ trợ vận chuyển từ nhà máy ra công trường và giám sát chất lượng lắp đặt."
            }
          ],
          "scenariosTagline": "Kịch bản Thực tế",
          "scenariosTitle": "Ví dụ Kịch bản Dự án",
          "scenariosItems": [
            {
              "title": "Xây dựng Nhà ở Mô-đun Nhiều Căn",
              "type": "Công suất Mô-đun",
              "description": "Dự án nhà phố cần đối tác nhà máy mô-đun sản xuất và lắp dựng 6 khối nhà 2 tầng.",
              "targetMatch": "Nhà phát triển + Nhà máy Mô-đun"
            },
            {
              "title": "Thi công Biệt thự Kiến trúc Tùy chỉnh",
              "type": "Cần Nhà thầu",
              "description": "Dự án nhà ở cao cấp đã có bản vẽ cần tìm nhà thầu có kinh nghiệm vật liệu hiện đại.",
              "targetMatch": "Chủ nhà + Tổng thầu"
            }
          ],
          "resourcesTagline": "Thông tin & Tham khảo",
          "futureResourcesTitle": "Tài nguyên & Trường hợp Thực tế",
          "futureResourcesDesc": "Truy cập khung thực thi, thông số nhà máy mô-đun và các bài viết thực tế.",
          "futureResourcesItems": [
            {
              "title": "Hướng dẫn Vận chuyển & Lắp đặt Mô-đun",
              "description": "Hướng dẫn kỹ thuật về xe cẩu, vận chuyển và kết nối hạ tầng.",
              "status": "Hướng dẫn Kỹ thuật",
              "linkText": "Xem Hướng dẫn"
            },
            {
              "title": "Đăng ký Năng lực Nhà thầu",
              "description": "Các tiêu chí cần thiết cho nhà thầu gia nhập mạng lưới Le Sector.",
              "status": "Mẫu Đăng ký",
              "linkText": "Đăng ký Năng lực"
            },
            {
              "title": "Bài viết Dự án Thực tế Le Sector",
              "description": "Khám phá các dự án nhà ở hoàn thiện bằng phương pháp truyền thống và mô-đun.",
              "status": "Trường hợp Thực tế",
              "linkText": "Xem Dự án Thực tế"
            }
          ],
          "ctaBox": {
            "title": "Bạn có Dự án hoặc Năng lực Thực thi?",
            "desc": "Liên hệ cố vấn Le Sector để trao đổi yêu cầu dự án hoặc gia nhập mạng lưới đối tác thực thi.",
            "buttonText": "Tư vấn Dự án & Thực thi"
          }
        },
        "professionalExpertise": {
          "badge": "HỆ SINH THÁI LE SECTOR • CHUYÊN GIA & TƯ VẤN",
          "title": "Lĩnh vực Chuyên gia & Tư vấn",
          "headline": "Năng lực Chuyên môn & Cố vấn",
          "subheadline": "Giới thiệu các cơ hội tham gia dành cho chuyên gia tư vấn thiết kế, kỹ thuật, quy hoạch và cố vấn.",
          "heroCta": "Tư vấn Chuyên gia & Chuyên môn",
          "secondaryCta": "Xem Danh mục Cơ hội",
          "backToAll": "Quay lại Tất cả Cơ hội",
          "highlights": [
            "Kiến trúc sư & Thiết kế",
            "Kỹ sư Kết cấu & Hạ tầng",
            "Chuyên gia Quy hoạch & Đo đạc",
            "Tư vấn Chuyên môn"
          ],
          "overviewTagline": "Hệ sinh thái Chuyên gia",
          "overviewTitle": "Mục đích của Lĩnh vực này",
          "overviewDesc": "Lĩnh vực Chuyên gia & Tư vấn kết nối các kiến trúc sư, kỹ sư kết cấu, kỹ sư hạ tầng, chuyên gia quy hoạch, trắc địa và tư vấn tài chính với các dự án nhà ở ngay từ giai đoạn đầu. Le Sector đưa chuyên gia vào sớm nhằm tối ưu hóa thiết kế, phê duyệt quy hoạch và tính khả thi thi công.",
          "overviewSubText": "Việc tham gia sớm của các chuyên gia giúp dự án đạt chất lượng kiến trúc cao hơn, rút ngắn thời gian phê duyệt và tích hợp mượt mà với công nghệ xây dựng hiện đại.",
          "cardTitle": "Mạng lưới Tư vấn Bài bản",
          "cardDesc": "Le Sector kết nối chuyên gia tư vấn với dự án nhà ở thông qua quy trình làm việc chuyên nghiệp và bảo mật.",
          "whoTagline": "Đối tượng Tham gia",
          "whoIsItForTitle": "Dành cho Ai",
          "whoIsItForItems": [
            {
              "title": "Kiến trúc sư & Nhà thiết kế",
              "desc": "Tìm kiếm cơ hội thiết kế nhà ở tùy chỉnh và nhà ở mô-đun."
            },
            {
              "title": "Kỹ sư & Chuyên gia Quy hoạch",
              "desc": "Kỹ sư kết cấu, hạ tầng và quy hoạch chuyên sâu về phân lô và thi công mô-đun."
            },
            {
              "title": "Chuyên gia Thẩm định & Pháp lý",
              "desc": "Trắc địa, kiểm định, dự toán và tài chính dự án."
            }
          ],
          "typesTagline": "Phạm vi Cơ hội",
          "opportunityTypesTitle": "Các Loại hình Cơ hội",
          "opportunityTypesItems": [
            {
              "title": "Thiết kế Kiến trúc & DfMA",
              "desc": "Tư vấn thiết kế tối ưu cho sản xuất và lắp ráp nhà máy (DfMA).",
              "tag": "Tích hợp Thiết kế"
            },
            {
              "title": "Thẩm tra Kỹ thuật & Kết cấu",
              "desc": "Tư vấn kỹ thuật kết cấu nền móng và hạ tầng cho công trình mô-đun.",
              "tag": "Kỹ thuật Kết cấu"
            },
            {
              "title": "Tư vấn Quy hoạch & Phê duyệt",
              "desc": "Tư vấn quy hoạch đô thị cho dự án phân lô và tái thiết phức tạp.",
              "tag": "Quy hoạch Đô thị"
            },
            {
              "title": "Kiểm định & Chứng nhận Xây dựng",
              "desc": "Tư vấn tuân thủ quy chuẩn xây dựng, tiêu chuẩn năng lượng và cấp phép.",
              "tag": "Pháp lý Kiểm định"
            }
          ],
          "facTagline": "Quy trình Thực thi",
          "facilitationTitle": "Cách Le Sector Hỗ trợ",
          "facilitationSteps": [
            {
              "step": "01",
              "title": "Xác định Nhu cầu Chuyên môn",
              "desc": "Xác định các yêu cầu kỹ thuật của dự án ngay từ giai đoạn ý tưởng."
            },
            {
              "step": "02",
              "title": "Khớp nối Chuyên gia",
              "desc": "Lựa chọn chuyên gia tư vấn có kinh nghiệm phù hợp với bài toán dự án."
            },
            {
              "step": "03",
              "title": "Kết nối Bảo mật",
              "desc": "Tổ chức trao đổi thông tin bảo mật giữa chủ dự án và chuyên gia."
            },
            {
              "step": "04",
              "title": "Phối hợp Tích hợp",
              "desc": "Hỗ trợ quy trình phối hợp thiết kế giữa chuyên gia, nhà máy và công trường."
            }
          ],
          "scenariosTagline": "Kịch bản Thực tế",
          "scenariosTitle": "Ví dụ Kịch bản Dự án",
          "scenariosItems": [
            {
              "title": "Tư vấn Thiết kế Kiến trúc DfMA",
              "type": "Tích hợp Thiết kế",
              "description": "Dự án nhà ở cần kiến trúc sư am hiểu giới hạn sản xuất mô-đun để hoàn thiện thiết kế cơ sở.",
              "targetMatch": "Chủ dự án + Kiến trúc sư Mô-đun"
            },
            {
              "title": "Phê duyệt Quy hoạch Phân lô",
              "type": "Quy hoạch Đô thị",
              "description": "Chủ đất cần đơn vị tư vấn quy hoạch lập hồ sơ xin phép phân lô với chính quyền địa phương.",
              "targetMatch": "Chủ đất + Chuyên gia Quy hoạch"
            }
          ],
          "resourcesTagline": "Thông tin & Tham khảo",
          "futureResourcesTitle": "Tài nguyên & Liên kết Chuyên môn",
          "futureResourcesDesc": "Truy cập khung thiết kế DfMA, hướng dẫn quy hoạch và thông tin mạng lưới chuyên gia.",
          "futureResourcesItems": [
            {
              "title": "Hướng dẫn Thiết kế Mô-đun (DfMA)",
              "description": "Sổ tay kỹ thuật về kích thước vận chuyển, mối nối kết cấu và lưới trục.",
              "status": "Sổ tay Kỹ thuật",
              "linkText": "Xem Hướng dẫn DfMA"
            },
            {
              "title": "Đăng ký Mạng lưới Chuyên gia",
              "description": "Gia nhập danh mục chuyên gia Le Sector để nhận cơ hội dự án sớm.",
              "status": "Mẫu Đăng ký",
              "linkText": "Gia nhập Mạng lưới"
            },
            {
              "title": "Trường hợp Tích hợp Thực tế",
              "description": "Ví dụ sự phối hợp thành công giữa kiến trúc sư, kỹ sư và nhà thầu.",
              "status": "Trường hợp Thực tế",
              "linkText": "Khám phá Dự án"
            }
          ],
          "ctaBox": {
            "title": "Bạn là Chuyên gia Chuyên môn hoặc Cần Đội ngũ Tư vấn?",
            "desc": "Liên hệ Le Sector để trao đổi cơ hội tư vấn dự án hoặc gia nhập mạng lưới chuyên gia.",
            "buttonText": "Tư vấn Chuyên gia & Chuyên môn"
          }
        }
      }
    }
  },
  "hi": {
    "promo": {
      "open": "प्रचार ऑफ़र खोलें",
      "latestUpdates": "नवीनतम अपडेट",
      "close": "प्रचार पॉपअप बंद करें",
      "prev": "पिछली स्लाइड",
      "next": "अगली स्लाइड",
      "goto": "स्लाइड पर जाएं",
      "dontShowAgain": "फिर न दिखाएं"
    },
    "nav": {
      "home": "होम",
      "about": "हमारे बारे में",
      "services": "सेवाएं",
      "howItWorks": "यह कैसे काम करता है",
      "faq": "अक्सर पूछे जाने वाले सवाल",
      "contact": "संपर्क करें",
      "hub": "विज्ञापन हब"
    },
    "footer": {
      "tagline": "पारंपरिक और मॉड्यूलर कस्टम होम्स। निश्चितता के साथ डिलीवरी।",
      "mission": "हम पारंपरिक कस्टम होम्स और मॉड्यूलर होम्स दोनों के लिए पेशेवर प्रोजेक्ट मैनेजमेंट और डिलीवरी प्रदान करते हैं, जिससे क्लाइंट के डिज़ाइन को सुव्यवस्थित नियंत्रण और स्पष्टता मिलती है।",
      "col1": "गैजेट्स",
      "col2": "सेवाएं",
      "col3": "संपर्क",
      "col4": "भागीदार और कानूनी",
      "gadgets": {
        "calculator": "लागत कैलकुलेटर",
        "measurer": "साइट मापक",
        "design": "डिज़ाइन स्टूडियो"
      },
      "serviceList": [
        "मॉड्यूलर आवास डिलीवरी",
        "डिज़ाइन व्यवहार्यता",
        "मॉड्यूलर अनुकूलन",
        "निर्माण पूर्व समन्वय"
      ],
      "contactDetails": {
        "phone": "0432 866 168",
        "area": "ऑस्ट्रेलिया (विक्टोरिया)",
        "hours": "सोमवार – शुक्रवार, 9:00am – 5:00pm"
      },
      "partners": [
        {
          "name": "1st Mortgage Group",
          "role": "बंधक दलाल",
          "url": "https://www.1stmg.com.au"
        },
        {
          "name": "8 Miles Estate",
          "role": "रियल एस्टेट एजेंसी",
          "url": "https://www.8milesestate.com.au"
        },
        {
          "name": "Core-Base Construction",
          "role": "नींव और ज़मीनी काम",
          "url": "https://www.corebasecons.com.au"
        }
      ],
      "legal": [
        "गोपनीयता नीति",
        "नियम और शर्तें"
      ],
      "copyright": "© Le Sector 2025. सर्वाधिकार सुरक्षित।",
      "disclaimer": "Le Sector मॉड्यूलर आवास वितरण और समन्वय सेवाएं प्रदान करता है। डिज़ाइन सेवाएं बाहरी वास्तुकारों और डिजाइनरों द्वारा प्रदान की जाती हैं। परिषद के अनुमोदन और साइट की शर्तों के अधीन।",
      "cta": {
        "title": "भूमि या वित्त की चुनौतियों का सामना कर रहे हैं?",
        "sub": "अपनी निर्माण-पूर्व बाधाओं को हल करने के लिए हमारे रणनीतिक भागीदारों से बात करें।",
        "btn": "भागीदारों से जुड़ें"
      }
    },
    "home": {
      "heroTitle": "Le Sector प्रोजेक्ट डिलीवरी। पारंपरिक और मॉड्यूलर कस्टम होम्स का निश्चितता के साथ प्रबंधन।",
      "heroSub": "हम ग्राहकों को उनके प्रोजेक्ट के लिए सबसे उपयुक्त निर्माण दृष्टिकोण चुनने में मदद करते हैं—पारंपरिक कस्टम होम्स और मॉड्यूलर होम्स दोनों को योजना से पूर्णता तक पेशेवर प्रोजेक्ट प्रबंधन के साथ वितरित करते हैं।",
      "ctaStart": "अपनी निर्माण यात्रा शुरू करें",
      "ctaSpeak": "हमारी टीम से बात करें",
      "whoTitle": "हम कौन हैं",
      "whoMain": "Le Sector एक पेशेवर प्रोजेक्ट मैनेजमेंट और प्रोजेक्ट डिलीवरी कंपनी है जो पारंपरिक कस्टम होम्स और मॉड्यूलर होम्स दोनों का निर्माण करती है।",
      "whoSub1": "हम योजना, खरीद, सलाहकारों, साइट निर्माण और मॉड्यूलर निर्माण का समन्वय करते हैं—ताकि घर के मालिक और आर्किटेक्ट अपने विजन और बजट के अनुसार सही निर्माण विधि चुन सकें।",
      "whoSub2": "चाहे पारंपरिक कस्टम होम हो या मॉड्यूलर होम, प्रत्येक प्रोजेक्ट हमारी सुव्यवस्थित प्रोजेक्ट प्रबंधन प्रणाली का पालन करता है—जो कठोर गुणवत्ता आश्वासन, पारदर्शी दस्तावेज़ीकरण और सटीक प्रोजेक्ट डिलीवरी के लिए आईएसओ प्रबंधन ढांचे (ISO management framework) द्वारा समर्थित है।",
      "salesPoints": {
        "warranty": {
          "title": "विनियमित वारंटी",
          "desc": "पूर्ण मानक भवन वारंटी सुरक्षा (7 वर्ष), बिल्कुल पारंपरिक निर्माण की तरह।"
        },
        "delays": {
          "title": "साइट पर कोई देरी नहीं",
          "desc": "साइट पर कभी भी श्रम चरण नहीं होते। फैक्टरी विनिर्माण बारिश की देरी को समाप्त करता है।"
        },
        "fixedPrice": {
          "title": "निश्चित मूल्य अनुबंध",
          "desc": "निश्चित मूल्य पर रहें। हम विनिर्माण शुरू होने से पहले लागत को लॉक करते हैं।"
        },
        "costRises": {
          "title": "कोई लागत वृद्धि नहीं",
          "desc": "कभी भी सामग्री लागत वृद्धि का सामना न करें। हम पहले ही सामग्री खरीद लेते हैं।"
        }
      },
      "lowRisk": {
        "badge": "वित्तीय लाभ",
        "title": "कम जोखिम वाला मॉड्यूलर बिल्डिंग मॉडल",
        "desc": "Le Sector घर के मालिकों के लिए वित्तीय जोखिम को कम करने के लिए डिज़ाइन किया गया एक मॉड्यूलर हाउसिंग डिलीवरी मॉडल प्रदान करता है। पारंपरिक निर्माण परियोजनाओं के विपरीत, जिन्हें अक्सर प्रगतिशील निर्माण ऋण और निरंतर ब्याज भुगतान की आवश्यकता होती है, हमारा मॉड्यूलर डिलीवरी दृष्टिकोण ग्राहकों को निर्माण चरण के दौरान काफी कम वित्तीय जोखिम के साथ आगे बढ़ने की अनुमति देता है।",
        "cardTitle": "पूरा होने पर भुगतान करें",
        "highlight": "निर्माण नींव (स्लैब) के साथ शुरू होता है, जिसके बाद मॉड्यूलर घर की असेंबली और स्थापना होती है। इस प्रक्रिया के दौरान, ग्राहकों को कुल अनुबंध मूल्य का केवल 5% से शुरू होने वाली जमा राशि का भुगतान करना होता है। शेष राशि का भुगतान निपटान पर देय होता है जब घर पूरा हो जाता है और सौंपने के लिए तैयार होता है।",
        "keyAdvantages": "मुख्य लाभ",
        "bullets": [
          "कई मामलों में पारंपरिक निर्माण ऋण की कोई आवश्यकता नहीं",
          "परियोजना में देरी और लागत की अनिश्चितता का कम जोखिम",
          "निर्माण अवधि के दौरान कोई निरंतर ब्याज भुगतान नहीं",
          "परियोजना के शुरू होने और पूरा होने के बीच अधिक निश्चितता",
          "तैयार घर के वितरण के साथ भुगतान संरेखित"
        ],
        "conclusion": "घर के मालिक से निर्माण जोखिम को हटाकर एक नियंत्रित वितरण प्रक्रिया में स्थानांतरित करके, Le Sector एक नया घर बनाने के लिए एक सुरक्षित और अधिक अनुमानित मार्ग प्रदान करता है।"
      }
    },
    "ecosystem": {
      "tag": "एकीकृत पारिस्थितिकी तंत्र",
      "title": "Le Sector + CoreBase",
      "desc": "Le Sector, CoreBase Construction के साथ मिलकर काम करता है, जो नींव और ज़मीनी काम के विशेषज्ञ हैं।",
      "quote": "यह सहयोग विनिर्माण और साइट पर होने वाले काम के बीच के अंतर को समाप्त करता है—जिसके परिणामस्वरूप बेहतर डिलीवरी होती है।",
      "leSectorSubtitle": "डिज़ाइन और मॉड्यूलर",
      "coreBaseSubtitle": "ज़मीनी काम"
    },
    "process": {
      "tag": "हमारी प्रक्रिया",
      "title": "प्रक्रिया-आधारित आत्मविश्वास",
      "steps": [
        {
          "num": "01",
          "title": "डिज़ाइन समीक्षा और व्यवहार्यता",
          "desc": "हम मॉड्यूलर उपयुक्तता और अनुपालन के लिए योजनाओं का आकलन करते हैं।"
        },
        {
          "num": "02",
          "title": "मॉड्यूलर अनुकूलन",
          "desc": "तेज़ी और लागत निश्चितता के लिए योजनाओं को परिष्कृत किया जाता है।"
        },
        {
          "num": "03",
          "title": "निर्माण पूर्व समन्वय",
          "desc": "हम विनिर्माण और साइट की आवश्यकताओं को संरेखित करते हैं।"
        },
        {
          "num": "04",
          "title": "मॉड्यूलर डिलीवरी",
          "desc": "मॉड्यूल्स को साइट के बाहर बनाया जाता है और सटीकता के साथ डिलीवर किया जाता है।"
        }
      ],
      "finalCta": "क्या आप मॉड्यूलर डिलीवरी के लिए तैयार हैं?",
      "bookBtn": "परामर्श बुक करें"
    },
    "about": {
      "tag": "हमारा दृष्टिकोण",
      "title": "आर्किटेक्चर पहले। डिलीवरी हमेशा।",
      "sub1": "Le Sector को डिज़ाइन-संचालित मॉड्यूलर आवास की बढ़ती मांग का समर्थन करने के लिए स्थापित किया गया था—जहां वास्तुशिल्प गुणवत्ता का त्याग किए बिना गति और निश्चितता की आवश्यकता होती है।",
      "sub2": "पूर्व-डिज़ाइन किए गए किट पेश करने के बजाय, हम एक मॉड्यूलर निर्माण पद्धति के माध्यम से ग्राहक-डिज़ाइन की गई योजनाओं को वितरित करने में विशेषज्ञ हैं।",
      "approachTitle": "आधुनिक मॉड्यूलर घरों के प्रति हमारा दृष्टिकोण",
      "approachP1": "Le Sector में, हम इंजीनियर स्टील फ्रेम संरचनाओं और उन्नत हल्के कंक्रीट पैनलों के साथ निर्मित आधुनिक मॉड्यूलर घरों को वितरित करने में विशेषज्ञ हैं।",
      "approachP2": "हमारी निर्माण प्रणाली को एक स्थायी घर से अपेक्षित स्थायित्व, सुरक्षा और आराम प्रदान करने के लिए डिज़ाइन किया गया है, जबकि मॉड्यूलर निर्माण की दक्षता से लाभ होता है।",
      "approachP3": "कंटेनर आवास के विपरीत, हमारे मॉड्यूलर घर परिवर्तित शिपिंग कंटेनर नहीं हैं। प्रत्येक घर को एक संरचनात्मक स्टील फ्रेम और एक एकीकृत लॉकिंग सिस्टम के साथ सटीक-निर्मित दीवार पैनलों का उपयोग करके उद्देश्य-निर्मित किया गया है। यह मजबूत संरचनात्मक अखंडता, उत्कृष्ट इन्सुलेशन प्रदर्शन और कुशल ऑन-साइट स्थापना की अनुमति देता है।",
      "approachP4": "एक बार स्लैब नींव तैयार हो जाने के बाद, मुख्य मॉड्यूलर संरचना को साइट पर तेजी से स्थापित किया जा सकता है, जिससे पारंपरिक निर्माण के लिए आमतौर पर आवश्यक समय काफी कम हो जाता है।",
      "approachP5": "हमारा लक्ष्य आधुनिक निर्माण तकनीक को विचारशील परियोजना समन्वय के साथ जोड़ना है, जिससे घर के मालिकों को डिजाइन से लेकर पूर्ण घर तक एक विश्वसनीय और कुशल मार्ग मिल सके।",
      "roleTitle": "हमारी भूमिका",
      "roleSub": "हम एक डिलीवरी पार्टनर हैं।",
      "roles": [
        {
          "title": "अनुवाद",
          "desc": "जटिल आर्किटेक्चरल डिज़ाइन को मॉड्यूलर निष्पादन में बदलना।"
        },
        {
          "title": "प्रबंधन",
          "desc": "विनिर्माण और साइट के बीच इंटरफेस का प्रबंधन करना।"
        },
        {
          "title": "स्पष्टता",
          "desc": "पूरी प्रक्रिया के दौरान संरचना और निश्चितता प्रदान करना।"
        }
      ],
      "integrity": "डिज़ाइन अखंडता",
      "builtTitle": "ताकत, आराम और दीर्घायु के लिए निर्मित",
      "builtP1": "Le Sector मॉड्यूलर घरों को दीर्घकालिक आवासीय जीवन के लिए डिज़ाइन किया गया है। हमारी संरचनात्मक प्रणाली में स्टील फ्रेमिंग और उन्नत हल्के कंक्रीट पैनल शामिल हैं जो आग प्रतिरोधी, जल प्रतिरोधी हैं, और मजबूत ध्वनिक और थर्मल प्रदर्शन प्रदान करते हैं।",
      "builtP2": "यह दृष्टिकोण सुनिश्चित करता है कि मॉड्यूलर निर्माण गुणवत्ता में समझौता नहीं है, बल्कि अधिक दक्षता और निश्चितता के साथ आधुनिक घरों को वितरित करने का एक बेहतर तरीका है।"
    },
    "services": {
      "tag": "हमारी विशेषज्ञता",
      "title": "डिज़ाइन-आधारित मॉड्यूलर डिलीवरी",
      "desc": "पहले डिज़ाइन समीक्षा से अंतिम इंस्टॉलेशन तक पूरी निगरानी।",
      "list": [
        "डिज़ाइन व्यवहार्यता मूल्यांकन",
        "मॉड्यूलर अनुकूलन और समन्वय",
        "इंजीनियरिंग संरेखण",
        "निर्माण पूर्व योजना",
        "विनिर्माण समन्वय",
        "डिलीवरी और इंस्टॉलेशन निगरानी"
      ],
      "feasibility": {
        "title": "मॉड्यूलर व्यवहार्यता मूल्यांकन पैकेज",
        "price": "$490 - $990",
        "tag": "पहला कदम",
        "desc": "हम सिर्फ निर्माण नहीं बेच रहे हैं। हम स्पष्टता बेच रहे हैं। किसी भी अनुबंध पर हस्ताक्षर करने से पहले, हम आपको यह तय करने में मदद करते हैं कि परियोजना सार्थक है या नहीं।",
        "features": [
          "प्रारंभिक साइट व्यवहार्यता जांच",
          "बजट रेंज विश्लेषण",
          "समयरेखा भविष्यवाणी",
          "जोखिम सूची",
          "वित्त पोषण संरचना सलाह",
          "लागत तुलना (पारंपरिक बनाम मॉड्यूलर)"
        ]
      },
      "modelsTitle": "सगाई के मॉडल",
      "models": [
        {
          "title": "मोड A: प्रोजेक्ट एजेंसी",
          "tag": "डिज़ाइन समन्वय + प्रबंधन",
          "fee": "निश्चित प्रबंधन शुल्क: 8–12%",
          "features": [
            "आप निर्माण अनुबंध रखते हैं",
            "हम एकीकरण का प्रबंधन करते हैं",
            "सामग्री पर कोई मार्कअप नहीं",
            "मालिक-बिल्डरों के लिए आदर्श"
          ]
        },
        {
          "title": "मोड B: निवेश कनेक्शन",
          "tag": "भूमि और पूंजी को जोड़ना",
          "fee": "संरचना शुल्क + प्रबंधन शुल्क",
          "features": [
            "हम जमींदारों को पूंजी से जोड़ते हैं",
            "हम निवेशकों को परियोजनाओं से जोड़ते हैं",
            "मध्यवर्ती संरचना डिजाइन",
            "पूर्ण परियोजना वितरण प्रबंधन"
          ]
        }
      ],
      "groundTitle": "नींव और साइट की तैयारी",
      "groundTag": "CoreBase के माध्यम से",
      "groundList": [
        {
          "title": "नींव",
          "desc": "मॉड्यूल के वजन के लिए अनुकूलित पेशेवर नींव।"
        },
        {
          "title": "ज़मीनी काम",
          "desc": "पूर्ण नागरिक कार्य, जल निकासी और उपयोगिता ट्रेंचिंग।"
        },
        {
          "title": "संरचनात्मक तैयारी",
          "desc": "सटीक सेट-आउट यह सुनिश्चित करने के लिए कि मॉड्यूल सही ढंग से फिट हो।"
        }
      ]
    },
    "faq": {
      "tag": "विश्वास और स्पष्टता",
      "title": "अक्सर पूछे जाने वाले सवाल",
      "items": [
        {
          "q": "क्या आप डिज़ाइन प्रदान करते हैं?",
          "a": "नहीं। हम ग्राहक की योजनाओं या बाहरी वास्तुकारों के साथ काम करते हैं।"
        },
        {
          "q": "क्या मॉड्यूलर सस्ता है?",
          "a": "यह समय और लागत की निश्चितता के बारे में है, लेकिन गुणवत्ता प्रीमियम रहती है।"
        },
        {
          "q": "क्या डिज़ाइन को अनुकूलित किया जा सकता है?",
          "a": "हाँ—हमारी प्रक्रिया कस्टम परिणामों का समर्थन करने के लिए बनाई गई है।"
        },
        {
          "q": "नींव का प्रबंधन कौन करता है?",
          "a": "नींव का काम CoreBase Construction के साथ मिलकर किया जाता है।"
        }
      ]
    },
    "contact": {
      "tag": "चर्चा करें",
      "title": "आपके प्रोजेक्ट की शुरुआत",
      "desc": "मेलबर्न और विक्टोरिया के क्षेत्रों में सेवाएं।",
      "labels": {
        "phone": "डायरेक्ट लाइन",
        "email": "ईमेल",
        "area": "सेवा क्षेत्र",
        "formTitle": "परामर्श अनुरोध",
        "name": "पूरा नाम",
        "brief": "प्रोजेक्ट संक्षिप्त",
        "placeholder": "अपने विजन का वर्णन करें...",
        "submit": "परामर्श बुक करें"
      }
    },
    "calculator": {
      "disclaimer": "* अस्वीकरण: सभी कीमतें अनुमानित हैं और अंतिम अनुबंध मूल्य के अधीन हैं।"
    },
    "ownerPartnership": {
      "hero": {
        "tagline": "रणनीतिक निर्माण सलाहकार",
        "title1": "रणनीतिक वितरण।",
        "title2": "पूर्ण वित्तीय नियंत्रण।",
        "desc": "एक विशेषज्ञ परियोजना प्रबंधन कार्यालय (PMO) के साथ कस्टम निर्माण की जटिलताओं को नेविगेट करें। आप पूर्ण वित्तीय नियंत्रण बनाए रखते हैं जबकि हम दिन-प्रतिदिन के निष्पादन को चलाते हैं。",
        "cta": "परामर्श बुक करें"
      },
      "why": {
        "tagline": "सलाहकार का लाभ",
        "title": "ग्राहक-निर्माता संबंध की पुनर्कल्पना",
        "p1": "पारंपरिक निश्चित मूल्य अनुबंध स्वाभाविक रूप से निर्माता के लाभ को घर के मालिक के बजट के खिलाफ रखते हैं। विविधताएं विवाद बन जाती हैं, और अस्पष्टता एक वित्तीय ढाल के रूप में कार्य करती है। हमने इस घर्षण को पूरी तरह से खत्म करने के लिए ओनर पार्टनरशिप बनाई है।",
        "p2": "एक पारदर्शी प्रबंधन शुल्क पर काम करके, हमारे वित्तीय प्रोत्साहन पूरी तरह से आपके साथ संरेखित हैं: एक असाधारण वास्तुशिल्प संपत्ति को बिना किसी समझौते के, यथासंभव कुशलता से वितरित करना।",
        "cardTitle": "सच्चा संरेखण",
        "cardDesc": "साझा उद्देश्य पारंपरिक संघर्ष की जगह लेते हैं।",
        "b1Title": "100% लागत पारदर्शिता",
        "b1Desc": "प्रत्येक व्यापार छूट और बचत सीधे आपको वापस कर दी जाती है।",
        "b2Title": "पूर्ण सुरक्षा",
        "b2Desc": "आप परियोजना की पूंजी रखते हैं। आपके द्वारा कार्य को मंजूरी देने तक धनराशि कभी भी आपका खाता नहीं छोड़ती है।"
      },
      "process": {
        "tagline": "रणनीतिक चरणबद्धता",
        "title": "वितरण कार्यप्रणाली",
        "steps": [
          {
            "title": "रणनीतिक संरेखण",
            "desc": "हम आपकी वास्तुशिल्प योजनाओं का विश्लेषण करते हैं, यथार्थवादी बजटीय ढांचा स्थापित करते हैं, और मास्टर डिलीवरी रणनीति को परिभाषित करते हैं।"
          },
          {
            "title": "पारदर्शी खरीद",
            "desc": "हम प्रीमियम ट्रेडों के हमारे जाँचे-परखे नेटवर्क को आपकी परियोजना का टेंडर देते हैं, यह सुनिश्चित करते हुए कि प्रतिस्पर्धी बाजार दरें सीधे आप तक पहुंचाई जाएं।"
          },
          {
            "title": "सटीक निष्पादन",
            "desc": "हमारी समर्पित साइट प्रबंधन टीम दैनिक कार्यों का समन्वय करती है, सख्त गुणवत्ता नियंत्रण और मास्टर शेड्यूल के पालन को लागू करती है।"
          },
          {
            "title": "गुणवत्ता सौंपना",
            "desc": "हम विस्तृत दोष समीक्षा करते हैं, प्रमाणन को अंतिम रूप देते हैं, और औपचारिक रूप से आपकी पूर्ण वास्तुशिल्प संपत्ति सौंपते हैं।"
          }
        ]
      },
      "matrix": {
        "tagline": "परिभाषित दायरा",
        "title": "निष्पादन में स्पष्टता",
        "ownerTitle": "मालिक",
        "ownerItems": [
          "एक समर्पित निजी खाते में पूंजी बरकरार रखता है",
          "सभी प्रमुख डिजाइन और स्कोप विविधताओं को अधिकृत करता है",
          "ट्रेडों को मील का पत्थर भुगतान को मंजूरी देता है और जारी करता है",
          "अंतिम कार्यकारी निगरानी बनाए रखता है"
        ],
        "sharedTitle": "साझा",
        "sharedItems": [
          "चल रहे बजट अनुकूलन और समीक्षा",
          "सक्रिय मूल्य इंजीनियरिंग निर्णय",
          "वास्तुशिल्प खत्म और सामग्री चयन",
          "व्यापक मासिक प्रगति आकलन"
        ],
        "lsTitle": "Le Sector (PMO)",
        "lsItems": [
          "व्यापक साइट प्रबंधन और WHS अनुपालन",
          "कठोर व्यापार खरीद और माइक्रो-शेड्यूलिंग",
          "समझौताहीन गुणवत्ता आश्वासन और दोष समाधान",
          "एंड-टू-एंड काउंसिल और सर्टिफायर समन्वय"
        ]
      },
      "flow": {
        "tagline": "पूंजी संरक्षण",
        "title": "समझौताहीन वित्तीय सुरक्षा",
        "p1": "कस्टम निर्माण में सबसे बड़ा जोखिम इसकी तैनाती में दृश्यता के बिना अपनी पूंजी पर नियंत्रण छोड़ना है।",
        "p2": "हमारा मॉडल इसे पूरी तरह से कम करता है। Le Sector कभी भी आपके निर्माण कोष को नहीं रखता है। हम कार्य को कठोरता से प्रमाणित करते हैं, लेकिन भुगतान जारी करने का अधिकार आपके पास रहता है।",
        "s1Title": "विशेषज्ञ प्रमाणन",
        "s1Desc": "हमारे साइट प्रबंधक कठोरता से सत्यापित करते हैं कि व्यापार कार्य हमारे गुणवत्ता मानकों को पूरा करता है।",
        "s2Title": "वित्तीय मान्यता",
        "s2Desc": "PMO अनुमोदित निविदा और खरीद अनुसूची के खिलाफ चालान का मिलान करता है।",
        "s3Title": "कार्यकारी अनुमोदन",
        "s3Desc": "आप सीधे अपने सुरक्षित खाते से ट्रेडों को धनराशि जारी करने को अधिकृत करते हैं।"
      },
      "dash": {
        "tagline": "वास्तविक समय की खुफिया",
        "title": "हर आयाम में दृश्यता",
        "desc": "अज्ञात की चिंता को दूर करें। हमारा डिजिटल इंफ्रास्ट्रक्चर आपको हर समय आपके प्रोजेक्ट के स्वास्थ्य, प्रगति और वित्तीय स्थिति के बारे में कार्यकारी-स्तर की दृश्यता प्रदान करता है。",
        "items": [
          "गतिशील अनुसूची पूर्वानुमान",
          "व्यापक दैनिक साइट लॉग",
          "वास्तविक समय बजट भिन्नता विश्लेषण",
          "लाइव खरीद ट्रैकिंग"
        ],
        "hub": "प्रोजेक्ट हब",
        "budget": "तैनात पूंजी",
        "schedule": "मास्टर अनुसूची",
        "onTrack": "सही दिशा में",
        "week": "32 में से सप्ताह 14",
        "cashflow": "अनुमानित नकदी प्रवाह"
      },
      "suit": {
        "tagline": "क्लाइंट प्रोफाइल",
        "title": "क्या यह मॉडल आपके लिए सही है?",
        "idealTitle": "इनके लिए आदर्श",
        "idealItems": [
          "जटिल, प्रीमियम वास्तुशिल्प निर्माण करने वाले ग्राहक",
          "वे घर के मालिक जो समझौताहीन वित्तीय पारदर्शिता की मांग करते हैं",
          "वे जो दैनिक प्रबंधन के बोझ के बिना कार्यकारी निगरानी चाहते हैं",
          "लागत दक्षता और प्रत्यक्ष व्यापार पहुंच को प्राथमिकता देने वाले डेवलपर्स"
        ],
        "notTitle": "इनके लिए आदर्श नहीं",
        "notItems": [
          "पारंपरिक 'सेट-एंड-फॉरगेट' निश्चित मूल्य अनुबंध चाहने वाले ग्राहक",
          "साप्ताहिक मील का पत्थर भुगतान अधिकृत करने के लिए अनिच्छुक या असमर्थ",
          "ऐसी परियोजनाएं जो बिल्डर द्वारा आपूर्ति किए गए निर्माण वित्त पर निर्भर करती हैं",
          "मानकीकृत, वॉल्यूम-आधारित आवासीय निर्माण"
        ]
      },
      "faq": {
        "tagline": "सलाहकार अंतर्दृष्टि",
        "title": "अक्सर पूछे जाने वाले प्रश्न",
        "q1": "पूंजी नियंत्रण तंत्र वास्तव में कैसे कार्य करता है?",
        "a1": "आपकी परियोजना की धनराशि सुरक्षित रूप से आपके अपने नामित बैंक खाते में रहती है। यद्यपि Le Sector खरीद, अनुसूची और गुणवत्ता प्रमाणन का विशेषज्ञ प्रबंधन करता है, हमारे पास आपकी पूंजी तक सीधी पहुंच नहीं है। आप अंतिम वित्तीय प्राधिकरण के रूप में कार्य करते हैं, और केवल तभी सीधे सत्यापित ट्रेडों को भुगतान जारी करते हैं जब मील के पत्थर सफलतापूर्वक प्राप्त हो जाते हैं।",
        "q2": "यदि Le Sector धनराशि नहीं रखता है, तो आपकी जिम्मेदारी का दायरा क्या है?",
        "a2": "हम आपके समर्पित प्रोजेक्ट मैनेजमेंट ऑफिस (PMO) और विशिष्ट साइट टीम के रूप में काम करते हैं। हम रणनीतिक योजना, कठोर खरीद, दैनिक साइट प्रबंधन, समझौताहीन गुणवत्ता नियंत्रण और अनुसूची समन्वय के लिए पूरी जिम्मेदारी लेते हैं। हम निर्माण की जटिल वास्तविकता को निष्पादित करते हैं, जिससे आप पूरी तरह से कार्यकारी निगरानी पर ध्यान केंद्रित कर सकते हैं।",
        "q3": "क्या इस मॉडल के लिए मुझे पहले से निर्माण का अनुभव होना आवश्यक है?",
        "a3": "बिल्कुल नहीं। वास्तव में, हमारा मॉडल एक पारंपरिक अनुबंध की तुलना में काफी अधिक मार्गदर्शन और सुरक्षा प्रदान करता है। हमारे विशेषज्ञ सलाहकार दल के साथ हमारे पारदर्शी रिपोर्टिंग बुनियादी ढांचे को जोड़कर, आपको तकनीकी निर्माण विशेषज्ञता की आवश्यकता के बिना सूचित निर्णय लेने का अधिकार दिया गया है।"
      },
      "cta": {
        "title": "बातचीत शुरू करें",
        "desc": "एक निजी परामर्श अनुसूची करें ताकि पता लगाया जा सके कि हमारा सलाहकार मॉडल आपकी पूंजी की रक्षा कैसे कर सकता है और आपकी वास्तुशिल्प परियोजना के वितरण को कैसे बढ़ा सकता है।"
      },
      "form": {
        "step": "चरण",
        "of": "में से",
        "s1Title": "प्रोजेक्ट प्रकार",
        "s1Opts": [
          "एकल आवास",
          "बहु-इकाई विकास",
          "वाणिज्यिक",
          "अन्य"
        ],
        "s2Title": "प्रोजेक्ट चरण",
        "s2Opts": [
          "अवधारणा / विचार",
          "योजना / डिजाइन",
          "परिषद की मंजूरी",
          "निर्माण के लिए तैयार"
        ],
        "s3Title": "प्रोजेक्ट स्थान",
        "s3Ph": "उपनगर या पिनकोड",
        "s4Title": "अनुमानित निर्माण बजट",
        "s4Opts": [
          "$500k से कम",
          "$500k - $1M",
          "$1M - $2.5M",
          "$2.5M+"
        ],
        "s5Title": "आवश्यक सेवाएँ",
        "s5Opts": [
          "व्यवहार्यता अध्ययन",
          "वास्तुशिल्प डिजाइन",
          "परियोजना प्रबंधन",
          "निर्माण वितरण"
        ],
        "s6Title": "ग्राहक प्राथमिकताएं",
        "s6Opts": [
          "लागत दक्षता",
          "प्रीमियम गुणवत्ता",
          "वितरण की गति",
          "अधिकतम पारदर्शिता"
        ],
        "s7Title": "समयरेखा",
        "s7Opts": [
          "जितनी जल्दी हो सके",
          "3-6 महीने",
          "6-12 महीने",
          "12+ महीने"
        ],
        "s8Title": "अतिरिक्त जानकारी",
        "s8Ph": "हमें अपने प्रोजेक्ट दृष्टिकोण के बारे में और बताएं...",
        "s9Title": "संपर्क विवरण",
        "s9Ph1": "पूरा नाम",
        "s9Ph2": "ईमेल पता",
        "s9Ph3": "फोन नंबर",
        "back": "वापस",
        "continue": "जारी रखें",
        "submit": "परामर्श का अनुरोध करें",
        "submitting": "प्रस्तुत कर रहा है...",
        "error": "कृपया सभी आवश्यक फ़ील्ड पूरे करें।",
        "emailError": "कृपया एक वैध ईमेल पता दर्ज करें।",
        "submitError": "एक त्रुटि हुई। कृपया पुनः प्रयास करें।"
      },
      "confirm": {
        "title": "आवेदन प्राप्त हुआ",
        "desc": "आपकी रुचि के लिए धन्यवाद। हम आपके प्रोजेक्ट विवरण की समीक्षा करेंगे और 24 घंटे के भीतर आपसे संपर्क करेंगे।",
        "nextSteps": "आगे क्या होता है?",
        "bookTitle": "प्रारंभिक परामर्श",
        "bookDesc": "हमारा एक सलाहकार आपकी परिचयात्मक कॉल शेड्यूल करने के लिए संपर्क करेगा।",
        "dlTitle": "प्रोजेक्ट समीक्षा",
        "dlDesc": "हम आपके प्रोजेक्ट विवरण का प्रारंभिक मूल्यांकन करेंगे।"
      }
    },
    "exploreOpportunities": {
      "hero": {
        "badge": "LE SECTOR इकोसिस्टम • सुगम पेशेवर परिचय",
        "headline1": "हर बेहतरीन प्रोजेक्ट सही कनेक्शन से शुरू होता है।",
        "subheadline": "Le Sector द्वारा पेशेवर रूप से सुगम बनाए गए प्रोजेक्ट अवसरों और संभावित सहयोग का अन्वेषण करें।",
        "primaryCta": "अवसरों का अन्वेषण करें",
        "secondaryCta": "हमें बताएं कि आप क्या ढूंढ रहे हैं"
      },
      "pathways": {
        "tagline": "इकोसिस्टम के स्तंभ",
        "title": "आप क्या अन्वेषण कर सकते हैं?",
        "desc": "आवासीय अचल संपत्ति, विकास पूंजी और मॉड्यूलर प्रोजेक्ट डिलीवरी में सक्रिय मार्गों की खोज करें।",
        "items": [
          {
            "title": "संपत्ति और स्थल",
            "desc": "कस्टम आवासीय या मॉड्यूलर डिलीवरी के लिए उपयुक्त भूमि अवसर, विकास क्षमता और संपत्ति से जुड़े अवसर।"
          },
          {
            "title": "निवेश और पूंजी",
            "desc": "जांचे गए आवासीय निर्माण प्रस्तावों के लिए निवेश अवसर, पूंजी आवश्यकताएं और संभावित फंडिंग संरेखण।"
          },
          {
            "title": "प्रोजेक्ट्स और डिलीवरी",
            "desc": "लाइसेंस प्राप्त बिल्डरों, डेवलपर्स, विशेष विनिर्माण क्षमता या डिलीवरी भागीदारों की तलाश करने वाले सक्रिय प्रोजेक्ट।"
          },
          {
            "title": "पेशेवर विशेषज्ञता",
            "desc": "शुरुआती चरण में एकीकरण चाहने वाले आर्किटेक्ट, स्ट्रक्चरल इंजीनियर, शहरी योजनाकार और विशेष सलाहकार।"
          }
        ]
      },
      "howItWorks": {
        "tagline": "सुगम यात्रा",
        "title": "यह कैसे काम करता है",
        "steps": [
          {
            "step": "01",
            "title": "अन्वेषण करें",
            "desc": "निजी संपर्क जानकारी या संपत्ति का सटीक पता उजागर किए बिना अवसरों को ब्राउज़ करें।"
          },
          {
            "step": "02",
            "title": "रुचि व्यक्त करें",
            "desc": "Le Sector को बताएं कि कौन सा अवसर आपके लक्ष्यों और क्षमताओं के अनुकूल है।"
          },
          {
            "step": "03",
            "title": "हम समीक्षा करते हैं",
            "desc": "Le Sector पूछताछ की समीक्षा करता है, इरादे को सत्यापित करता है और उपयुक्तता की जांच करता है।"
          },
          {
            "step": "04",
            "title": "हम परिचय कराते हैं",
            "desc": "जहाँ उपयुक्त हो, Le Sector दोनों पक्षों की सहमति से पेशेवर परिचय सुगम बनाता है।"
          },
          {
            "step": "05",
            "title": "अवसर का अन्वेषण करें",
            "desc": "संबंधित पक्ष Le Sector की प्रोजेक्ट प्रबंधन विशेषज्ञता के समर्थन से संयुक्त तालमेल का मूल्यांकन करते हैं।"
          }
        ]
      },
      "why": {
        "tagline": "Le Sector का लाभ",
        "title": "Le Sector के माध्यम से क्यों?",
        "desc": "एक पेशेवर रूप से प्रबंधित अवसर नेटवर्क जो असत्यापित फ़ोरमों, सोशल मीडिया के शोर और अवांछित कॉल से मुक्त है।",
        "features": [
          {
            "title": "गोपनीयता सर्वोपरि",
            "desc": "कोई भी सार्वजनिक फोन नंबर, ईमेल पता या संपत्ति का सटीक पता उजागर नहीं किया जाता है।"
          },
          {
            "title": "पेशेवर समीक्षा",
            "desc": "परिचय से पहले अवसरों और व्यक्त की गई रुचियों का पूरी तरह से मूल्यांकन किया जाता है।"
          },
          {
            "title": "संरचित परिचय",
            "desc": "संपर्क विवरण देने के बजाय Le Sector शुरुआती परिचय को प्रबंधित और संचालित करता है।"
          },
          {
            "title": "प्रोजेक्ट डिलीवरी विशेषज्ञता",
            "desc": "Le Sector के अनुशासित कस्टम होम और मॉड्यूलर प्रोजेक्ट डिलीवरी ढांचे द्वारा सीधे समर्थित।"
          },
          {
            "title": "ISO-संरेखित गुणवत्ता प्रबंधन",
            "desc": "संरचित प्रबंधन ढांचे और ISO-संरेखित गुणवत्ता आश्वासन सिद्धांतों द्वारा निर्देशित।"
          },
          {
            "title": "शून्य वाणिज्यिक शोर",
            "desc": "स्पैम, अवांछित बिक्री संदेशों या असत्यापित विज्ञापनों से पूरी तरह मुक्त।"
          }
        ]
      },
      "scenarios": {
        "tagline": "उदाहरण परिदृश्य",
        "title": "अवसर परिदृश्य",
        "desc": "वास्तविक दुनिया के उदाहरण जो दिखाते हैं कि हमारे इकोसिस्टम में हितधारक कैसे जुड़ते हैं। (उदाहरण परिदृश्य — लाइव निर्देशिका सूची से अलग)।",
        "badge": "मामला परिदृश्य",
        "items": [
          {
            "title": "मेरे पास जमीन है",
            "quote": "“मैं यह समझना चाहता हूं कि सार्वजनिक रूप से सूचीबद्ध किए बिना मेरी संपत्ति के लिए क्या विकास अवसर मौजूद हो सकते हैं।”",
            "targetMatch": "डेवलपर्स, इक्विटी निवेशक, मॉड्यूलर विशेषज्ञ"
          },
          {
            "title": "मेरे पास पूंजी है",
            "quote": "“मैं संरचित परियोजना प्रबंधन द्वारा समर्थित संपत्ति या विकास के अवसरों की तलाश कर रहा हूं।”",
            "targetMatch": "भूमि मालिक, योग्य डेवलपर्स, कस्टम प्रोजेक्ट्स"
          },
          {
            "title": "मेरे पास एक प्रोजेक्ट है",
            "quote": "“मैं निर्माण में जाने के लिए सही डिलीवरी क्षमता, बिल्डर या निवेश भागीदार की तलाश कर रहा हूं।”",
            "targetMatch": "लाइसेंस प्राप्त बिल्डर, इक्विटी पार्टनर, प्रोजेक्ट डायरेक्टर"
          },
          {
            "title": "मेरे पास विशेषज्ञता है",
            "quote": "“मैं उपयुक्त आवासीय परियोजनाओं की तलाश कर रहा हूं जहां मैं विशेष डिजाइन या इंजीनियरिंग का योगदान दे सकूं।”",
            "targetMatch": "प्रोजेक्ट मालिक, डेवलपर्स, प्लानिंग कंसल्टेंट्स"
          }
        ]
      },
      "directory": {
        "tagline": "सक्रिय निर्देशिका",
        "title": "वर्तमान अवसर",
        "filterAllCategories": "सभी श्रेणियां",
        "filterAllModes": "सभी डिलीवरी मोड",
        "modeTraditional": "पारंपरिक कस्टम",
        "modeModular": "मॉड्यूलर असेंबली",
        "modeHybrid": "हाइब्रिड डिलीवरी",
        "emptyState": {
          "title": "नए अवसरों की वर्तमान में समीक्षा की जा रही है।",
          "desc": "हमें बताएं कि आप क्या ढूंढ रहे हैं और हम यह निर्धारित करने में मदद करेंगे कि क्या Le Sector नेटवर्क में कोई उपयुक्त अवसर है।",
          "cta": "हमें बताएं कि आप क्या ढूंढ रहे हैं"
        },
        "card": {
          "ref": "संदर्भ",
          "status": "स्थिति",
          "location": "स्थान",
          "stage": "चरण",
          "type": "प्रोजेक्ट का प्रकार",
          "budget": "बजट सीमा",
          "seeking": "की तलाश में",
          "timeframe": "समयसीमा",
          "expressInterest": "रुचि व्यक्त करें (EOI)",
          "viewDetails": "सारांश देखें"
        }
      },
      "privacy": {
        "tagline": "विवेक और सुरक्षा",
        "title": "गोपनीयता, विवेक और विश्वास गारंटी",
        "desc": "हम हर कदम पर आपकी पहचान, प्रोजेक्ट विवरण और वाणिज्यिक हितों की रक्षा करते हैं।",
        "items": [
          {
            "title": "कोई सार्वजनिक संपर्क जानकारी नहीं",
            "desc": "आपकी व्यक्तिगत पहचान, फोन और ईमेल कभी भी सार्वजनिक रूप से प्रकाशित या साझा नहीं किए जाते हैं।"
          },
          {
            "title": "समीक्षित परिचय",
            "desc": "किसी भी औपचारिक परिचय से पहले सभी पूछताछों की जांच Le Sector सलाहकारों द्वारा की जाती है।"
          },
          {
            "title": "दोहरी सहमति",
            "desc": "परिचय केवल तभी होता है जब प्रारंभिक स्क्रीनिंग के बाद दोनों पक्ष स्पष्ट रूप से सहमति व्यक्त करते हैं।"
          }
        ]
      },
      "cta": {
        "title": "क्या आपके मन में कोई अवसर है?",
        "desc": "हमें बताएं कि आप क्या ढूंढ रहे हैं और Le Sector को यह निर्धारित करने में मदद करने दें कि क्या हमारे नेटवर्क में कोई उपयुक्त कनेक्शन हो सकता है।",
        "button": "हमें बताएं कि आप क्या ढूंढ रहे हैं"
      },
      "formModal": {
        "title": "गोपनीय अवसर पूछताछ",
        "sub": "अपनी आवश्यकताओं या अवसर के बारे में बताएं। एक Le Sector सलाहकार पूर्ण विवेक के साथ समीक्षा करेगा।",
        "roleLabel": "आपकी प्राथमिक भूमिका",
        "roles": {
          "landOwner": "संपत्ति / भूमि मालिक",
          "investor": "निजी / इक्विटी निवेशक",
          "projectOwner": "प्रोजेक्ट डेवलपर / मालिक",
          "expert": "पेशेवर / उद्योग विशेषज्ञ"
        },
        "name": "पूरा नाम",
        "email": "ईमेल पता",
        "phone": "फोन नंबर",
        "region": "लक्ष्य क्षेत्र / स्थान",
        "category": "रुचि की श्रेणी",
        "details": "प्रोजेक्ट पैरामीटर और आप क्या ढूंढ रहे हैं",
        "disclaimer": "आपकी जानकारी के साथ सख्त गोपनीयता बरती जाती है। आपकी स्पष्ट सहमति के बिना कोई भी संपर्क विवरण या सटीक संपत्ति का पता सार्वजनिक रूप से साझा नहीं किया जाएगा।",
        "submit": "पूछताछ जमा करें",
        "successTitle": "पूछताछ प्राप्त हुई",
        "successDesc": "संपर्क करने के लिए धन्यवाद। संदर्भ कोड: ",
        "successSub": "एक Le Sector सलाहकार गोपनीय रूप से आपकी पूछताछ की समीक्षा करेगा और 24-48 घंटों के भीतर संपर्क करेगा।",
        "close": "बंद करें"
      },
      "pathwayDetails": {
        "propertySites": {
          "badge": "LE SECTOR इकोसिस्टम • संपत्ति और भूमि",
          "title": "संपत्ति और भूमि पाथवे",
          "headline": "भूमि विकास और उप-विभाजन की संभावनाएं",
          "subheadline": "कस्टम रिहायशी या मॉड्यूलर निर्माण के लिए उपयुक्त भूमि, विकास स्थलों और संपत्ति क्षमता से जुड़े अवसरों का अन्वेषण करें।",
          "heroCta": "संपत्ति और भूमि के बारे में पूछताछ करें",
          "secondaryCta": "अवसर निर्देशिका देखें",
          "backToAll": "सभी अवसरों पर वापस जाएं",
          "highlights": [
            "उप-विभाजन मूल्यांकन",
            "विकास क्षमता आकलन",
            "मॉड्यूलर साइट लॉजिस्टिक्स",
            "गोपनीय सुविधा"
          ],
          "overviewTagline": "भूमि और संपत्ति इकोसिस्टम",
          "overviewTitle": "यह पाथवे किस बारे में है",
          "overviewDesc": "प्रॉपर्टी एंड साइट्स पाथवे भूमि मालिकों, संपत्ति धारकों और साझेदारों को सुव्यवस्थित विकास क्षमताओं से जोड़ता है। चाहे ग्रीनफील्ड उप-विभाजन क्षमता, इनफिल पुनर्विकास, या मॉड्यूलर कस्टम होम उपयुक्तता का मूल्यांकन करना हो, Le Sector प्रारंभिक चरण का समन्वय और व्यवहार्यता प्रदान करता है।",
          "overviewSubText": "Le Sector एक पेशेवर सुविधाप्रदाता के रूप में कार्य करता है—सार्वजनिक लिस्टिंग के दबाव के बिना संपत्ति मालिकों को विकास क्षमता का आकलन करने में मदद करता है।",
          "cardTitle": "गोपनीय साइट समन्वय",
          "cardDesc": "Le Sector प्रारंभिक साइट मूल्यांकन करता है और सख्त गोपनीयता बनाए रखते हुए भूमि मालिकों को उपयुक्त निष्पादन साझेदारों से जोड़ता है।",
          "whoTagline": "लक्ष्य प्रतिभागी",
          "whoIsItForTitle": "यह किसके लिए है",
          "whoIsItForItems": [
            {
              "title": "भूमि मालिक और संपत्ति धारक",
              "desc": "कस्टम रिहायशी या मॉड्यूलर विकास के लिए भूमि की उप-विभाजन क्षमता या उपयोग का अन्वेषण करना।"
            },
            {
              "title": "उप-विभाजन भागीदार",
              "desc": "संरचित रिहायशी संयुक्त डिलीवरी में रुचि रखने वाले भूमि मालिकों से जुड़ना।"
            },
            {
              "title": "पुनर्विकास स्थल मालिक",
              "desc": "मौजूदा रिहायशी संपत्तियों के मालिक जो इनफिल पुनर्विकास या मॉड्यूलर पुनर्निर्माण साझेदारी चाहते हैं।"
            }
          ],
          "typesTagline": "अवसर दायरा",
          "opportunityTypesTitle": "क्या अवसर हो सकते हैं",
          "opportunityTypesItems": [
            {
              "title": "रिहायशी उप-विभाजन स्थल",
              "desc": "बहु-प्लॉट रिहायशी उप-विभाजन या आधुनिक मॉड्यूलर मास्टरप्लान के लिए उपयुक्त भूमि भूखंड।",
              "tag": "उप-विभाजन"
            },
            {
              "title": "इनफिल विकास स्थल",
              "desc": "कस्टम आर्किटेक्चरल घरों या मॉड्यूलर निर्माण के लिए उपयुक्त शहरी और उपनगरीय प्लॉट।",
              "tag": "इनफिल पुनर्विकास"
            },
            {
              "title": "विकास साझेदारी",
              "desc": "भूमि मालिकों के लिए अनुभवी डेवलपर्स और बिल्डर नेटवर्क के साथ भागीदार बनने के अवसर।",
              "tag": "संयुक्त उद्यम"
            },
            {
              "title": "मॉड्यूलर अनुकूल स्थल",
              "desc": "क्रेन पहुंच, फैक्टरी असेंबली लॉजिस्टिक्स और तीव्र मॉड्यूलर डिलीवरी के लिए मूल्यांकित स्थल।",
              "tag": "मॉड्यूलर तैयार"
            }
          ],
          "facTagline": "सुविधाजनक निष्पादन",
          "facilitationTitle": "Le Sector कैसे सुविधा प्रदान करता है",
          "facilitationSteps": [
            {
              "step": "01",
              "title": "साइट मूल्यांकन",
              "desc": "साइट अभिविन्यास, पहुंच, ज़ोनिंग और मॉड्यूलर निर्माण व्यवहार्यता की प्रारंभिक समीक्षा।"
            },
            {
              "step": "02",
              "title": "संरचना और रणनीति",
              "desc": "इष्टतम प्रोजेक्ट पथ निर्धारित करना—कस्टम निर्माण, उप-विभाजन, या संयुक्त डिलीवरी।"
            },
            {
              "step": "03",
              "title": "गोपनीय मिलान",
              "desc": "सार्वजनिक प्रकटीकरण के बिना सत्यापित डिलीवरी भागीदारों या पूंजी का परिचय देना।"
            },
            {
              "step": "04",
              "title": "निष्पादन संरेखण",
              "desc": "डिलीवरी के माध्यम से आर्किटेक्चरल, इंजीनियरिंग और विनिर्माण टीमों का समन्वय करना।"
            }
          ],
          "scenariosTagline": "व्यावहारिक अनुप्रयोग",
          "scenariosTitle": "उदाहरण प्रोजेक्ट परिदृश्य",
          "scenariosItems": [
            {
              "title": "3-प्लॉट रिहायशी उप-विभाजन",
              "type": "उप-विभाजन क्षमता",
              "description": "तेजी से प्रोजेक्ट पूरा करने के लिए मॉड्यूलर घरों का उपयोग करके 3 रिहायशी भूखंडों में विभाजित करने की चाह रखने वाला भूमि मालिक।",
              "targetMatch": "भूमि मालिक + मॉड्यूलर डिलीवरी नेटवर्क"
            },
            {
              "title": "इनफिल प्लॉट पुनर्विकास",
              "type": "पुनर्विकास स्थल",
              "description": "पुराने घर को आधुनिक दोहरी रिहायशी कस्टम इमारत से बदलने की इच्छा रखने वाला स्थल मालिक।",
              "targetMatch": "साइट मालिक + कस्टम बिल्डर / डिजाइनर"
            }
          ],
          "resourcesTagline": "जानकारी और संदर्भ",
          "futureResourcesTitle": "संसाधन और प्रोजेक्ट संदर्भ",
          "futureResourcesDesc": "साइट योजना चेकलिस्ट, मॉड्यूलर साइट व्यवहार्यता दिशानिर्देशों और संबंधित Le Sector सेवाओं तक पहुंचें।",
          "futureResourcesItems": [
            {
              "title": "मॉड्यूलर साइट व्यवहार्यता चेकलिस्ट",
              "description": "क्रेन पहुंच, साइट सेटबैक, उपयोगिता कनेक्शन और ढलान मूल्यांकन पर मार्गदर्शन।",
              "status": "संदर्भ गाइड",
              "linkText": "व्यवहार्यता ढांचा देखें"
            },
            {
              "title": "उप-विभाजन जानकारी ब्रीफ",
              "description": "विकास और डिलीवरी भागीदारों को भूमि भूखंड प्रस्तुत करने के लिए मानकीकृत पैरामीटर।",
              "status": "सूचना प्रारूप",
              "linkText": "प्रारूप का अनुरोध करें"
            },
            {
              "title": "Le Sector साइट सलाहकार सेवाएं",
              "description": "जानें कि हमारी सलाहकार टीम प्रारंभिक चरण की साइट समीक्षा कैसे करती है।",
              "status": "सेवा अवलोकन",
              "linkText": "साइट सलाह का अन्वेषण करें"
            }
          ],
          "ctaBox": {
            "title": "क्या आपके पास संपत्ति या भूमि का अवसर है?",
            "desc": "साइट क्षमता का मूल्यांकन करने और गोपनीय सुविधा विकल्पों पर चर्चा करने के लिए Le Sector सलाहकार से बात करें।",
            "buttonText": "संपत्ति और भूमि के बारे में पूछताछ करें"
          }
        },
        "investmentCapital": {
          "badge": "LE SECTOR इकोसिस्टम • निवेश और पूंजी",
          "title": "निवेश और पूंजी पाथवे",
          "headline": "पूंजी और विकास संरेखण",
          "subheadline": "विकास पूंजी को उपयुक्त संपत्ति, रिहायशी विकास और निर्माण अवसरों से जोड़ें।",
          "heroCta": "निवेश और पूंजी के बारे में पूछताछ करें",
          "secondaryCta": "अवसर निर्देशिका देखें",
          "backToAll": "सभी अवसरों पर वापस जाएं",
          "highlights": [
            "विकास पूंजी संरेखण",
            "संपत्ति-समर्थित अवसर",
            "पेशेवर प्रोजेक्ट गवर्नेंस",
            "सुविधाजनक साझेदारी"
          ],
          "overviewTagline": "पूंजी इकोसिस्टम",
          "overviewTitle": "यह पाथवे किस बारे में है",
          "overviewDesc": "इन्वेस्टमेंट एंड कैपिटल पाथवे पूंजी को सत्यापित संपत्ति, विकास और निर्माण परियोजनाओं से जोड़ने वाले अवसरों का परिचय देता है। Le Sector एक पेशेवर सुविधाप्रदाता और प्रोजेक्ट-डिलीवरी पार्टनर के रूप में कार्य करता है।",
          "overviewSubText": "सार्वजनिक निवेश बाजार या फंड के रूप में कार्य करने के बजाय, Le Sector गोपनीयता से पूंजी को रिहायशी और मॉड्यूलर निर्माण अवसरों के साथ जोड़ता है।",
          "cardTitle": "पेशेवर सुविधा भागीदार",
          "cardDesc": "Le Sector सख्त गोपनीयता और जोखिम प्रशासन ढांचे के तहत प्रोजेक्ट डिलीवरी भागीदारों के साथ पूंजी संरेखण का समन्वय करता है।",
          "whoTagline": "लक्ष्य प्रतिभागी",
          "whoIsItForTitle": "यह किसके लिए है",
          "whoIsItForItems": [
            {
              "title": "निजी और इक्विटी निवेशक",
              "desc": "सत्यापित रिहायशी निर्माण और विकास अवसरों में संरचित भागीदारी की तलाश।"
            },
            {
              "title": "विकास वित्त पोषण भागीदार",
              "desc": "स्पष्ट डिलीवरी मील के पत्थरों के साथ पेशेवर रूप से प्रबंधित रिहायशी परियोजनाओं की तलाश।"
            },
            {
              "title": "प्रोजेक्ट डेवलपर्स",
              "desc": "रिहायशी और मॉड्यूलर निर्माण परियोजनाओं के लिए इक्विटी या विकास पूंजी संरेखण की तलाश।"
            }
          ],
          "typesTagline": "अवसर दायरा",
          "opportunityTypesTitle": "क्या अवसर हो सकते हैं",
          "opportunityTypesItems": [
            {
              "title": "इक्विटी साझेदारी",
              "desc": "स्पष्ट मील का पत्थर प्रशासन के साथ रिहायशी परियोजनाओं के लिए सह-विकास संरचनाएं।",
              "tag": "इक्विटी संरेखण"
            },
            {
              "title": "विकास फंडिंग",
              "desc": "मॉड्यूलर विनिर्माण और रिहायशी स्थल डिलीवरी के लिए प्रोजेक्ट-स्तरीय फंडिंग।",
              "tag": "फंडिंग संरचना"
            },
            {
              "title": "संपत्ति-समर्थित प्रोजेक्ट",
              "desc": "भौतिक अचल संपत्ति संपत्तियों द्वारा समर्थित संपत्ति विकास के अवसर।",
              "tag": "संपत्ति समर्थित"
            },
            {
              "title": "पेशेवर रूप से प्रबंधित प्रोजेक्ट",
              "desc": "अनुभवी डिलीवरी टीमों द्वारा प्रबंधित परियोजनाओं की तलाश करने वाले निवेशकों के लिए अवसर।",
              "tag": "प्रबंधित डिलीवरी"
            }
          ],
          "facTagline": "सुविधाजनक निष्पादन",
          "facilitationTitle": "Le Sector कैसे सुविधा प्रदान करता है",
          "facilitationSteps": [
            {
              "step": "01",
              "title": "अवसर जांच",
              "desc": "प्रोजेक्ट मापदंडों, ज़ोनिंग और डिलीवरी व्यवहार्यता पर प्रारंभिक उचित परिश्रम।"
            },
            {
              "step": "02",
              "title": "संरचना संरेखण",
              "desc": "प्रोजेक्ट पैमाने और जोखिम मापदंडों के अनुकूल जुड़ाव मॉडल तैयार करना।"
            },
            {
              "step": "03",
              "title": "गोपनीय परिचय",
              "desc": "पूंजी प्रदाताओं को सीधे प्रोजेक्ट डिलीवरी प्रिंसिपलों से जोड़ना।"
            },
            {
              "step": "04",
              "title": "प्रशासन और रिपोर्टिंग",
              "desc": "पूरे निर्माण चक्र के दौरान प्रोजेक्ट ओवरसाइट और डिलीवरी ट्रैकिंग प्रदान करना।"
            }
          ],
          "scenariosTagline": "व्यावहारिक अनुप्रयोग",
          "scenariosTitle": "उदाहरण प्रोजेक्ट परिदृश्य",
          "scenariosItems": [
            {
              "title": "उप-विभाजन विकास पूंजी",
              "type": "इक्विटी संरेखण",
              "description": "साइट वर्क और मॉड्यूलर निर्माण के लिए इक्विटी पूंजी संरेखण चाहने वाला स्वीकृत 4-प्लॉट रिहायशी उप-विभाजन।",
              "targetMatch": "प्रोजेक्ट डेवलपर + इक्विटी पार्टनर"
            },
            {
              "title": "मॉड्यूलर प्रोजेक्ट फंडिंग",
              "type": "विकास फंडिंग",
              "description": "फैक्टरी प्री-फैब्रिकेशन और साइट इंस्टॉलेशन के लिए संरचित फंडिंग की आवश्यकता वाला रिहायशी प्रस्ताव।",
              "targetMatch": "डिलीवरी टीम + फंडिंग पार्टनर"
            }
          ],
          "resourcesTagline": "जानकारी और संदर्भ",
          "futureResourcesTitle": "संसाधन और निवेश जानकारी",
          "futureResourcesDesc": "शासन दिशानिर्देशों, पूंजी संरेखण ब्रीफ और संबंधित सलाहकारी सेवाओं तक पहुंचें।",
          "futureResourcesItems": [
            {
              "title": "पूंजी जांच ढांचा",
              "description": "प्रोजेक्ट व्यवहार्यता का मूल्यांकन करने के लिए Le Sector द्वारा उपयोग किए जाने वाले मानदंडों का अवलोकन।",
              "status": "ढांचा अवलोकन",
              "linkText": "जांच दिशानिर्देश देखें"
            },
            {
              "title": "प्रोजेक्ट ब्रीफ संरचना",
              "description": "संभावित भागीदारों को पूंजी आवश्यकताओं को प्रस्तुत करने के लिए मानकीकृत संरचना।",
              "status": "संरचना ब्रीफ",
              "linkText": "प्रारूप का अनुरोध करें"
            },
            {
              "title": "Le Sector सलाहकारी सहायता",
              "description": "जानें कि हमारी टीम प्रोजेक्ट डिलीवरी मील के पत्थरों की सुरक्षा के लिए कैसे देखरेख करती है।",
              "status": "सेवा अवलोकन",
              "linkText": "सलाहकारी सेवाओं का अन्वेषण करें"
            }
          ],
          "ctaBox": {
            "title": "क्या आप पूंजी या निवेश संरेखण की तलाश में हैं?",
            "desc": "पूंजी आवश्यकताओं या प्रोजेक्ट संरेखण पर गोपनीय रूप से चर्चा करने के लिए Le Sector सलाहकार से जुड़ें।",
            "buttonText": "निवेश और पूंजी के बारे में पूछताछ करें"
          }
        },
        "projectsDelivery": {
          "badge": "LE SECTOR इकोसिस्टम • प्रोजेक्ट और डिलीवरी",
          "title": "प्रोजेक्ट और डिलीवरी पाथवे",
          "headline": "डिलीवरी क्षमता और रणनीतिक भागीदार",
          "subheadline": "सक्रिय या उभरते प्रोजेक्ट प्रस्तुत करें जिन्हें निर्माण डिलीवरी क्षमता, मॉड्यूलर निर्माण या रणनीतिक भागीदारों की आवश्यकता है।",
          "heroCta": "प्रोजेक्ट और डिलीवरी के बारे में पूछताछ करें",
          "secondaryCta": "अवसर निर्देशिका देखें",
          "backToAll": "सभी अवसरों पर वापस जाएं",
          "highlights": [
            "लाइसेंस प्राप्त बिल्डर एकीकरण",
            "मॉड्यूलर फैक्टरी संरेखण",
            "प्रोजेक्ट प्रबंधन सहायता",
            "विशेषज्ञ ट्रेड क्षमता"
          ],
          "overviewTagline": "डिलीवरी इकोसिस्टम",
          "overviewTitle": "यह पाथवे किस बारे में है",
          "overviewDesc": "प्रोजेक्ट्स एंड डिलीवरी पाथवे सक्रिय रिहायशी विकासों को लाइसेंस प्राप्त बिल्डरों, मॉड्यूलर विनिर्माण सुविधाओं, विशेषज्ञ ट्रेडों और प्रोजेक्ट प्रबंधन विशेषज्ञों से जोड़ता है। Le Sector डिलीवरी समन्वय की सुविधा प्रदान करता है ताकि प्रोजेक्ट कुशलतापूर्वक निष्पादित हों।",
          "overviewSubText": "चाहे किसी प्रोजेक्ट को पारंपरिक कस्टम निर्माण की आवश्यकता हो या ऑफसाइट मॉड्यूलर निर्माण की, Le Sector सही निष्पादन भागीदारों को जोड़ता है।",
          "cardTitle": "एकीकृत डिलीवरी समन्वय",
          "cardDesc": "Le Sector योग्य बिल्डरों, मॉड्यूलर संयंत्रों और विशिष्ट ट्रेड क्षमता के साथ प्रोजेक्ट विशिष्टताओं का मिलान करता है।",
          "whoTagline": "लक्ष्य प्रतिभागी",
          "whoIsItForTitle": "यह किसके लिए है",
          "whoIsItForItems": [
            {
              "title": "लाइसेंस प्राप्त बिल्डर और ठेकेदार",
              "desc": "सत्यापित रिहायशी और मॉड्यूलर निर्माण परियोजनाओं तक पहुंच की तलाश।"
            },
            {
              "title": "मॉड्यूलर निर्माता",
              "desc": "उत्पादन पाइपलाइन मात्रा और साइट असेंबली साझेदारी की तलाश में फैक्टरी संयंत्र।"
            },
            {
              "title": "विशेषज्ञ ट्रेड और डिलीवरी पार्टनर",
              "desc": "प्रोजेक्ट में भागीदारी की तलाश करने वाले ठेकेदार और तकनीकी विशेषज्ञ।"
            }
          ],
          "typesTagline": "अवसर दायरा",
          "opportunityTypesTitle": "क्या अवसर हो सकते हैं",
          "opportunityTypesItems": [
            {
              "title": "लाइसेंस प्राप्त बिल्डरों की तलाश वाले प्रोजेक्ट",
              "desc": "मुख्य ठेकेदार निर्माण क्षमता की आवश्यकता वाले रिहायशी विकास।",
              "tag": "बिल्डर आवश्यक"
            },
            {
              "title": "मॉड्यूलर विनिर्माण संरेखण",
              "desc": "वॉल पैनल, पॉड्स या स्टील फ्रेम के लिए फैक्टरी प्री-फैब्रिकेशन क्षमता की तलाश वाले प्रोजेक्ट।",
              "tag": "मॉड्यूलर क्षमता"
            },
            {
              "title": "निर्माण डिलीवरी साझेदारी",
              "desc": "साइट की तैयारी को ऑफसाइट मॉड्यूलर इंस्टॉलेशन के साथ जोड़ने वाले मॉडल।",
              "tag": "डिलीवरी पार्टनर"
            },
            {
              "title": "प्रोजेक्ट प्रबंधन सहायता",
              "desc": "विशेषज्ञ क्लाइंट-साइड प्रोजेक्ट प्रबंधन और गुणवत्ता नियंत्रण की आवश्यकता वाले विकास।",
              "tag": "प्रोजेक्ट समर्थन"
            }
          ],
          "facTagline": "सुविधाजनक निष्पादन",
          "facilitationTitle": "Le Sector कैसे सुविधा प्रदान करता है",
          "facilitationSteps": [
            {
              "step": "01",
              "title": "प्रोजेक्ट स्कोपिंग",
              "desc": "प्रोजेक्ट ड्राइंग, इंजीनियरिंग चश्मा और डिलीवरी अनुसूची आवश्यकताओं का मूल्यांकन।"
            },
            {
              "step": "02",
              "title": "क्षमता मिलान",
              "desc": "मिलान क्षमता, लाइसेंसिंग और भौगोलिक दायरे वाले बिल्डरों या कारखानों की पहचान करना।"
            },
            {
              "step": "03",
              "title": "सुविधाजनक परिचय",
              "desc": "प्रोजेक्ट मालिकों और डिलीवरी टीमों के बीच संरचित परिचय आयोजित करना।"
            },
            {
              "step": "04",
              "title": "ऑन-साइट समन्वय",
              "desc": "फैक्टरी-टू-साइट लॉजिस्टिक्स और इंस्टॉलेशन गुणवत्ता मानकों का समर्थन करना।"
            }
          ],
          "scenariosTagline": "व्यावहारिक अनुप्रयोग",
          "scenariosTitle": "उदाहरण प्रोजेक्ट परिदृश्य",
          "scenariosItems": [
            {
              "title": "मल्टी-यूनिट मॉड्यूलर निर्माण",
              "type": "मॉड्यूलर क्षमता",
              "description": "6 दो-मंजिला रिहायशी मॉड्यूल के लिए लाइसेंस प्राप्त मॉड्यूलर फैक्टरी पार्टनर की आवश्यकता वाला टाउनहाउस प्रोजेक्ट।",
              "targetMatch": "डेवलपर + मॉड्यूलर फैक्टरी"
            },
            {
              "title": "कस्टम आर्किटेक्चरल होम निर्माण",
              "type": "बिल्डर आवश्यक",
              "description": "आधुनिक सामग्री अनुभव वाले पंजीकृत बिल्डर की तलाश में पूर्व-डिज़ाइन किया गया उच्च-अंत कस्टम रिहायशी प्रोजेक्ट।",
              "targetMatch": "मालिक + मुख्य ठेकेदार"
            }
          ],
          "resourcesTagline": "जानकारी और संदर्भ",
          "futureResourcesTitle": "संसाधन और डिलीवरी केस स्टडी",
          "futureResourcesDesc": "डिलीवरी ढांचा, मॉड्यूलर निर्माण विनिर्देशों और केस स्टडी तक पहुंचें।",
          "futureResourcesItems": [
            {
              "title": "मॉड्यूलर असेंबली और लॉजिस्टिक्स गाइड",
              "description": "फैक्टरी परिवहन, क्रेन स्थिति और साइट कनेक्शन के लिए तकनीकी दिशानिर्देश।",
              "status": "तकनीकी गाइड",
              "linkText": "लॉजिस्टिक्स गाइड देखें"
            },
            {
              "title": "बिल्डर क्षमता पंजीकरण",
              "description": "Le Sector डिलीवरी नेटवर्क में शामिल होने के लिए लाइसेंस प्राप्त बिल्डरों के लिए आवश्यक मापदंड।",
              "status": "पंजीकरण स्लॉट",
              "linkText": "क्षमता पंजीकृत करें"
            },
            {
              "title": "Le Sector डिलीवरी केस स्टडीज",
              "description": "कस्टम और मॉड्यूलर तरीकों से दिए गए पूर्ण रिहायशी प्रोजेक्ट्स का अन्वेषण करें।",
              "status": "केस स्टडीज",
              "linkText": "केस स्टडीज का अन्वेषण करें"
            }
          ],
          "ctaBox": {
            "title": "क्या आपके पास कोई प्रोजेक्ट या डिलीवरी क्षमता है?",
            "desc": "प्रोजेक्ट आवश्यकताओं पर चर्चा करने या हमारे डिलीवरी पार्टनर नेटवर्क में शामिल होने के लिए Le Sector सलाहकार से संपर्क करें।",
            "buttonText": "प्रोजेक्ट और डिलीवरी के बारे में पूछताछ करें"
          }
        },
        "professionalExpertise": {
          "badge": "LE SECTOR इकोसिस्टम • पेशेवर विशेषज्ञता",
          "title": "पेशेवर विशेषज्ञता पाथवे",
          "headline": "विशेषज्ञ क्षमताएं और सलाहकार",
          "subheadline": "डिज़ाइन, इंजीनियरिंग, योजना और सलाहकारी में पेशेवर और विशेषज्ञ क्षमताओं से जुड़े अवसरों का परिचय दें।",
          "heroCta": "पेशेवर विशेषज्ञता के बारे में पूछताछ करें",
          "secondaryCta": "अवसर निर्देशिका देखें",
          "backToAll": "सभी अवसरों पर वापस जाएं",
          "highlights": [
            "आर्किटेक्ट और डिज़ाइनर",
            "स्ट्रक्चरल और सिविल इंजीनियर",
            "टाउन प्लानर और सर्वेयर",
            "विशेषज्ञ सलाहकार"
          ],
          "overviewTagline": "विशेषज्ञता इकोसिस्टम",
          "overviewTitle": "यह पाथवे किस बारे में है",
          "overviewDesc": "प्रोफेशनल एक्सपर्टाइज पाथवे पंजीकृत आर्किटेक्ट्स, स्ट्रक्चरल इंजीनियर्स, सिविल इंजीनियर्स, टाउन प्लानर्स, सर्वेयर और वित्तीय सलाहकारों को शुरुआती चरण की रिहायशी परियोजनाओं से जोड़ता है। Le Sector डिज़ाइन, ज़ोनिंग अनुपालन और मॉड्यूलर निर्माण क्षमता को अनुकूलित करने के लिए प्रोजेक्ट जीवनचक्र में जल्दी विशेषज्ञता को शामिल करता है।",
          "overviewSubText": "पेशेवरों को जल्दी शामिल करके, प्रोजेक्ट परिणाम उच्च आर्किटेक्चरल गुणवत्ता, तेज़ योजना अनुमोदन और आधुनिक निर्माण तरीकों के साथ सहज एकीकरण प्राप्त करते हैं।",
          "cardTitle": "संरचित सलाहकार नेटवर्क",
          "cardDesc": "Le Sector एक संरचित, गोपनीय परिचय प्रक्रिया के माध्यम से विशेषज्ञ सलाहकारों को रिहायशी प्रोजेक्ट अवसरों से जोड़ता है।",
          "whoTagline": "लक्ष्य प्रतिभागी",
          "whoIsItForTitle": "यह किसके लिए है",
          "whoIsItForItems": [
            {
              "title": "आर्किटेक्ट्स और बिल्डिंग डिज़ाइनर",
              "desc": "कस्टम और मॉड्यूलर रिहायशी डिज़ाइन अवसरों की तलाश।"
            },
            {
              "title": "इंजीनियर और टाउन प्लानर",
              "desc": "रिहायशी उप-विभाजन और मॉड्यूलर एकीकरण में विशेषज्ञता वाले स्ट्रक्चरल, सिविल और प्लानिंग सलाहकार।"
            },
            {
              "title": "विशेषज्ञ सलाहकार",
              "desc": "सर्वेयर, प्रमाणक, मात्रा सर्वेयर और प्रोजेक्ट वित्त विशेषज्ञ।"
            }
          ],
          "typesTagline": "अवसर दायरा",
          "opportunityTypesTitle": "क्या अवसर हो सकते हैं",
          "opportunityTypesItems": [
            {
              "title": "आर्किटेक्चरल और DfMA डिज़ाइन",
              "desc": "निर्माण और असेंबली के लिए डिज़ाइन (DfMA) के लिए तैयार किए गए डिज़ाइन जुड़ाव।",
              "tag": "डिज़ाइन एकीकरण"
            },
            {
              "title": "इंजीनियरिंग और स्ट्रक्चरल समीक्षा",
              "desc": "मॉड्यूलर नींव और साइट कार्यों के लिए सिविल और स्ट्रक्चरल इंजीनियरिंग।",
              "tag": "इंजीनियरिंग"
            },
            {
              "title": "योजना और ज़ोनिंग अनुमोदन",
              "desc": "जटिल रिहायशी उप-विभाजनों के लिए टाउन प्लानिंग सलाहकारी।",
              "tag": "टाउन प्लानिंग"
            },
            {
              "title": "बिल्डिंग प्रमाणन और अनुपालन",
              "desc": "बिल्डिंग कोड अनुपालन, ऊर्जा रेटिंग और प्रमाणन के लिए परामर्श।",
              "tag": "अनुपालन"
            }
          ],
          "facTagline": "सुविधाजनक निष्पादन",
          "facilitationTitle": "Le Sector कैसे सुविधा प्रदान करता है",
          "facilitationSteps": [
            {
              "step": "01",
              "title": "विशेषज्ञता स्कोपिंग",
              "desc": "प्रारंभिक डिज़ाइन चरणों के दौरान प्रोजेक्ट तकनीकी आवश्यकताओं की पहचान करना।"
            },
            {
              "step": "02",
              "title": "सलाहकार संरेखण",
              "desc": "विशिष्ट सलाहकार विशेषज्ञता के साथ प्रोजेक्ट आवश्यकताओं का मिलान करना।"
            },
            {
              "step": "03",
              "title": "संरचित परिचय",
              "desc": "प्रोजेक्ट मालिकों और सलाहकारों के बीच गोपनीय ब्रीफ की सुविधा प्रदान करना।"
            },
            {
              "step": "04",
              "title": "एकीकृत सहयोग",
              "desc": "विनिर्माण और साइट टीमों के साथ सहयोगात्मक डिज़ाइन कार्यप्रवाह का समर्थन करना।"
            }
          ],
          "scenariosTagline": "व्यावहारिक अनुप्रयोग",
          "scenariosTitle": "उदाहरण प्रोजेक्ट परिदृश्य",
          "scenariosItems": [
            {
              "title": "DfMA आर्किटेक्चरल डिज़ाइन ब्रीफ",
              "type": "डिज़ाइन एकीकरण",
              "description": "प्रारंभिक अवधारणा डिजाइनों को परिष्कृत करने के लिए मॉड्यूलर विनिर्माण बाधाओं में अनुभवी आर्किटेक्ट की तलाश में एक कस्टम रिहायशी प्रोजेक्ट।",
              "targetMatch": "प्रोजेक्ट मालिक + मॉड्यूलर आर्किटेक्ट"
            },
            {
              "title": "उप-विभाजन टाउन प्लानिंग अनुमोदन",
              "type": "टाउन प्लानिंग",
              "description": "स्थानीय परिषद के साथ बहु-प्लॉट उप-विभाजन अनुमोदन नेविगेट करने के लिए टाउन प्लानिंग परामर्श की आवश्यकता वाले भूमि मालिक।",
              "targetMatch": "भूमि मालिक + टाउन प्लानर"
            }
          ],
          "resourcesTagline": "जानकारी और संदर्भ",
          "futureResourcesTitle": "संसाधन और पेशेवर संदर्भ लिंक",
          "futureResourcesDesc": "DfMA डिज़ाइन ढांचे, योजना दिशानिर्देशों और सलाहकार नेटवर्क की जानकारी तक पहुंचें।",
          "futureResourcesItems": [
            {
              "title": "मॉड्यूलर डिज़ाइन (DfMA) दिशानिर्देश",
              "description": "मॉड्यूल परिवहन आयाम, कनेक्शन जोड़ों और स्ट्रक्चरल ग्रिड पर तकनीकी पुस्तिका।",
              "status": "तकनीकी पुस्तिका",
              "linkText": "DfMA दिशानिर्देश देखें"
            },
            {
              "title": "सलाहकार नेटवर्क पंजीकरण",
              "description": "प्रारंभिक चरण के प्रोजेक्ट परिचय के लिए Le Sector पेशेवर रजिस्टर में शामिल हों।",
              "status": "पंजीकरण स्लॉट",
              "linkText": "सलाहकार नेटवर्क में शामिल हों"
            },
            {
              "title": "एकीकृत केस स्टडीज",
              "description": "कस्टम रिहायशी डिलीवरी में सफल आर्किटेक्ट-इंजीनियर-बिल्डर सहयोग के उदाहरण।",
              "status": "केस स्टडीज",
              "linkText": "एकीकृत प्रोजेक्ट्स का अन्वेषण करें"
            }
          ],
          "ctaBox": {
            "title": "क्या आप एक पेशेवर विशेषज्ञ हैं या विशेषज्ञता की तलाश कर रहे हैं?",
            "desc": "प्रोजेक्ट परामर्श अवसरों पर चर्चा करने या हमारे पेशेवर नेटवर्क में शामिल होने के लिए Le Sector से जुड़ें।",
            "buttonText": "पेशेवर विशेषज्ञता के बारे में पूछताछ करें"
          }
        }
      }
    }
  }
};
